import {EventHandlerBase,CommandBase,PythaEventDataChange,Primitive,PrimitiveType,EventGraphBase,EventGraphNode,EventHandlerStart,EventHandlerCalPointerDown,EventGraphNodeNotice,NeedInputEnum,PythaEventName,EventHandlerDisableSelect,EventHandlerInitCapture,EventHandlerCalPointerMove,EventHandlerCapture,EventGraphNodeAND,EventHandlerGrid,EventGraphNodeRender,UpdateType,EHChangeCrossCursor,EventHandlerConfirm,EventHandlerCmdEnd,EventHandlerCalPrimitiveInView,EventHandlerSelectStart,EventHandlerCalPointerUp,EventGraphNodeSwitch,EventHandlerEmptySelect,EventHandlerDisableClamp,EHChangePickCursor,EventHandlerOptionInput,EventHandlerGenRegion,CommandGroup,EventGraphPluginBase,EventGraphDelete,GraftOrderEnum,EventHandlerGenCommandGroup,EventHanderCalAllBoundingBox,EventHanderShowAllInRect,LabelBase,RenderItemType,EventHandlerNumberInput,OutputDataType,PythaEventOutputData,EventHandlerObjectInput,EventHandlerEditPri,PythaPluginBase}from'pytha2';import {BaseUtils,Point,Line,RegionFace,Arc,Circle,ExtrudeSolid,Plane}from'tnbase';/**
 * @license
 * Copyright 2010-2024 Three.js Authors
 * SPDX-License-Identifier: MIT
 */
const REVISION = '166';

const WebGLCoordinateSystem = 2000;
const WebGPUCoordinateSystem = 2001;

function clamp( value, min, max ) {

	return Math.max( min, Math.min( max, value ) );

}

class Quaternion {

	constructor( x = 0, y = 0, z = 0, w = 1 ) {

		this.isQuaternion = true;

		this._x = x;
		this._y = y;
		this._z = z;
		this._w = w;

	}

	static slerpFlat( dst, dstOffset, src0, srcOffset0, src1, srcOffset1, t ) {

		// fuzz-free, array-based Quaternion SLERP operation

		let x0 = src0[ srcOffset0 + 0 ],
			y0 = src0[ srcOffset0 + 1 ],
			z0 = src0[ srcOffset0 + 2 ],
			w0 = src0[ srcOffset0 + 3 ];

		const x1 = src1[ srcOffset1 + 0 ],
			y1 = src1[ srcOffset1 + 1 ],
			z1 = src1[ srcOffset1 + 2 ],
			w1 = src1[ srcOffset1 + 3 ];

		if ( t === 0 ) {

			dst[ dstOffset + 0 ] = x0;
			dst[ dstOffset + 1 ] = y0;
			dst[ dstOffset + 2 ] = z0;
			dst[ dstOffset + 3 ] = w0;
			return;

		}

		if ( t === 1 ) {

			dst[ dstOffset + 0 ] = x1;
			dst[ dstOffset + 1 ] = y1;
			dst[ dstOffset + 2 ] = z1;
			dst[ dstOffset + 3 ] = w1;
			return;

		}

		if ( w0 !== w1 || x0 !== x1 || y0 !== y1 || z0 !== z1 ) {

			let s = 1 - t;
			const cos = x0 * x1 + y0 * y1 + z0 * z1 + w0 * w1,
				dir = ( cos >= 0 ? 1 : - 1 ),
				sqrSin = 1 - cos * cos;

			// Skip the Slerp for tiny steps to avoid numeric problems:
			if ( sqrSin > Number.EPSILON ) {

				const sin = Math.sqrt( sqrSin ),
					len = Math.atan2( sin, cos * dir );

				s = Math.sin( s * len ) / sin;
				t = Math.sin( t * len ) / sin;

			}

			const tDir = t * dir;

			x0 = x0 * s + x1 * tDir;
			y0 = y0 * s + y1 * tDir;
			z0 = z0 * s + z1 * tDir;
			w0 = w0 * s + w1 * tDir;

			// Normalize in case we just did a lerp:
			if ( s === 1 - t ) {

				const f = 1 / Math.sqrt( x0 * x0 + y0 * y0 + z0 * z0 + w0 * w0 );

				x0 *= f;
				y0 *= f;
				z0 *= f;
				w0 *= f;

			}

		}

		dst[ dstOffset ] = x0;
		dst[ dstOffset + 1 ] = y0;
		dst[ dstOffset + 2 ] = z0;
		dst[ dstOffset + 3 ] = w0;

	}

	static multiplyQuaternionsFlat( dst, dstOffset, src0, srcOffset0, src1, srcOffset1 ) {

		const x0 = src0[ srcOffset0 ];
		const y0 = src0[ srcOffset0 + 1 ];
		const z0 = src0[ srcOffset0 + 2 ];
		const w0 = src0[ srcOffset0 + 3 ];

		const x1 = src1[ srcOffset1 ];
		const y1 = src1[ srcOffset1 + 1 ];
		const z1 = src1[ srcOffset1 + 2 ];
		const w1 = src1[ srcOffset1 + 3 ];

		dst[ dstOffset ] = x0 * w1 + w0 * x1 + y0 * z1 - z0 * y1;
		dst[ dstOffset + 1 ] = y0 * w1 + w0 * y1 + z0 * x1 - x0 * z1;
		dst[ dstOffset + 2 ] = z0 * w1 + w0 * z1 + x0 * y1 - y0 * x1;
		dst[ dstOffset + 3 ] = w0 * w1 - x0 * x1 - y0 * y1 - z0 * z1;

		return dst;

	}

	get x() {

		return this._x;

	}

	set x( value ) {

		this._x = value;
		this._onChangeCallback();

	}

	get y() {

		return this._y;

	}

	set y( value ) {

		this._y = value;
		this._onChangeCallback();

	}

	get z() {

		return this._z;

	}

	set z( value ) {

		this._z = value;
		this._onChangeCallback();

	}

	get w() {

		return this._w;

	}

	set w( value ) {

		this._w = value;
		this._onChangeCallback();

	}

	set( x, y, z, w ) {

		this._x = x;
		this._y = y;
		this._z = z;
		this._w = w;

		this._onChangeCallback();

		return this;

	}

	clone() {

		return new this.constructor( this._x, this._y, this._z, this._w );

	}

	copy( quaternion ) {

		this._x = quaternion.x;
		this._y = quaternion.y;
		this._z = quaternion.z;
		this._w = quaternion.w;

		this._onChangeCallback();

		return this;

	}

	setFromEuler( euler, update = true ) {

		const x = euler._x, y = euler._y, z = euler._z, order = euler._order;

		// http://www.mathworks.com/matlabcentral/fileexchange/
		// 	20696-function-to-convert-between-dcm-euler-angles-quaternions-and-euler-vectors/
		//	content/SpinCalc.m

		const cos = Math.cos;
		const sin = Math.sin;

		const c1 = cos( x / 2 );
		const c2 = cos( y / 2 );
		const c3 = cos( z / 2 );

		const s1 = sin( x / 2 );
		const s2 = sin( y / 2 );
		const s3 = sin( z / 2 );

		switch ( order ) {

			case 'XYZ':
				this._x = s1 * c2 * c3 + c1 * s2 * s3;
				this._y = c1 * s2 * c3 - s1 * c2 * s3;
				this._z = c1 * c2 * s3 + s1 * s2 * c3;
				this._w = c1 * c2 * c3 - s1 * s2 * s3;
				break;

			case 'YXZ':
				this._x = s1 * c2 * c3 + c1 * s2 * s3;
				this._y = c1 * s2 * c3 - s1 * c2 * s3;
				this._z = c1 * c2 * s3 - s1 * s2 * c3;
				this._w = c1 * c2 * c3 + s1 * s2 * s3;
				break;

			case 'ZXY':
				this._x = s1 * c2 * c3 - c1 * s2 * s3;
				this._y = c1 * s2 * c3 + s1 * c2 * s3;
				this._z = c1 * c2 * s3 + s1 * s2 * c3;
				this._w = c1 * c2 * c3 - s1 * s2 * s3;
				break;

			case 'ZYX':
				this._x = s1 * c2 * c3 - c1 * s2 * s3;
				this._y = c1 * s2 * c3 + s1 * c2 * s3;
				this._z = c1 * c2 * s3 - s1 * s2 * c3;
				this._w = c1 * c2 * c3 + s1 * s2 * s3;
				break;

			case 'YZX':
				this._x = s1 * c2 * c3 + c1 * s2 * s3;
				this._y = c1 * s2 * c3 + s1 * c2 * s3;
				this._z = c1 * c2 * s3 - s1 * s2 * c3;
				this._w = c1 * c2 * c3 - s1 * s2 * s3;
				break;

			case 'XZY':
				this._x = s1 * c2 * c3 - c1 * s2 * s3;
				this._y = c1 * s2 * c3 - s1 * c2 * s3;
				this._z = c1 * c2 * s3 + s1 * s2 * c3;
				this._w = c1 * c2 * c3 + s1 * s2 * s3;
				break;

			default:
				console.warn( 'THREE.Quaternion: .setFromEuler() encountered an unknown order: ' + order );

		}

		if ( update === true ) this._onChangeCallback();

		return this;

	}

	setFromAxisAngle( axis, angle ) {

		// http://www.euclideanspace.com/maths/geometry/rotations/conversions/angleToQuaternion/index.htm

		// assumes axis is normalized

		const halfAngle = angle / 2, s = Math.sin( halfAngle );

		this._x = axis.x * s;
		this._y = axis.y * s;
		this._z = axis.z * s;
		this._w = Math.cos( halfAngle );

		this._onChangeCallback();

		return this;

	}

	setFromRotationMatrix( m ) {

		// http://www.euclideanspace.com/maths/geometry/rotations/conversions/matrixToQuaternion/index.htm

		// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

		const te = m.elements,

			m11 = te[ 0 ], m12 = te[ 4 ], m13 = te[ 8 ],
			m21 = te[ 1 ], m22 = te[ 5 ], m23 = te[ 9 ],
			m31 = te[ 2 ], m32 = te[ 6 ], m33 = te[ 10 ],

			trace = m11 + m22 + m33;

		if ( trace > 0 ) {

			const s = 0.5 / Math.sqrt( trace + 1.0 );

			this._w = 0.25 / s;
			this._x = ( m32 - m23 ) * s;
			this._y = ( m13 - m31 ) * s;
			this._z = ( m21 - m12 ) * s;

		} else if ( m11 > m22 && m11 > m33 ) {

			const s = 2.0 * Math.sqrt( 1.0 + m11 - m22 - m33 );

			this._w = ( m32 - m23 ) / s;
			this._x = 0.25 * s;
			this._y = ( m12 + m21 ) / s;
			this._z = ( m13 + m31 ) / s;

		} else if ( m22 > m33 ) {

			const s = 2.0 * Math.sqrt( 1.0 + m22 - m11 - m33 );

			this._w = ( m13 - m31 ) / s;
			this._x = ( m12 + m21 ) / s;
			this._y = 0.25 * s;
			this._z = ( m23 + m32 ) / s;

		} else {

			const s = 2.0 * Math.sqrt( 1.0 + m33 - m11 - m22 );

			this._w = ( m21 - m12 ) / s;
			this._x = ( m13 + m31 ) / s;
			this._y = ( m23 + m32 ) / s;
			this._z = 0.25 * s;

		}

		this._onChangeCallback();

		return this;

	}

	setFromUnitVectors( vFrom, vTo ) {

		// assumes direction vectors vFrom and vTo are normalized

		let r = vFrom.dot( vTo ) + 1;

		if ( r < Number.EPSILON ) {

			// vFrom and vTo point in opposite directions

			r = 0;

			if ( Math.abs( vFrom.x ) > Math.abs( vFrom.z ) ) {

				this._x = - vFrom.y;
				this._y = vFrom.x;
				this._z = 0;
				this._w = r;

			} else {

				this._x = 0;
				this._y = - vFrom.z;
				this._z = vFrom.y;
				this._w = r;

			}

		} else {

			// crossVectors( vFrom, vTo ); // inlined to avoid cyclic dependency on Vector3

			this._x = vFrom.y * vTo.z - vFrom.z * vTo.y;
			this._y = vFrom.z * vTo.x - vFrom.x * vTo.z;
			this._z = vFrom.x * vTo.y - vFrom.y * vTo.x;
			this._w = r;

		}

		return this.normalize();

	}

	angleTo( q ) {

		return 2 * Math.acos( Math.abs( clamp( this.dot( q ), - 1, 1 ) ) );

	}

	rotateTowards( q, step ) {

		const angle = this.angleTo( q );

		if ( angle === 0 ) return this;

		const t = Math.min( 1, step / angle );

		this.slerp( q, t );

		return this;

	}

	identity() {

		return this.set( 0, 0, 0, 1 );

	}

	invert() {

		// quaternion is assumed to have unit length

		return this.conjugate();

	}

	conjugate() {

		this._x *= - 1;
		this._y *= - 1;
		this._z *= - 1;

		this._onChangeCallback();

		return this;

	}

	dot( v ) {

		return this._x * v._x + this._y * v._y + this._z * v._z + this._w * v._w;

	}

	lengthSq() {

		return this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w;

	}

	length() {

		return Math.sqrt( this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w );

	}

	normalize() {

		let l = this.length();

		if ( l === 0 ) {

			this._x = 0;
			this._y = 0;
			this._z = 0;
			this._w = 1;

		} else {

			l = 1 / l;

			this._x = this._x * l;
			this._y = this._y * l;
			this._z = this._z * l;
			this._w = this._w * l;

		}

		this._onChangeCallback();

		return this;

	}

	multiply( q ) {

		return this.multiplyQuaternions( this, q );

	}

	premultiply( q ) {

		return this.multiplyQuaternions( q, this );

	}

	multiplyQuaternions( a, b ) {

		// from http://www.euclideanspace.com/maths/algebra/realNormedAlgebra/quaternions/code/index.htm

		const qax = a._x, qay = a._y, qaz = a._z, qaw = a._w;
		const qbx = b._x, qby = b._y, qbz = b._z, qbw = b._w;

		this._x = qax * qbw + qaw * qbx + qay * qbz - qaz * qby;
		this._y = qay * qbw + qaw * qby + qaz * qbx - qax * qbz;
		this._z = qaz * qbw + qaw * qbz + qax * qby - qay * qbx;
		this._w = qaw * qbw - qax * qbx - qay * qby - qaz * qbz;

		this._onChangeCallback();

		return this;

	}

	slerp( qb, t ) {

		if ( t === 0 ) return this;
		if ( t === 1 ) return this.copy( qb );

		const x = this._x, y = this._y, z = this._z, w = this._w;

		// http://www.euclideanspace.com/maths/algebra/realNormedAlgebra/quaternions/slerp/

		let cosHalfTheta = w * qb._w + x * qb._x + y * qb._y + z * qb._z;

		if ( cosHalfTheta < 0 ) {

			this._w = - qb._w;
			this._x = - qb._x;
			this._y = - qb._y;
			this._z = - qb._z;

			cosHalfTheta = - cosHalfTheta;

		} else {

			this.copy( qb );

		}

		if ( cosHalfTheta >= 1.0 ) {

			this._w = w;
			this._x = x;
			this._y = y;
			this._z = z;

			return this;

		}

		const sqrSinHalfTheta = 1.0 - cosHalfTheta * cosHalfTheta;

		if ( sqrSinHalfTheta <= Number.EPSILON ) {

			const s = 1 - t;
			this._w = s * w + t * this._w;
			this._x = s * x + t * this._x;
			this._y = s * y + t * this._y;
			this._z = s * z + t * this._z;

			this.normalize(); // normalize calls _onChangeCallback()

			return this;

		}

		const sinHalfTheta = Math.sqrt( sqrSinHalfTheta );
		const halfTheta = Math.atan2( sinHalfTheta, cosHalfTheta );
		const ratioA = Math.sin( ( 1 - t ) * halfTheta ) / sinHalfTheta,
			ratioB = Math.sin( t * halfTheta ) / sinHalfTheta;

		this._w = ( w * ratioA + this._w * ratioB );
		this._x = ( x * ratioA + this._x * ratioB );
		this._y = ( y * ratioA + this._y * ratioB );
		this._z = ( z * ratioA + this._z * ratioB );

		this._onChangeCallback();

		return this;

	}

	slerpQuaternions( qa, qb, t ) {

		return this.copy( qa ).slerp( qb, t );

	}

	random() {

		// sets this quaternion to a uniform random unit quaternnion

		// Ken Shoemake
		// Uniform random rotations
		// D. Kirk, editor, Graphics Gems III, pages 124-132. Academic Press, New York, 1992.

		const theta1 = 2 * Math.PI * Math.random();
		const theta2 = 2 * Math.PI * Math.random();

		const x0 = Math.random();
		const r1 = Math.sqrt( 1 - x0 );
		const r2 = Math.sqrt( x0 );

		return this.set(
			r1 * Math.sin( theta1 ),
			r1 * Math.cos( theta1 ),
			r2 * Math.sin( theta2 ),
			r2 * Math.cos( theta2 ),
		);

	}

	equals( quaternion ) {

		return ( quaternion._x === this._x ) && ( quaternion._y === this._y ) && ( quaternion._z === this._z ) && ( quaternion._w === this._w );

	}

	fromArray( array, offset = 0 ) {

		this._x = array[ offset ];
		this._y = array[ offset + 1 ];
		this._z = array[ offset + 2 ];
		this._w = array[ offset + 3 ];

		this._onChangeCallback();

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this._x;
		array[ offset + 1 ] = this._y;
		array[ offset + 2 ] = this._z;
		array[ offset + 3 ] = this._w;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this._x = attribute.getX( index );
		this._y = attribute.getY( index );
		this._z = attribute.getZ( index );
		this._w = attribute.getW( index );

		this._onChangeCallback();

		return this;

	}

	toJSON() {

		return this.toArray();

	}

	_onChange( callback ) {

		this._onChangeCallback = callback;

		return this;

	}

	_onChangeCallback() {}

	*[ Symbol.iterator ]() {

		yield this._x;
		yield this._y;
		yield this._z;
		yield this._w;

	}

}

class Vector3 {

	constructor( x = 0, y = 0, z = 0 ) {

		Vector3.prototype.isVector3 = true;

		this.x = x;
		this.y = y;
		this.z = z;

	}

	set( x, y, z ) {

		if ( z === undefined ) z = this.z; // sprite.scale.set(x,y)

		this.x = x;
		this.y = y;
		this.z = z;

		return this;

	}

	setScalar( scalar ) {

		this.x = scalar;
		this.y = scalar;
		this.z = scalar;

		return this;

	}

	setX( x ) {

		this.x = x;

		return this;

	}

	setY( y ) {

		this.y = y;

		return this;

	}

	setZ( z ) {

		this.z = z;

		return this;

	}

	setComponent( index, value ) {

		switch ( index ) {

			case 0: this.x = value; break;
			case 1: this.y = value; break;
			case 2: this.z = value; break;
			default: throw new Error( 'index is out of range: ' + index );

		}

		return this;

	}

	getComponent( index ) {

		switch ( index ) {

			case 0: return this.x;
			case 1: return this.y;
			case 2: return this.z;
			default: throw new Error( 'index is out of range: ' + index );

		}

	}

	clone() {

		return new this.constructor( this.x, this.y, this.z );

	}

	copy( v ) {

		this.x = v.x;
		this.y = v.y;
		this.z = v.z;

		return this;

	}

	add( v ) {

		this.x += v.x;
		this.y += v.y;
		this.z += v.z;

		return this;

	}

	addScalar( s ) {

		this.x += s;
		this.y += s;
		this.z += s;

		return this;

	}

	addVectors( a, b ) {

		this.x = a.x + b.x;
		this.y = a.y + b.y;
		this.z = a.z + b.z;

		return this;

	}

	addScaledVector( v, s ) {

		this.x += v.x * s;
		this.y += v.y * s;
		this.z += v.z * s;

		return this;

	}

	sub( v ) {

		this.x -= v.x;
		this.y -= v.y;
		this.z -= v.z;

		return this;

	}

	subScalar( s ) {

		this.x -= s;
		this.y -= s;
		this.z -= s;

		return this;

	}

	subVectors( a, b ) {

		this.x = a.x - b.x;
		this.y = a.y - b.y;
		this.z = a.z - b.z;

		return this;

	}

	multiply( v ) {

		this.x *= v.x;
		this.y *= v.y;
		this.z *= v.z;

		return this;

	}

	multiplyScalar( scalar ) {

		this.x *= scalar;
		this.y *= scalar;
		this.z *= scalar;

		return this;

	}

	multiplyVectors( a, b ) {

		this.x = a.x * b.x;
		this.y = a.y * b.y;
		this.z = a.z * b.z;

		return this;

	}

	applyEuler( euler ) {

		return this.applyQuaternion( _quaternion$4.setFromEuler( euler ) );

	}

	applyAxisAngle( axis, angle ) {

		return this.applyQuaternion( _quaternion$4.setFromAxisAngle( axis, angle ) );

	}

	applyMatrix3( m ) {

		const x = this.x, y = this.y, z = this.z;
		const e = m.elements;

		this.x = e[ 0 ] * x + e[ 3 ] * y + e[ 6 ] * z;
		this.y = e[ 1 ] * x + e[ 4 ] * y + e[ 7 ] * z;
		this.z = e[ 2 ] * x + e[ 5 ] * y + e[ 8 ] * z;

		return this;

	}

	applyNormalMatrix( m ) {

		return this.applyMatrix3( m ).normalize();

	}

	applyMatrix4( m ) {

		const x = this.x, y = this.y, z = this.z;
		const e = m.elements;

		const w = 1 / ( e[ 3 ] * x + e[ 7 ] * y + e[ 11 ] * z + e[ 15 ] );

		this.x = ( e[ 0 ] * x + e[ 4 ] * y + e[ 8 ] * z + e[ 12 ] ) * w;
		this.y = ( e[ 1 ] * x + e[ 5 ] * y + e[ 9 ] * z + e[ 13 ] ) * w;
		this.z = ( e[ 2 ] * x + e[ 6 ] * y + e[ 10 ] * z + e[ 14 ] ) * w;

		return this;

	}

	applyQuaternion( q ) {

		// quaternion q is assumed to have unit length

		const vx = this.x, vy = this.y, vz = this.z;
		const qx = q.x, qy = q.y, qz = q.z, qw = q.w;

		// t = 2 * cross( q.xyz, v );
		const tx = 2 * ( qy * vz - qz * vy );
		const ty = 2 * ( qz * vx - qx * vz );
		const tz = 2 * ( qx * vy - qy * vx );

		// v + q.w * t + cross( q.xyz, t );
		this.x = vx + qw * tx + qy * tz - qz * ty;
		this.y = vy + qw * ty + qz * tx - qx * tz;
		this.z = vz + qw * tz + qx * ty - qy * tx;

		return this;

	}

	project( camera ) {

		return this.applyMatrix4( camera.matrixWorldInverse ).applyMatrix4( camera.projectionMatrix );

	}

	unproject( camera ) {

		return this.applyMatrix4( camera.projectionMatrixInverse ).applyMatrix4( camera.matrixWorld );

	}

	transformDirection( m ) {

		// input: THREE.Matrix4 affine matrix
		// vector interpreted as a direction

		const x = this.x, y = this.y, z = this.z;
		const e = m.elements;

		this.x = e[ 0 ] * x + e[ 4 ] * y + e[ 8 ] * z;
		this.y = e[ 1 ] * x + e[ 5 ] * y + e[ 9 ] * z;
		this.z = e[ 2 ] * x + e[ 6 ] * y + e[ 10 ] * z;

		return this.normalize();

	}

	divide( v ) {

		this.x /= v.x;
		this.y /= v.y;
		this.z /= v.z;

		return this;

	}

	divideScalar( scalar ) {

		return this.multiplyScalar( 1 / scalar );

	}

	min( v ) {

		this.x = Math.min( this.x, v.x );
		this.y = Math.min( this.y, v.y );
		this.z = Math.min( this.z, v.z );

		return this;

	}

	max( v ) {

		this.x = Math.max( this.x, v.x );
		this.y = Math.max( this.y, v.y );
		this.z = Math.max( this.z, v.z );

		return this;

	}

	clamp( min, max ) {

		// assumes min < max, componentwise

		this.x = Math.max( min.x, Math.min( max.x, this.x ) );
		this.y = Math.max( min.y, Math.min( max.y, this.y ) );
		this.z = Math.max( min.z, Math.min( max.z, this.z ) );

		return this;

	}

	clampScalar( minVal, maxVal ) {

		this.x = Math.max( minVal, Math.min( maxVal, this.x ) );
		this.y = Math.max( minVal, Math.min( maxVal, this.y ) );
		this.z = Math.max( minVal, Math.min( maxVal, this.z ) );

		return this;

	}

	clampLength( min, max ) {

		const length = this.length();

		return this.divideScalar( length || 1 ).multiplyScalar( Math.max( min, Math.min( max, length ) ) );

	}

	floor() {

		this.x = Math.floor( this.x );
		this.y = Math.floor( this.y );
		this.z = Math.floor( this.z );

		return this;

	}

	ceil() {

		this.x = Math.ceil( this.x );
		this.y = Math.ceil( this.y );
		this.z = Math.ceil( this.z );

		return this;

	}

	round() {

		this.x = Math.round( this.x );
		this.y = Math.round( this.y );
		this.z = Math.round( this.z );

		return this;

	}

	roundToZero() {

		this.x = Math.trunc( this.x );
		this.y = Math.trunc( this.y );
		this.z = Math.trunc( this.z );

		return this;

	}

	negate() {

		this.x = - this.x;
		this.y = - this.y;
		this.z = - this.z;

		return this;

	}

	dot( v ) {

		return this.x * v.x + this.y * v.y + this.z * v.z;

	}

	// TODO lengthSquared?

	lengthSq() {

		return this.x * this.x + this.y * this.y + this.z * this.z;

	}

	length() {

		return Math.sqrt( this.x * this.x + this.y * this.y + this.z * this.z );

	}

	manhattanLength() {

		return Math.abs( this.x ) + Math.abs( this.y ) + Math.abs( this.z );

	}

	normalize() {

		return this.divideScalar( this.length() || 1 );

	}

	setLength( length ) {

		return this.normalize().multiplyScalar( length );

	}

	lerp( v, alpha ) {

		this.x += ( v.x - this.x ) * alpha;
		this.y += ( v.y - this.y ) * alpha;
		this.z += ( v.z - this.z ) * alpha;

		return this;

	}

	lerpVectors( v1, v2, alpha ) {

		this.x = v1.x + ( v2.x - v1.x ) * alpha;
		this.y = v1.y + ( v2.y - v1.y ) * alpha;
		this.z = v1.z + ( v2.z - v1.z ) * alpha;

		return this;

	}

	cross( v ) {

		return this.crossVectors( this, v );

	}

	crossVectors( a, b ) {

		const ax = a.x, ay = a.y, az = a.z;
		const bx = b.x, by = b.y, bz = b.z;

		this.x = ay * bz - az * by;
		this.y = az * bx - ax * bz;
		this.z = ax * by - ay * bx;

		return this;

	}

	projectOnVector( v ) {

		const denominator = v.lengthSq();

		if ( denominator === 0 ) return this.set( 0, 0, 0 );

		const scalar = v.dot( this ) / denominator;

		return this.copy( v ).multiplyScalar( scalar );

	}

	projectOnPlane( planeNormal ) {

		_vector$c.copy( this ).projectOnVector( planeNormal );

		return this.sub( _vector$c );

	}

	reflect( normal ) {

		// reflect incident vector off plane orthogonal to normal
		// normal is assumed to have unit length

		return this.sub( _vector$c.copy( normal ).multiplyScalar( 2 * this.dot( normal ) ) );

	}

	angleTo( v ) {

		const denominator = Math.sqrt( this.lengthSq() * v.lengthSq() );

		if ( denominator === 0 ) return Math.PI / 2;

		const theta = this.dot( v ) / denominator;

		// clamp, to handle numerical problems

		return Math.acos( clamp( theta, - 1, 1 ) );

	}

	distanceTo( v ) {

		return Math.sqrt( this.distanceToSquared( v ) );

	}

	distanceToSquared( v ) {

		const dx = this.x - v.x, dy = this.y - v.y, dz = this.z - v.z;

		return dx * dx + dy * dy + dz * dz;

	}

	manhattanDistanceTo( v ) {

		return Math.abs( this.x - v.x ) + Math.abs( this.y - v.y ) + Math.abs( this.z - v.z );

	}

	setFromSpherical( s ) {

		return this.setFromSphericalCoords( s.radius, s.phi, s.theta );

	}

	setFromSphericalCoords( radius, phi, theta ) {

		const sinPhiRadius = Math.sin( phi ) * radius;

		this.x = sinPhiRadius * Math.sin( theta );
		this.y = Math.cos( phi ) * radius;
		this.z = sinPhiRadius * Math.cos( theta );

		return this;

	}

	setFromCylindrical( c ) {

		return this.setFromCylindricalCoords( c.radius, c.theta, c.y );

	}

	setFromCylindricalCoords( radius, theta, y ) {

		this.x = radius * Math.sin( theta );
		this.y = y;
		this.z = radius * Math.cos( theta );

		return this;

	}

	setFromMatrixPosition( m ) {

		const e = m.elements;

		this.x = e[ 12 ];
		this.y = e[ 13 ];
		this.z = e[ 14 ];

		return this;

	}

	setFromMatrixScale( m ) {

		const sx = this.setFromMatrixColumn( m, 0 ).length();
		const sy = this.setFromMatrixColumn( m, 1 ).length();
		const sz = this.setFromMatrixColumn( m, 2 ).length();

		this.x = sx;
		this.y = sy;
		this.z = sz;

		return this;

	}

	setFromMatrixColumn( m, index ) {

		return this.fromArray( m.elements, index * 4 );

	}

	setFromMatrix3Column( m, index ) {

		return this.fromArray( m.elements, index * 3 );

	}

	setFromEuler( e ) {

		this.x = e._x;
		this.y = e._y;
		this.z = e._z;

		return this;

	}

	setFromColor( c ) {

		this.x = c.r;
		this.y = c.g;
		this.z = c.b;

		return this;

	}

	equals( v ) {

		return ( ( v.x === this.x ) && ( v.y === this.y ) && ( v.z === this.z ) );

	}

	fromArray( array, offset = 0 ) {

		this.x = array[ offset ];
		this.y = array[ offset + 1 ];
		this.z = array[ offset + 2 ];

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this.x;
		array[ offset + 1 ] = this.y;
		array[ offset + 2 ] = this.z;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this.x = attribute.getX( index );
		this.y = attribute.getY( index );
		this.z = attribute.getZ( index );

		return this;

	}

	random() {

		this.x = Math.random();
		this.y = Math.random();
		this.z = Math.random();

		return this;

	}

	randomDirection() {

		// https://mathworld.wolfram.com/SpherePointPicking.html

		const theta = Math.random() * Math.PI * 2;
		const u = Math.random() * 2 - 1;
		const c = Math.sqrt( 1 - u * u );

		this.x = c * Math.cos( theta );
		this.y = u;
		this.z = c * Math.sin( theta );

		return this;

	}

	*[ Symbol.iterator ]() {

		yield this.x;
		yield this.y;
		yield this.z;

	}

}

const _vector$c = /*@__PURE__*/ new Vector3();
const _quaternion$4 = /*@__PURE__*/ new Quaternion();

class Matrix4 {

	constructor( n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44 ) {

		Matrix4.prototype.isMatrix4 = true;

		this.elements = [

			1, 0, 0, 0,
			0, 1, 0, 0,
			0, 0, 1, 0,
			0, 0, 0, 1

		];

		if ( n11 !== undefined ) {

			this.set( n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44 );

		}

	}

	set( n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44 ) {

		const te = this.elements;

		te[ 0 ] = n11; te[ 4 ] = n12; te[ 8 ] = n13; te[ 12 ] = n14;
		te[ 1 ] = n21; te[ 5 ] = n22; te[ 9 ] = n23; te[ 13 ] = n24;
		te[ 2 ] = n31; te[ 6 ] = n32; te[ 10 ] = n33; te[ 14 ] = n34;
		te[ 3 ] = n41; te[ 7 ] = n42; te[ 11 ] = n43; te[ 15 ] = n44;

		return this;

	}

	identity() {

		this.set(

			1, 0, 0, 0,
			0, 1, 0, 0,
			0, 0, 1, 0,
			0, 0, 0, 1

		);

		return this;

	}

	clone() {

		return new Matrix4().fromArray( this.elements );

	}

	copy( m ) {

		const te = this.elements;
		const me = m.elements;

		te[ 0 ] = me[ 0 ]; te[ 1 ] = me[ 1 ]; te[ 2 ] = me[ 2 ]; te[ 3 ] = me[ 3 ];
		te[ 4 ] = me[ 4 ]; te[ 5 ] = me[ 5 ]; te[ 6 ] = me[ 6 ]; te[ 7 ] = me[ 7 ];
		te[ 8 ] = me[ 8 ]; te[ 9 ] = me[ 9 ]; te[ 10 ] = me[ 10 ]; te[ 11 ] = me[ 11 ];
		te[ 12 ] = me[ 12 ]; te[ 13 ] = me[ 13 ]; te[ 14 ] = me[ 14 ]; te[ 15 ] = me[ 15 ];

		return this;

	}

	copyPosition( m ) {

		const te = this.elements, me = m.elements;

		te[ 12 ] = me[ 12 ];
		te[ 13 ] = me[ 13 ];
		te[ 14 ] = me[ 14 ];

		return this;

	}

	setFromMatrix3( m ) {

		const me = m.elements;

		this.set(

			me[ 0 ], me[ 3 ], me[ 6 ], 0,
			me[ 1 ], me[ 4 ], me[ 7 ], 0,
			me[ 2 ], me[ 5 ], me[ 8 ], 0,
			0, 0, 0, 1

		);

		return this;

	}

	extractBasis( xAxis, yAxis, zAxis ) {

		xAxis.setFromMatrixColumn( this, 0 );
		yAxis.setFromMatrixColumn( this, 1 );
		zAxis.setFromMatrixColumn( this, 2 );

		return this;

	}

	makeBasis( xAxis, yAxis, zAxis ) {

		this.set(
			xAxis.x, yAxis.x, zAxis.x, 0,
			xAxis.y, yAxis.y, zAxis.y, 0,
			xAxis.z, yAxis.z, zAxis.z, 0,
			0, 0, 0, 1
		);

		return this;

	}

	extractRotation( m ) {

		// this method does not support reflection matrices

		const te = this.elements;
		const me = m.elements;

		const scaleX = 1 / _v1$5$1.setFromMatrixColumn( m, 0 ).length();
		const scaleY = 1 / _v1$5$1.setFromMatrixColumn( m, 1 ).length();
		const scaleZ = 1 / _v1$5$1.setFromMatrixColumn( m, 2 ).length();

		te[ 0 ] = me[ 0 ] * scaleX;
		te[ 1 ] = me[ 1 ] * scaleX;
		te[ 2 ] = me[ 2 ] * scaleX;
		te[ 3 ] = 0;

		te[ 4 ] = me[ 4 ] * scaleY;
		te[ 5 ] = me[ 5 ] * scaleY;
		te[ 6 ] = me[ 6 ] * scaleY;
		te[ 7 ] = 0;

		te[ 8 ] = me[ 8 ] * scaleZ;
		te[ 9 ] = me[ 9 ] * scaleZ;
		te[ 10 ] = me[ 10 ] * scaleZ;
		te[ 11 ] = 0;

		te[ 12 ] = 0;
		te[ 13 ] = 0;
		te[ 14 ] = 0;
		te[ 15 ] = 1;

		return this;

	}

	makeRotationFromEuler( euler ) {

		const te = this.elements;

		const x = euler.x, y = euler.y, z = euler.z;
		const a = Math.cos( x ), b = Math.sin( x );
		const c = Math.cos( y ), d = Math.sin( y );
		const e = Math.cos( z ), f = Math.sin( z );

		if ( euler.order === 'XYZ' ) {

			const ae = a * e, af = a * f, be = b * e, bf = b * f;

			te[ 0 ] = c * e;
			te[ 4 ] = - c * f;
			te[ 8 ] = d;

			te[ 1 ] = af + be * d;
			te[ 5 ] = ae - bf * d;
			te[ 9 ] = - b * c;

			te[ 2 ] = bf - ae * d;
			te[ 6 ] = be + af * d;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'YXZ' ) {

			const ce = c * e, cf = c * f, de = d * e, df = d * f;

			te[ 0 ] = ce + df * b;
			te[ 4 ] = de * b - cf;
			te[ 8 ] = a * d;

			te[ 1 ] = a * f;
			te[ 5 ] = a * e;
			te[ 9 ] = - b;

			te[ 2 ] = cf * b - de;
			te[ 6 ] = df + ce * b;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'ZXY' ) {

			const ce = c * e, cf = c * f, de = d * e, df = d * f;

			te[ 0 ] = ce - df * b;
			te[ 4 ] = - a * f;
			te[ 8 ] = de + cf * b;

			te[ 1 ] = cf + de * b;
			te[ 5 ] = a * e;
			te[ 9 ] = df - ce * b;

			te[ 2 ] = - a * d;
			te[ 6 ] = b;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'ZYX' ) {

			const ae = a * e, af = a * f, be = b * e, bf = b * f;

			te[ 0 ] = c * e;
			te[ 4 ] = be * d - af;
			te[ 8 ] = ae * d + bf;

			te[ 1 ] = c * f;
			te[ 5 ] = bf * d + ae;
			te[ 9 ] = af * d - be;

			te[ 2 ] = - d;
			te[ 6 ] = b * c;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'YZX' ) {

			const ac = a * c, ad = a * d, bc = b * c, bd = b * d;

			te[ 0 ] = c * e;
			te[ 4 ] = bd - ac * f;
			te[ 8 ] = bc * f + ad;

			te[ 1 ] = f;
			te[ 5 ] = a * e;
			te[ 9 ] = - b * e;

			te[ 2 ] = - d * e;
			te[ 6 ] = ad * f + bc;
			te[ 10 ] = ac - bd * f;

		} else if ( euler.order === 'XZY' ) {

			const ac = a * c, ad = a * d, bc = b * c, bd = b * d;

			te[ 0 ] = c * e;
			te[ 4 ] = - f;
			te[ 8 ] = d * e;

			te[ 1 ] = ac * f + bd;
			te[ 5 ] = a * e;
			te[ 9 ] = ad * f - bc;

			te[ 2 ] = bc * f - ad;
			te[ 6 ] = b * e;
			te[ 10 ] = bd * f + ac;

		}

		// bottom row
		te[ 3 ] = 0;
		te[ 7 ] = 0;
		te[ 11 ] = 0;

		// last column
		te[ 12 ] = 0;
		te[ 13 ] = 0;
		te[ 14 ] = 0;
		te[ 15 ] = 1;

		return this;

	}

	makeRotationFromQuaternion( q ) {

		return this.compose( _zero, q, _one );

	}

	lookAt( eye, target, up ) {

		const te = this.elements;

		_z.subVectors( eye, target );

		if ( _z.lengthSq() === 0 ) {

			// eye and target are in the same position

			_z.z = 1;

		}

		_z.normalize();
		_x.crossVectors( up, _z );

		if ( _x.lengthSq() === 0 ) {

			// up and z are parallel

			if ( Math.abs( up.z ) === 1 ) {

				_z.x += 0.0001;

			} else {

				_z.z += 0.0001;

			}

			_z.normalize();
			_x.crossVectors( up, _z );

		}

		_x.normalize();
		_y.crossVectors( _z, _x );

		te[ 0 ] = _x.x; te[ 4 ] = _y.x; te[ 8 ] = _z.x;
		te[ 1 ] = _x.y; te[ 5 ] = _y.y; te[ 9 ] = _z.y;
		te[ 2 ] = _x.z; te[ 6 ] = _y.z; te[ 10 ] = _z.z;

		return this;

	}

	multiply( m ) {

		return this.multiplyMatrices( this, m );

	}

	premultiply( m ) {

		return this.multiplyMatrices( m, this );

	}

	multiplyMatrices( a, b ) {

		const ae = a.elements;
		const be = b.elements;
		const te = this.elements;

		const a11 = ae[ 0 ], a12 = ae[ 4 ], a13 = ae[ 8 ], a14 = ae[ 12 ];
		const a21 = ae[ 1 ], a22 = ae[ 5 ], a23 = ae[ 9 ], a24 = ae[ 13 ];
		const a31 = ae[ 2 ], a32 = ae[ 6 ], a33 = ae[ 10 ], a34 = ae[ 14 ];
		const a41 = ae[ 3 ], a42 = ae[ 7 ], a43 = ae[ 11 ], a44 = ae[ 15 ];

		const b11 = be[ 0 ], b12 = be[ 4 ], b13 = be[ 8 ], b14 = be[ 12 ];
		const b21 = be[ 1 ], b22 = be[ 5 ], b23 = be[ 9 ], b24 = be[ 13 ];
		const b31 = be[ 2 ], b32 = be[ 6 ], b33 = be[ 10 ], b34 = be[ 14 ];
		const b41 = be[ 3 ], b42 = be[ 7 ], b43 = be[ 11 ], b44 = be[ 15 ];

		te[ 0 ] = a11 * b11 + a12 * b21 + a13 * b31 + a14 * b41;
		te[ 4 ] = a11 * b12 + a12 * b22 + a13 * b32 + a14 * b42;
		te[ 8 ] = a11 * b13 + a12 * b23 + a13 * b33 + a14 * b43;
		te[ 12 ] = a11 * b14 + a12 * b24 + a13 * b34 + a14 * b44;

		te[ 1 ] = a21 * b11 + a22 * b21 + a23 * b31 + a24 * b41;
		te[ 5 ] = a21 * b12 + a22 * b22 + a23 * b32 + a24 * b42;
		te[ 9 ] = a21 * b13 + a22 * b23 + a23 * b33 + a24 * b43;
		te[ 13 ] = a21 * b14 + a22 * b24 + a23 * b34 + a24 * b44;

		te[ 2 ] = a31 * b11 + a32 * b21 + a33 * b31 + a34 * b41;
		te[ 6 ] = a31 * b12 + a32 * b22 + a33 * b32 + a34 * b42;
		te[ 10 ] = a31 * b13 + a32 * b23 + a33 * b33 + a34 * b43;
		te[ 14 ] = a31 * b14 + a32 * b24 + a33 * b34 + a34 * b44;

		te[ 3 ] = a41 * b11 + a42 * b21 + a43 * b31 + a44 * b41;
		te[ 7 ] = a41 * b12 + a42 * b22 + a43 * b32 + a44 * b42;
		te[ 11 ] = a41 * b13 + a42 * b23 + a43 * b33 + a44 * b43;
		te[ 15 ] = a41 * b14 + a42 * b24 + a43 * b34 + a44 * b44;

		return this;

	}

	multiplyScalar( s ) {

		const te = this.elements;

		te[ 0 ] *= s; te[ 4 ] *= s; te[ 8 ] *= s; te[ 12 ] *= s;
		te[ 1 ] *= s; te[ 5 ] *= s; te[ 9 ] *= s; te[ 13 ] *= s;
		te[ 2 ] *= s; te[ 6 ] *= s; te[ 10 ] *= s; te[ 14 ] *= s;
		te[ 3 ] *= s; te[ 7 ] *= s; te[ 11 ] *= s; te[ 15 ] *= s;

		return this;

	}

	determinant() {

		const te = this.elements;

		const n11 = te[ 0 ], n12 = te[ 4 ], n13 = te[ 8 ], n14 = te[ 12 ];
		const n21 = te[ 1 ], n22 = te[ 5 ], n23 = te[ 9 ], n24 = te[ 13 ];
		const n31 = te[ 2 ], n32 = te[ 6 ], n33 = te[ 10 ], n34 = te[ 14 ];
		const n41 = te[ 3 ], n42 = te[ 7 ], n43 = te[ 11 ], n44 = te[ 15 ];

		//TODO: make this more efficient
		//( based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm )

		return (
			n41 * (
				+ n14 * n23 * n32
				 - n13 * n24 * n32
				 - n14 * n22 * n33
				 + n12 * n24 * n33
				 + n13 * n22 * n34
				 - n12 * n23 * n34
			) +
			n42 * (
				+ n11 * n23 * n34
				 - n11 * n24 * n33
				 + n14 * n21 * n33
				 - n13 * n21 * n34
				 + n13 * n24 * n31
				 - n14 * n23 * n31
			) +
			n43 * (
				+ n11 * n24 * n32
				 - n11 * n22 * n34
				 - n14 * n21 * n32
				 + n12 * n21 * n34
				 + n14 * n22 * n31
				 - n12 * n24 * n31
			) +
			n44 * (
				- n13 * n22 * n31
				 - n11 * n23 * n32
				 + n11 * n22 * n33
				 + n13 * n21 * n32
				 - n12 * n21 * n33
				 + n12 * n23 * n31
			)

		);

	}

	transpose() {

		const te = this.elements;
		let tmp;

		tmp = te[ 1 ]; te[ 1 ] = te[ 4 ]; te[ 4 ] = tmp;
		tmp = te[ 2 ]; te[ 2 ] = te[ 8 ]; te[ 8 ] = tmp;
		tmp = te[ 6 ]; te[ 6 ] = te[ 9 ]; te[ 9 ] = tmp;

		tmp = te[ 3 ]; te[ 3 ] = te[ 12 ]; te[ 12 ] = tmp;
		tmp = te[ 7 ]; te[ 7 ] = te[ 13 ]; te[ 13 ] = tmp;
		tmp = te[ 11 ]; te[ 11 ] = te[ 14 ]; te[ 14 ] = tmp;

		return this;

	}

	setPosition( x, y, z ) {

		const te = this.elements;

		if ( x.isVector3 ) {

			te[ 12 ] = x.x;
			te[ 13 ] = x.y;
			te[ 14 ] = x.z;

		} else {

			te[ 12 ] = x;
			te[ 13 ] = y;
			te[ 14 ] = z;

		}

		return this;

	}

	invert() {

		// based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm
		const te = this.elements,

			n11 = te[ 0 ], n21 = te[ 1 ], n31 = te[ 2 ], n41 = te[ 3 ],
			n12 = te[ 4 ], n22 = te[ 5 ], n32 = te[ 6 ], n42 = te[ 7 ],
			n13 = te[ 8 ], n23 = te[ 9 ], n33 = te[ 10 ], n43 = te[ 11 ],
			n14 = te[ 12 ], n24 = te[ 13 ], n34 = te[ 14 ], n44 = te[ 15 ],

			t11 = n23 * n34 * n42 - n24 * n33 * n42 + n24 * n32 * n43 - n22 * n34 * n43 - n23 * n32 * n44 + n22 * n33 * n44,
			t12 = n14 * n33 * n42 - n13 * n34 * n42 - n14 * n32 * n43 + n12 * n34 * n43 + n13 * n32 * n44 - n12 * n33 * n44,
			t13 = n13 * n24 * n42 - n14 * n23 * n42 + n14 * n22 * n43 - n12 * n24 * n43 - n13 * n22 * n44 + n12 * n23 * n44,
			t14 = n14 * n23 * n32 - n13 * n24 * n32 - n14 * n22 * n33 + n12 * n24 * n33 + n13 * n22 * n34 - n12 * n23 * n34;

		const det = n11 * t11 + n21 * t12 + n31 * t13 + n41 * t14;

		if ( det === 0 ) return this.set( 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0 );

		const detInv = 1 / det;

		te[ 0 ] = t11 * detInv;
		te[ 1 ] = ( n24 * n33 * n41 - n23 * n34 * n41 - n24 * n31 * n43 + n21 * n34 * n43 + n23 * n31 * n44 - n21 * n33 * n44 ) * detInv;
		te[ 2 ] = ( n22 * n34 * n41 - n24 * n32 * n41 + n24 * n31 * n42 - n21 * n34 * n42 - n22 * n31 * n44 + n21 * n32 * n44 ) * detInv;
		te[ 3 ] = ( n23 * n32 * n41 - n22 * n33 * n41 - n23 * n31 * n42 + n21 * n33 * n42 + n22 * n31 * n43 - n21 * n32 * n43 ) * detInv;

		te[ 4 ] = t12 * detInv;
		te[ 5 ] = ( n13 * n34 * n41 - n14 * n33 * n41 + n14 * n31 * n43 - n11 * n34 * n43 - n13 * n31 * n44 + n11 * n33 * n44 ) * detInv;
		te[ 6 ] = ( n14 * n32 * n41 - n12 * n34 * n41 - n14 * n31 * n42 + n11 * n34 * n42 + n12 * n31 * n44 - n11 * n32 * n44 ) * detInv;
		te[ 7 ] = ( n12 * n33 * n41 - n13 * n32 * n41 + n13 * n31 * n42 - n11 * n33 * n42 - n12 * n31 * n43 + n11 * n32 * n43 ) * detInv;

		te[ 8 ] = t13 * detInv;
		te[ 9 ] = ( n14 * n23 * n41 - n13 * n24 * n41 - n14 * n21 * n43 + n11 * n24 * n43 + n13 * n21 * n44 - n11 * n23 * n44 ) * detInv;
		te[ 10 ] = ( n12 * n24 * n41 - n14 * n22 * n41 + n14 * n21 * n42 - n11 * n24 * n42 - n12 * n21 * n44 + n11 * n22 * n44 ) * detInv;
		te[ 11 ] = ( n13 * n22 * n41 - n12 * n23 * n41 - n13 * n21 * n42 + n11 * n23 * n42 + n12 * n21 * n43 - n11 * n22 * n43 ) * detInv;

		te[ 12 ] = t14 * detInv;
		te[ 13 ] = ( n13 * n24 * n31 - n14 * n23 * n31 + n14 * n21 * n33 - n11 * n24 * n33 - n13 * n21 * n34 + n11 * n23 * n34 ) * detInv;
		te[ 14 ] = ( n14 * n22 * n31 - n12 * n24 * n31 - n14 * n21 * n32 + n11 * n24 * n32 + n12 * n21 * n34 - n11 * n22 * n34 ) * detInv;
		te[ 15 ] = ( n12 * n23 * n31 - n13 * n22 * n31 + n13 * n21 * n32 - n11 * n23 * n32 - n12 * n21 * n33 + n11 * n22 * n33 ) * detInv;

		return this;

	}

	scale( v ) {

		const te = this.elements;
		const x = v.x, y = v.y, z = v.z;

		te[ 0 ] *= x; te[ 4 ] *= y; te[ 8 ] *= z;
		te[ 1 ] *= x; te[ 5 ] *= y; te[ 9 ] *= z;
		te[ 2 ] *= x; te[ 6 ] *= y; te[ 10 ] *= z;
		te[ 3 ] *= x; te[ 7 ] *= y; te[ 11 ] *= z;

		return this;

	}

	getMaxScaleOnAxis() {

		const te = this.elements;

		const scaleXSq = te[ 0 ] * te[ 0 ] + te[ 1 ] * te[ 1 ] + te[ 2 ] * te[ 2 ];
		const scaleYSq = te[ 4 ] * te[ 4 ] + te[ 5 ] * te[ 5 ] + te[ 6 ] * te[ 6 ];
		const scaleZSq = te[ 8 ] * te[ 8 ] + te[ 9 ] * te[ 9 ] + te[ 10 ] * te[ 10 ];

		return Math.sqrt( Math.max( scaleXSq, scaleYSq, scaleZSq ) );

	}

	makeTranslation( x, y, z ) {

		if ( x.isVector3 ) {

			this.set(

				1, 0, 0, x.x,
				0, 1, 0, x.y,
				0, 0, 1, x.z,
				0, 0, 0, 1

			);

		} else {

			this.set(

				1, 0, 0, x,
				0, 1, 0, y,
				0, 0, 1, z,
				0, 0, 0, 1

			);

		}

		return this;

	}

	makeRotationX( theta ) {

		const c = Math.cos( theta ), s = Math.sin( theta );

		this.set(

			1, 0, 0, 0,
			0, c, - s, 0,
			0, s, c, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeRotationY( theta ) {

		const c = Math.cos( theta ), s = Math.sin( theta );

		this.set(

			 c, 0, s, 0,
			 0, 1, 0, 0,
			- s, 0, c, 0,
			 0, 0, 0, 1

		);

		return this;

	}

	makeRotationZ( theta ) {

		const c = Math.cos( theta ), s = Math.sin( theta );

		this.set(

			c, - s, 0, 0,
			s, c, 0, 0,
			0, 0, 1, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeRotationAxis( axis, angle ) {

		// Based on http://www.gamedev.net/reference/articles/article1199.asp

		const c = Math.cos( angle );
		const s = Math.sin( angle );
		const t = 1 - c;
		const x = axis.x, y = axis.y, z = axis.z;
		const tx = t * x, ty = t * y;

		this.set(

			tx * x + c, tx * y - s * z, tx * z + s * y, 0,
			tx * y + s * z, ty * y + c, ty * z - s * x, 0,
			tx * z - s * y, ty * z + s * x, t * z * z + c, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeScale( x, y, z ) {

		this.set(

			x, 0, 0, 0,
			0, y, 0, 0,
			0, 0, z, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeShear( xy, xz, yx, yz, zx, zy ) {

		this.set(

			1, yx, zx, 0,
			xy, 1, zy, 0,
			xz, yz, 1, 0,
			0, 0, 0, 1

		);

		return this;

	}

	compose( position, quaternion, scale ) {

		const te = this.elements;

		const x = quaternion._x, y = quaternion._y, z = quaternion._z, w = quaternion._w;
		const x2 = x + x,	y2 = y + y, z2 = z + z;
		const xx = x * x2, xy = x * y2, xz = x * z2;
		const yy = y * y2, yz = y * z2, zz = z * z2;
		const wx = w * x2, wy = w * y2, wz = w * z2;

		const sx = scale.x, sy = scale.y, sz = scale.z;

		te[ 0 ] = ( 1 - ( yy + zz ) ) * sx;
		te[ 1 ] = ( xy + wz ) * sx;
		te[ 2 ] = ( xz - wy ) * sx;
		te[ 3 ] = 0;

		te[ 4 ] = ( xy - wz ) * sy;
		te[ 5 ] = ( 1 - ( xx + zz ) ) * sy;
		te[ 6 ] = ( yz + wx ) * sy;
		te[ 7 ] = 0;

		te[ 8 ] = ( xz + wy ) * sz;
		te[ 9 ] = ( yz - wx ) * sz;
		te[ 10 ] = ( 1 - ( xx + yy ) ) * sz;
		te[ 11 ] = 0;

		te[ 12 ] = position.x;
		te[ 13 ] = position.y;
		te[ 14 ] = position.z;
		te[ 15 ] = 1;

		return this;

	}

	decompose( position, quaternion, scale ) {

		const te = this.elements;

		let sx = _v1$5$1.set( te[ 0 ], te[ 1 ], te[ 2 ] ).length();
		const sy = _v1$5$1.set( te[ 4 ], te[ 5 ], te[ 6 ] ).length();
		const sz = _v1$5$1.set( te[ 8 ], te[ 9 ], te[ 10 ] ).length();

		// if determine is negative, we need to invert one scale
		const det = this.determinant();
		if ( det < 0 ) sx = - sx;

		position.x = te[ 12 ];
		position.y = te[ 13 ];
		position.z = te[ 14 ];

		// scale the rotation part
		_m1$4.copy( this );

		const invSX = 1 / sx;
		const invSY = 1 / sy;
		const invSZ = 1 / sz;

		_m1$4.elements[ 0 ] *= invSX;
		_m1$4.elements[ 1 ] *= invSX;
		_m1$4.elements[ 2 ] *= invSX;

		_m1$4.elements[ 4 ] *= invSY;
		_m1$4.elements[ 5 ] *= invSY;
		_m1$4.elements[ 6 ] *= invSY;

		_m1$4.elements[ 8 ] *= invSZ;
		_m1$4.elements[ 9 ] *= invSZ;
		_m1$4.elements[ 10 ] *= invSZ;

		quaternion.setFromRotationMatrix( _m1$4 );

		scale.x = sx;
		scale.y = sy;
		scale.z = sz;

		return this;

	}

	makePerspective( left, right, top, bottom, near, far, coordinateSystem = WebGLCoordinateSystem ) {

		const te = this.elements;
		const x = 2 * near / ( right - left );
		const y = 2 * near / ( top - bottom );

		const a = ( right + left ) / ( right - left );
		const b = ( top + bottom ) / ( top - bottom );

		let c, d;

		if ( coordinateSystem === WebGLCoordinateSystem ) {

			c = - ( far + near ) / ( far - near );
			d = ( - 2 * far * near ) / ( far - near );

		} else if ( coordinateSystem === WebGPUCoordinateSystem ) {

			c = - far / ( far - near );
			d = ( - far * near ) / ( far - near );

		} else {

			throw new Error( 'THREE.Matrix4.makePerspective(): Invalid coordinate system: ' + coordinateSystem );

		}

		te[ 0 ] = x;	te[ 4 ] = 0;	te[ 8 ] = a; 	te[ 12 ] = 0;
		te[ 1 ] = 0;	te[ 5 ] = y;	te[ 9 ] = b; 	te[ 13 ] = 0;
		te[ 2 ] = 0;	te[ 6 ] = 0;	te[ 10 ] = c; 	te[ 14 ] = d;
		te[ 3 ] = 0;	te[ 7 ] = 0;	te[ 11 ] = - 1;	te[ 15 ] = 0;

		return this;

	}

	makeOrthographic( left, right, top, bottom, near, far, coordinateSystem = WebGLCoordinateSystem ) {

		const te = this.elements;
		const w = 1.0 / ( right - left );
		const h = 1.0 / ( top - bottom );
		const p = 1.0 / ( far - near );

		const x = ( right + left ) * w;
		const y = ( top + bottom ) * h;

		let z, zInv;

		if ( coordinateSystem === WebGLCoordinateSystem ) {

			z = ( far + near ) * p;
			zInv = - 2 * p;

		} else if ( coordinateSystem === WebGPUCoordinateSystem ) {

			z = near * p;
			zInv = - 1 * p;

		} else {

			throw new Error( 'THREE.Matrix4.makeOrthographic(): Invalid coordinate system: ' + coordinateSystem );

		}

		te[ 0 ] = 2 * w;	te[ 4 ] = 0;		te[ 8 ] = 0; 		te[ 12 ] = - x;
		te[ 1 ] = 0; 		te[ 5 ] = 2 * h;	te[ 9 ] = 0; 		te[ 13 ] = - y;
		te[ 2 ] = 0; 		te[ 6 ] = 0;		te[ 10 ] = zInv;	te[ 14 ] = - z;
		te[ 3 ] = 0; 		te[ 7 ] = 0;		te[ 11 ] = 0;		te[ 15 ] = 1;

		return this;

	}

	equals( matrix ) {

		const te = this.elements;
		const me = matrix.elements;

		for ( let i = 0; i < 16; i ++ ) {

			if ( te[ i ] !== me[ i ] ) return false;

		}

		return true;

	}

	fromArray( array, offset = 0 ) {

		for ( let i = 0; i < 16; i ++ ) {

			this.elements[ i ] = array[ i + offset ];

		}

		return this;

	}

	toArray( array = [], offset = 0 ) {

		const te = this.elements;

		array[ offset ] = te[ 0 ];
		array[ offset + 1 ] = te[ 1 ];
		array[ offset + 2 ] = te[ 2 ];
		array[ offset + 3 ] = te[ 3 ];

		array[ offset + 4 ] = te[ 4 ];
		array[ offset + 5 ] = te[ 5 ];
		array[ offset + 6 ] = te[ 6 ];
		array[ offset + 7 ] = te[ 7 ];

		array[ offset + 8 ] = te[ 8 ];
		array[ offset + 9 ] = te[ 9 ];
		array[ offset + 10 ] = te[ 10 ];
		array[ offset + 11 ] = te[ 11 ];

		array[ offset + 12 ] = te[ 12 ];
		array[ offset + 13 ] = te[ 13 ];
		array[ offset + 14 ] = te[ 14 ];
		array[ offset + 15 ] = te[ 15 ];

		return array;

	}

}

const _v1$5$1 = /*@__PURE__*/ new Vector3();
const _m1$4 = /*@__PURE__*/ new Matrix4();
const _zero = /*@__PURE__*/ new Vector3( 0, 0, 0 );
const _one = /*@__PURE__*/ new Vector3( 1, 1, 1 );
const _x = /*@__PURE__*/ new Vector3();
const _y = /*@__PURE__*/ new Vector3();
const _z = /*@__PURE__*/ new Vector3();

if ( typeof __THREE_DEVTOOLS__ !== 'undefined' ) {

	__THREE_DEVTOOLS__.dispatchEvent( new CustomEvent( 'register', { detail: {
		revision: REVISION,
	} } ) );

}

if ( typeof window !== 'undefined' ) {

	if ( window.__THREE__ ) {

		console.warn( 'WARNING: Multiple instances of Three.js being imported.' );

	} else {

		window.__THREE__ = REVISION;

	}

}// 布置构件方位类型
const OrientationType = {
    DIRECTION: 1, // 方向
    K: 2, // k点
    ANGLE: 3 // 轴转角
};/**
 * 结构构件组件
 */
class StructComponentInfo {
    constructor(uuid, primitiveUuid) {
        this.properties = {};
        this.uuid = uuid;
        this.id = uuid;
        this.primitiveUuid = primitiveUuid;
    }
}/**
 * 两点生成线性结构构件
 */
class EventHandlerCalStructComTwoPoint extends EventHandlerBase {
    run(data) {
        // 从缓存获取结构构件数据
        const graphCache = this.node.graph.cache;
        const structComData = graphCache.data.structComData;
        const storage = this.getPythaStorage();
        // 数据不正确
        if (structComData == null || Object.keys(structComData).length == 0) {
            return true;
        }
        const coordinateStore = storage.coordinateStore;
        const workPlane = coordinateStore.workPlane;
        if (graphCache.data.startPoint != null) {
            startP$4.copy(graphCache.data.startPoint);
        }
        else {
            startP$4.copy(graphCache.pointerDownList[graphCache.pointerDownList.length - 1].point);
        }
        if (graphCache.data.endPoint != null) {
            moveP$4.copy(graphCache.data.endPoint);
        }
        else {
            moveP$4.copy(graphCache.pointerMove.point);
        }
        let initMatrix = graphCache.data.initMatrix;
        const primitive = graphCache.data.primitive;
        const geo = graphCache.data.geo;
        if (!geo || !primitive)
            return true;
        if (!primitive.localModelMatrix) {
            primitive.localModelMatrix = new Matrix4();
        }
        if (!initMatrix) {
            initMatrix = primitive.localModelMatrix.clone();
            graphCache.data.initMatrix = initMatrix;
        }
        // 每次重置到初始状态
        primitive.localModelMatrix.copy(initMatrix);
        primitive.updateWorldModelMatrix(false, true);
        primitive.needRedraw = true;
        geo.height = 1;
        // 轴转角、k轴
        const orientationType = structComData.orientationType;
        const x = structComData.x;
        const y = structComData.y;
        const z = structComData.z;
        const angle = structComData.angle;
        // 根据轴转角计算
        if (OrientationType.K === orientationType && x != null && y != null && z != null) {
            // point指向k，point指向move是x轴，x轴叉乘k得到-y轴
            _p$7.set(x, y, z);
            _p1$6.subVectors(_p$7, startP$4).normalize(); // point指向k
            _v1$b.subVectors(moveP$4, startP$4).normalize(); // x轴
            _v2$9.crossVectors(_v1$b, _p1$6).multiplyScalar(-1).normalize(); // y轴
            _v3$5.crossVectors(_v1$b, _v2$9).normalize(); // z轴
            // 构建变换矩阵
            matrix$3.set(_v1$b.x, _v2$9.x, _v3$5.x, 0, _v1$b.y, _v2$9.y, _v3$5.y, 0, _v1$b.z, _v2$9.z, _v3$5.z, 0, 0, 0, 0, 1);
            primitive.localModelMatrix.copy(matrix$3);
            primitive.updateWorldModelMatrix(false, true);
        }
        else if (OrientationType.DIRECTION === orientationType && x != null && y != null && z != null) {
            // point指向move是x轴，x轴叉乘k得到-y轴
            _p$7.set(x, y, z);
            _v1$b.subVectors(moveP$4, startP$4).normalize(); // x轴
            _v2$9.crossVectors(_v1$b, _p$7).multiplyScalar(-1).normalize(); // y轴
            _v3$5.crossVectors(_v1$b, _v2$9).normalize(); // z轴
            // 构建变换矩阵
            matrix$3.set(_v1$b.x, _v2$9.x, _v3$5.x, 0, _v1$b.y, _v2$9.y, _v3$5.y, 0, _v1$b.z, _v2$9.z, _v3$5.z, 0, 0, 0, 0, 1);
            primitive.localModelMatrix.copy(matrix$3);
            primitive.updateWorldModelMatrix(false, true);
        }
        else {
            // 计算旋转角度
            workPlane.getNormalizeX(_p$7);
            _vec$3.subVectors(moveP$4, startP$4).normalize();
            // 计算旋转四元数
            _quaternion$3.setFromUnitVectors(_p$7, _vec$3);
            const rotationMatrix = new Matrix4();
            rotationMatrix.makeRotationFromQuaternion(_quaternion$3);
            primitive.localModelMatrix.premultiply(rotationMatrix);
            primitive.updateWorldModelMatrix(false, true);
        }
        _v1$b.setFromMatrixColumn(primitive.localModelMatrix, 0);
        // if (OrientationType.ANGLE === orientationType && angle != null && Math.abs(angle) > 1e-4) {
        if (angle != null && Math.abs(angle) > 1e-4) {
            primitive.rotate(workPlane.getP(), _v1$b, Math.PI * angle / 180);
        }
        // 计算偏移和高度
        const height = this.calcOffset();
        primitive.move(startP$4);
        geo.height = height;
        let structComInfos = graphCache.data['struct_com_info'];
        if (!structComInfos || !Array.isArray(structComInfos) || structComInfos.length === 0) {
            const uuid = BaseUtils.genUUID();
            const structComInfo = new StructComponentInfo(uuid, primitive.uuid);
            structComInfos = [];
            structComInfos.push(structComInfo);
            graphCache.data['struct_com_info'] = structComInfos;
        }
        const structComInfo = structComInfos[0];
        // 更新存储构件信息
        structComInfo.properties = [
            { key: 'sectionId', value: structComData.sectionId, category: 'structure' },
            { key: 'materialId', value: structComData.materialId, category: 'material' },
            { key: 'thickness', value: structComData.thickness, category: 'structure' },
            { key: 'height', value: height, category: 'geo' },
            { key: 'x', value: structComData.x, category: 'geo' },
            { key: 'y', value: structComData.y, category: 'geo' },
            { key: 'z', value: structComData.z, category: 'geo' },
            { key: 'angle', value: structComData.angle, category: 'geo' },
            { key: 'orientationType', value: structComData.orientationType, category: 'geo' },
            { key: 'type', value: structComData.type, category: 'structure' },
        ];
        primitive.needRedraw = true;
        return true;
    }
    // 计算y和z轴退让
    calcOffset() {
        const graphCache = this.node.graph.cache;
        const structComData = graphCache.data.structComData;
        const geo = graphCache.data.geo;
        const primitive = graphCache.data.primitive;
        const distance = moveP$4.distanceTo(startP$4);
        let height = distance; // 新的拉伸高度
        // 计算偏心参照
        const offsetIndex = structComData.offsetIndex;
        if (offsetIndex != null && !isNaN(offsetIndex) && offsetIndex >= 0 && offsetIndex <= 8) {
            const offsets = graphCache.data.offsets;
            // 获取y和z轴
            _v1$b.setFromMatrixColumn(primitive.localModelMatrix, 1);
            _v2$9.setFromMatrixColumn(primitive.localModelMatrix, 2);
            if (offsets != null && offsets.length === 18) {
                const width = offsets[offsetIndex * 2];
                const height = offsets[offsetIndex * 2 + 1];
                // startP往y轴移动width，z轴移动height
                startP$4.addScaledVector(_v1$b, width);
                startP$4.addScaledVector(_v2$9, height);
            }
        }
        const ey = structComData.ey;
        const ez = structComData.ez;
        const ex = structComData.ex;
        // x轴退让
        if (!!ex && ex.length === 2) {
            const startEx = ex[0];
            const endEx = ex[1];
            const newHeight = -startEx + endEx;
            if (height + newHeight < 1e-4) {
                height = 1;
            }
            else {
                height += newHeight;
            }
            _v3$5.setFromMatrixColumn(primitive.localModelMatrix, 0);
            startP$4.addScaledVector(_v3$5, startEx);
        }
        // z轴退让
        let ezOffset;
        if (!!ez && ez.length === 2) {
            const startEz = ez[0];
            const endEz = ez[1];
            ezOffset = endEz - startEz;
            // 构建起点的偏移向量
            _v3$5.setFromMatrixColumn(primitive.localModelMatrix, 2);
            startP$4.addScaledVector(_v3$5, startEz);
        }
        // y轴退让
        let eyOffset;
        if (!!ey && ey.length === 2) {
            const startEy = ey[0];
            const endEy = ey[1];
            eyOffset = endEy - startEy;
            _v3$5.setFromMatrixColumn(primitive.localModelMatrix, 1);
            startP$4.addScaledVector(_v3$5, startEy);
        }
        let zAxis = false;
        let yAxis = false;
        // 转换到局部坐标
        if (ezOffset != null && Math.abs(ezOffset) > 1e-4) {
            _v1$b.setFromMatrixColumn(primitive.localModelMatrix, 1).normalize();
            _v1$b.multiplyScalar(-ezOffset);
            zAxis = true;
        }
        if (eyOffset != null && Math.abs(eyOffset) > 1e-4) {
            _v2$9.setFromMatrixColumn(primitive.localModelMatrix, 2).normalize();
            _v2$9.multiplyScalar(-eyOffset);
            yAxis = true;
        }
        if (zAxis && yAxis) {
            _p1$6.addVectors(_v1$b, _v2$9);
            geo.offsetDirection = _p1$6;
        }
        else if (zAxis) {
            geo.offsetDirection = _v1$b;
        }
        else if (yAxis) {
            geo.offsetDirection = _v2$9;
        }
        return height;
    }
}
const startP$4 = new Vector3();
const moveP$4 = new Vector3();
const _vec$3 = new Vector3();
const _p$7 = new Vector3();
const _p1$6 = new Vector3();
const _v1$b = new Vector3();
const _v2$9 = new Vector3();
const _v3$5 = new Vector3();
const matrix$3 = new Matrix4();
const _quaternion$3 = new Quaternion();/**
 * 清除线性构件布置的临时图元
 */
class EventHandlerClearTempStructCom extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        graphCache.data.geo = null;
        graphCache.data.primitive = null;
        graphCache.primitiveList = [];
        graphCache.data['struct_com_info'] = [];
        return true;
    }
}/**
 * 引擎事件类型划分
 */
const PythaStructEventName = {
    STRUCT_DATA_CHANGE: 'struct_data_change', // 结构数据变更
    STRUCT_COM_DATA: 'struct_com_data', // 获取外部传入结构构件数据
};
/**
 * 输出引擎内部状态与数据变更的事件
 */
class PythaStructEventDataChange {
    constructor(data = {}) {
        this.cmd = PythaStructEventName.STRUCT_DATA_CHANGE;
        this.data = data;
    }
}
/**
 * 监听获取结构构件数据
 */
class PythaEventStructComData {
    constructor() {
        this.cmd = PythaStructEventName.STRUCT_COM_DATA;
    }
}
const ExtraPythaEventName = {
    MARERIAL_CHANGE: 'material_change', // 构件材料修改
    SECTION_CHANGE: 'section_change', // 构件截面修改
    TYPE_CHANGE: 'type_change', // 构件类型修改
};
class PythaEventMaterialChange {
    constructor() {
        this.cmd = ExtraPythaEventName.MARERIAL_CHANGE;
    }
}
class PythaEventSectionChange {
    constructor() {
        this.cmd = ExtraPythaEventName.SECTION_CHANGE;
    }
}
class PythaEventTypeChange {
    constructor() {
        this.cmd = ExtraPythaEventName.TYPE_CHANGE;
    }
}/**
 * 监听获取结构构件数据
 */
class EventHandlerListenStructComData extends EventHandlerBase {
    constructor(node) {
        super(node);
        this.needEvent = new PythaEventStructComData();
        this.needEventOnce = true;
    }
    run(event) {
        if (event.cmd != this.needEvent.cmd)
            return false; // 如果传入事件不符 则不执行处理器
        const graphCache = this.node.graph.cache;
        const data = event.data;
        if (!graphCache.data)
            graphCache.data = {};
        if (!graphCache.data.structComData)
            graphCache.data.structComData = {};
        Object.keys(data).forEach(key => {
            graphCache.data.structComData[key] = data[key];
        });
        return true;
    }
}/**
 * 生成图元和结构构件信息命令
 */
class GenPriAndStructInfoCmd extends CommandBase {
    /**
     * 构造函数
     * @param components
     * @param eventMediator
     */
    constructor(primitives, components, eventMediator) {
        super();
        /**
         * 生成的图元
         */
        this.primitives = [];
        /**
         * 构件信息
         */
        this.structComInfos = [];
        this.primitives = primitives;
        this.structComInfos = components;
        this.eventMediator = eventMediator;
    }
    execute() {
        const storage = this.eventMediator.storage;
        const primitiveDict = storage.primitiveStore.primitiveDict;
        this.primitives.forEach(primitive => {
            primitive.needRedraw = true;
            primitive.temporary = false;
            primitive.needDelete = false;
            primitive.layerUuid = storage.layerStore.currentLayerUuid || null;
            primitiveDict[primitive.uuid] = primitive;
        });
        this.eventMediator.noticeHandler.notice(this, new PythaEventDataChange({ add_primitives: this.primitives }));
        // 通知创建构件
        this.eventMediator.noticeHandler.notice(this, new PythaStructEventDataChange({ add_struct_com_info: this.structComInfos }));
    }
    undo() {
        this.primitives.forEach(primitive => {
            primitive.needDelete = true;
        });
        this.eventMediator.noticeHandler.notice(this, new PythaEventDataChange({ delete_primitives: this.primitives }));
    }
}/**
 * 生成图元并生成结构信息
 */
class EventHandlerGenPriAndStructInfo extends EventHandlerBase {
    run(data) {
        // 0 这里是开始逻辑
        // 获取存储
        const tempPrimitives = this.node.graph.cache.primitiveList;
        const storage = this.getPythaStorage();
        const graphCache = this.node.graph.cache;
        // 不存在信息或者信息为空
        if (!graphCache.data || !graphCache.data['struct_com_info'] || !Array.isArray(graphCache.data['struct_com_info'])
            || graphCache.data['struct_com_info'].length < 1 || tempPrimitives.length < 1) {
            return true;
        }
        const structComInfos = graphCache.data['struct_com_info'];
        const infoMap = {};
        structComInfos.forEach(info => {
            infoMap[info.primitiveUuid] = info;
        });
        tempPrimitives.forEach(primitive => {
            delete storage.primitiveStore.tempPrimitiveDict[primitive.uuid];
            const uuid = BaseUtils.genUUID();
            const info = infoMap[primitive.uuid];
            if (info) {
                info.primitiveUuid = uuid;
                info.comTypeUuid = info['properties']['comTypeUuid'];
            }
            primitive['majors'] = {
                'structure': info['properties']
            };
            primitive.uuid = uuid;
        });
        const cmd = new GenPriAndStructInfoCmd(tempPrimitives, structComInfos, this.node.graph.eventMediator);
        storage.historyStore.undos.push(cmd);
        cmd.execute();
        return true;
    }
}/**
 * 连续监听构件数据变化
 */
class EventHandlerListenStructDataContinue extends EventHandlerBase {
    constructor(node) {
        super(node);
        this.needEvent = new PythaEventStructComData();
        this.needEventOnce = false;
    }
    run(event) {
        if (event.cmd != this.needEvent.cmd)
            return false; // 如果传入事件不符 则不执行处理器
        const graphCache = this.node.graph.cache;
        const data = event.data;
        if (!graphCache.data)
            graphCache.data = {};
        if (!graphCache.data.structComData)
            graphCache.data.structComData = {};
        Object.keys(data).forEach(key => {
            graphCache.data.structComData[key] = data[key];
        });
        return true;
    }
}// 截面类型
const SectionType = {
    HR_H: 1, //H型钢
    HR_T: 2, //T型钢
    HR_I: 3, //工字钢
    HR_C: 4, //槽钢
    HR_L: 5, //等边角钢
    HR_L1: 6, //不等边角钢
    HR_CT: 7, //无缝钢管
    HR_F: 8, // 扁钢
    CF_C: 21, //卷边C型钢
    CF_C1: 22, //C型钢
    CF_Z: 23, //斜卷边Z型钢
    CF_Z1: 24, //直卷边Z型钢
    CF_ST: 25, //方钢管
    CF_RT: 26, //矩形管
    CF_L: 27, //等边L形
    CF_L1: 28, //卷边等边L形
    WS_H: 41, //H型
    WS_T: 42, //T型
    WS_RT: 43, //箱形
    WS_RT1: 44, //外伸箱形
    WS_CT: 45, //圆管
    WS_CR: 46, //十字形
    WS_TC: 47, //日字形
    WS_C: 48, //C形
    SC_H1: 61, //工字形1
    SC_H2: 62, //工字形2
    SC_H3: 63, //工字形3
    SC_H4: 64, //工字形4
    SC_H5: 65, //工字型5
    SC_T1: 66, //T形1
    SC_T2: 67, //T形2
    SC_T3: 68, //T形3
    SC_T4: 69, //T形4
    SC_T5: 70, //T形5
    SC_BL: 71, //箱形1
    SC_BC: 72, //箱形2
    SC_CL: 73, //十字形1
    SC_CT: 74, //十字形2
    REC: 81, //矩形
    CIRCLE: 91, //圆形
    BEAM_I: 101, //工形预应力梁
};
/**
 * 生成截面面域
 */
class EventHandlerGenSectionRegion extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        const structComData = graphCache.data.structComData;
        const sectionProtoType = structComData.sectionProtoType;
        // 拿到构件的面域
        // TODO 暂时只管一个面域的
        const sectionFaces = this.getRegionFaceBySection(sectionProtoType, structComData);
        const sectionFace = sectionFaces[0];
        graphCache.data.section = sectionFace;
        return true;
    }
    // 获取截面面域
    getRegionFaceBySection(type, structComData) {
        switch (type) {
            case SectionType.HR_H:
                return this.getHBeamRegionFace(structComData); //已测
            case SectionType.HR_T:
                return this.getTBeamRegionFace(structComData); //已测
            case SectionType.HR_I:
                return this.getIBeamRegionFace(structComData); //已测
            case SectionType.HR_C:
                return this.getCBeamRegionFace(structComData); //已测
            case SectionType.HR_L:
                return this.getLBeamRegionFace(structComData); //已测
            case SectionType.HR_L1:
                return this.getLBeamRegionFace(structComData); //已测
            case SectionType.HR_CT:
                return this.getRoundTubeRegionFace(structComData); //已测
            case SectionType.HR_F:
                return this.getTubeRegionFace(structComData); //已测
            case SectionType.CF_C:
                return this.getCRolledRegionFace(structComData);
            case SectionType.CF_C1:
                return this.getCTubeRegionFace(structComData); //已测
            case SectionType.CF_Z:
                return this.getZBeveledRegionFace(structComData);
            case SectionType.CF_Z1:
                return this.getZRolledRegionFace(structComData);
            case SectionType.CF_ST:
                return this.getRectTubeRegionFace(structComData); //已测
            case SectionType.CF_RT:
                return this.getRectTubeRegionFace(structComData); //已测
            case SectionType.CF_L:
                return this.getLEquilateralRegionFace(structComData);
            case SectionType.CF_L1:
                return this.getLRolledRegionFace(structComData);
            case SectionType.WS_H:
                return this.getHBeamRegionFace(structComData);
            case SectionType.WS_T:
                return this.getTBeamRegionFace(structComData);
            case SectionType.WS_RT:
                return this.getRectTubeRegionFace(structComData);
            case SectionType.WS_RT1:
                return this.getOverhangBoxRegionFace(structComData);
            case SectionType.WS_CT:
                return this.getRoundTubeRegionFace(structComData);
            case SectionType.WS_CR:
                return this.getCrossRegionFace(structComData);
            case SectionType.WS_TC:
                return this.getHeliogramRegionFace(structComData);
            case SectionType.WS_C:
                return this.getCTubeRegionFace(structComData);
            case SectionType.SC_H1:
                return [];
            case SectionType.SC_H2:
                return [];
            case SectionType.SC_H3:
                return [];
            case SectionType.SC_H4:
                return [];
            case SectionType.SC_H5:
                return [];
            case SectionType.SC_T1:
                return [];
            case SectionType.SC_T2:
                return [];
            case SectionType.SC_T3:
                return [];
            case SectionType.SC_T4:
                return [];
            case SectionType.SC_T5:
                return [];
            case SectionType.SC_BL:
                return [];
            case SectionType.SC_BC:
                return [];
            case SectionType.SC_CL:
                return [];
            case SectionType.SC_CT:
                return [];
            case SectionType.REC:
                return this.getRecRegionFace(structComData);
            case SectionType.CIRCLE:
                return this.getCircleRegionFace(structComData);
            case SectionType.BEAM_I:
                return this.getBeamIRegionFace(structComData);
            // case '实心圆形':
            //     return SectionTools.getCircleRegionFace(section);
            // case '圆形钢管混凝土':
            //     return SectionTools.getCircleCFSTRegionFace(section);
            // case '矩形混凝土包工字钢':
            //     return SectionTools.getRecIBeamRegionFace(section);
            default:
                return [];
        }
    }
    /**
     * t型钢 焊接T型
     */
    getTBeamRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b = section.paramIn.b * 1000 * unitRatio; // 翼缘宽度
        const h = section.paramIn.h * 1000 * unitRatio; // 总高度
        const t_w = section.paramIn.t_w * 1000 * unitRatio; // 腹板厚度
        const t_f = section.paramIn.t_f * 1000 * unitRatio; // 翼缘厚度
        // const r = section.paramIn.r * 1000; // 圆角半径，暂未使用
        let points = [
            // 翼缘部分 (顶部矩形)
            new Point(b / 2, h / 2 - t_f, 0), // 右下
            new Point(b / 2, h / 2, 0), // 右上
            new Point(-b / 2, h / 2, 0), // 左上
            new Point(-b / 2, h / 2 - t_f, 0), // 左下
            new Point(-t_w / 2, h / 2 - t_f, 0), // 腹板左上
            new Point(-t_w / 2, -h / 2, 0), // 腹板左下
            new Point(t_w / 2, -h / 2, 0), // 腹板右下
            new Point(t_w / 2, h / 2 - t_f, 0), // 腹板右上
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            const line = new Line(points[i], points[(i + 1) % points.length]);
            curves.push(line); // 自动闭合
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    // 槽钢（有斜度）
    getCBeamRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b = section.paramIn.b * 1000 * unitRatio;
        const h = section.paramIn.h * 1000 * unitRatio;
        const t_f = section.paramIn.t_f * 1000 * unitRatio;
        const t_w = section.paramIn.t_w * 1000 * unitRatio;
        // const r = section.paramIn.r * 1000;
        // const r_1 = section.paramIn.r_1 * 1000;
        const slope = 1 / 10; // 斜度比例 1:10
        const slope_offset = (b - t_w) / 2 * slope; // 翼缘的斜度偏移量
        // 原点在左下角
        let points = [
            new Point(-b / 2, -h / 2, 0), new Point(b - b / 2, -h / 2, 0), new Point(b - b / 2, t_f - h / 2 - slope_offset, 0), new Point(t_w - b / 2, t_f - h / 2 + slope_offset, 0),
            new Point(t_w - b / 2, h / 2 - t_f - slope_offset, 0), new Point(b - b / 2, h / 2 - t_f + slope_offset, 0), new Point(b - b / 2, h / 2, 0), new Point(0 - b / 2, h / 2, 0)
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            if (i != points.length - 1) {
                curves.push(new Line(points[i], points[i + 1]));
            }
            else {
                curves.push(new Line(points[i], points[0]));
            }
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    /**
     * 角钢
     * @param section
     * @returns
     */
    getLBeamRegionFace(section) {
        var _a;
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const h = (((_a = section.paramIn) === null || _a === void 0 ? void 0 : _a.h) || section.paramIn.b) * 1000 * unitRatio;
        const b = section.paramIn.b * 1000 * unitRatio;
        const r = section.paramIn.r * 1000 * unitRatio;
        const r_1 = section.paramIn.r_1 * 1000 * unitRatio;
        const offsets = [];
        offsets.push(-b / 2, -h / 2);
        offsets.push(0, -h / 2);
        offsets.push(b / 2, -h / 2);
        offsets.push(-b / 2, 0);
        offsets.push(0, 0);
        offsets.push(b / 2, 0);
        offsets.push(-b / 2, h / 2);
        offsets.push(0, h / 2);
        offsets.push(b / 2, h / 2);
        this.node.graph.cache.data.offsets = offsets;
        const line1 = new Line(new Vector3(-b / 2, h / 2, 0), new Vector3(-b / 2, -h / 2, 0));
        const line2 = new Line(new Vector3(-b / 2, -h / 2, 0), new Vector3(b / 2, -h / 2, 0));
        const arc1 = new Arc(new Vector3(b / 2 - r, -h / 2, 0), r, Math.PI / 2);
        const line3 = new Line(new Vector3(b / 2 - r, -h / 2 + r, 0), new Vector3(-b / 2 + r + r_1, -h / 2 + r, 0));
        const arc2 = new Arc(new Vector3(-b / 2 + r + r_1, -h / 2 + r + r_1, 0), r_1, Math.PI / 2, new Vector3(0, 0, 1), new Vector3(-1, 0, 0));
        const line4 = new Line(new Vector3(-b / 2 + r, -h / 2 + r + r_1, 0), new Vector3(-b / 2 + r, h / 2 - r, 0));
        const arc3 = new Arc(new Vector3(-b / 2, h / 2 - r, 0), r, Math.PI / 2);
        const curves = [line1, line2, arc1, line3, arc2, line4, arc3];
        const region = new RegionFace(curves, new Vector3(1, 0, 0), new Vector3(0, 0, 0));
        return [region];
    }
    /**
     * 扁钢
     * @param section
     * @returns
     */
    getTubeRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b = section.paramIn.b * 1000 * unitRatio;
        const t = section.paramIn.t * 1000 * unitRatio;
        const offsets = [];
        offsets.push(-b / 2, -t / 2);
        offsets.push(0, -t / 2);
        offsets.push(b / 2, -t / 2);
        offsets.push(-b / 2, 0);
        offsets.push(0, 0);
        offsets.push(b / 2, 0);
        offsets.push(-b / 2, t / 2);
        offsets.push(0, t / 2);
        offsets.push(b / 2, t / 2);
        this.node.graph.cache.data.offsets = offsets;
        const line1 = new Line(new Vector3(-b / 2, t / 2, 0), new Vector3(-b / 2, -t / 2, 0));
        const line2 = new Line(new Vector3(-b / 2, -t / 2, 0), new Vector3(b / 2, -t / 2, 0));
        const line3 = new Line(new Vector3(b / 2, -t / 2, 0), new Vector3(b / 2, t / 2, 0));
        const line4 = new Line(new Vector3(b / 2, t / 2, 0), new Vector3(-b / 2, t / 2, 0));
        const curves = [line1, line2, line3, line4];
        const region = new RegionFace(curves, new Vector3(1, 0, 0), new Vector3(0, 0, 0));
        return [region];
    }
    // 圆管、焊接圆管
    getRoundTubeRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        let outCilcle = new Circle(new Point(0, 0, 0), section.paramIn.d * 1000 / 2 * unitRatio);
        let inCilcle = new Circle(new Point(0, 0, 0), section.paramIn.d * 1000 / 2 * unitRatio - section.paramIn.t * 1000 * unitRatio);
        let regionface = new RegionFace();
        regionface.holes = [new RegionFace([inCilcle], new Vector3(1, 0, 0), new Point(0, 0, 0))];
        regionface.setRegionFace([outCilcle]);
        return [regionface];
    }
    //H型钢、焊接H型
    getHBeamRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b_1 = (section.paramIn.b || section.paramIn.b_1) * 1000 * unitRatio;
        const b_2 = (section.paramIn.b || section.paramIn.b_2) * 1000 * unitRatio;
        const h = section.paramIn.h * 1000 * unitRatio;
        const t_f1 = (section.paramIn.t_f || section.paramIn.t_f1) * 1000 * unitRatio;
        const t_f2 = (section.paramIn.t_f || section.paramIn.t_f2) * 1000 * unitRatio;
        const t_w = section.paramIn.t_w * 1000 * unitRatio;
        // const r = section.paramIn.r * 1000;
        const offsets = []; // 偏心参照数组，用两个下标分别表示宽度和高度
        // 第一排
        offsets.push(-b_1 / 2);
        offsets.push(-h / 2);
        offsets.push(0);
        offsets.push(-h / 2);
        offsets.push(b_1 / 2);
        offsets.push(-h / 2);
        // 第二排
        offsets.push(-(b_1 + b_2) / 4);
        offsets.push(0);
        offsets.push(0);
        offsets.push(0);
        offsets.push((b_1 + b_2) / 4);
        offsets.push(0);
        // 第三排
        offsets.push(-b_2 / 2);
        offsets.push(h / 2);
        offsets.push(0);
        offsets.push(h / 2);
        offsets.push(b_2 / 2);
        offsets.push(h / 2);
        this.node.graph.cache.data.offsets = offsets;
        let points = [
            new Point(t_w / 2, h / 2 - t_f1, 0), new Point(b_1 / 2, h / 2 - t_f1, 0), new Point(b_1 / 2, h / 2, 0),
            new Point(-b_1 / 2, h / 2, 0), new Point(-b_1 / 2, h / 2 - t_f1, 0), new Point(-t_w / 2, h / 2 - t_f1, 0),
            new Point(-t_w / 2, -h / 2 + t_f2, 0), new Point(-b_2 / 2, -h / 2 + t_f2, 0), new Point(-b_2 / 2, -h / 2, 0),
            new Point(b_2 / 2, -h / 2, 0), new Point(b_2 / 2, -h / 2 + t_f2, 0), new Point(t_w / 2, -h / 2 + t_f2, 0),
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            if (i != points.length - 1) {
                curves.push(new Line(points[i], points[i + 1]));
            }
            else {
                curves.push(new Line(points[i], points[0]));
            }
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    //工字钢（有斜度）
    getIBeamRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b_1 = (section.paramIn.b || section.paramIn.b_1) * 1000 * unitRatio;
        const b_2 = (section.paramIn.b || section.paramIn.b_2) * 1000 * unitRatio;
        const h = section.paramIn.h * 1000 * unitRatio;
        const t_f1 = (section.paramIn.t_f || section.paramIn.t_f1) * 1000 * unitRatio;
        const t_f2 = (section.paramIn.t_f || section.paramIn.t_f2) * 1000 * unitRatio;
        const t_w = section.paramIn.t_w * 1000 * unitRatio;
        // const r = section.paramIn.r * 1000;
        // const r_1 = section.paramIn.r_1 * 1000;
        const slope = 1 / 6; // 斜度比例 1:6
        const slope_offset_1 = (b_1 - t_w) / 4 * slope; // 上翼缘的斜度偏移量
        const slope_offset_2 = (b_2 - t_w) / 4 * slope; // 下翼缘的斜度偏移量
        const offsets = []; // 偏心参照数组，用两个下标分别表示宽度和高度
        // 第一排
        offsets.push(-b_1 / 2);
        offsets.push(-h / 2);
        offsets.push(0);
        offsets.push(-h / 2);
        offsets.push(b_1 / 2);
        offsets.push(-h / 2);
        // 第二排
        offsets.push(-(b_1 + b_2) / 4);
        offsets.push(0);
        offsets.push(0);
        offsets.push(0);
        offsets.push((b_1 + b_2) / 4);
        offsets.push(0);
        // 第三排
        offsets.push(-b_2 / 2);
        offsets.push(h / 2);
        offsets.push(0);
        offsets.push(h / 2);
        offsets.push(b_2 / 2);
        offsets.push(h / 2);
        this.node.graph.cache.data.offsets = offsets;
        let points = [
            new Point(t_w / 2, h / 2 - t_f1 - slope_offset_1, 0), new Point(b_1 / 2, h / 2 - t_f1 + slope_offset_1, 0), new Point(b_1 / 2, h / 2, 0),
            new Point(-b_1 / 2, h / 2, 0), new Point(-b_1 / 2, h / 2 - t_f1 + slope_offset_1, 0), new Point(-t_w / 2, h / 2 - t_f1 - slope_offset_1, 0),
            new Point(-t_w / 2, -h / 2 + t_f2 + slope_offset_2, 0), new Point(-b_2 / 2, -h / 2 + t_f2 - slope_offset_2, 0), new Point(-b_2 / 2, -h / 2, 0),
            new Point(b_2 / 2, -h / 2, 0), new Point(b_2 / 2, -h / 2 + t_f2 - slope_offset_2, 0), new Point(t_w / 2, -h / 2 + t_f2 + slope_offset_2, 0),
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            if (i != points.length - 1) {
                curves.push(new Line(points[i], points[i + 1]));
            }
            else {
                curves.push(new Line(points[i], points[0]));
            }
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    //C型钢（无斜度)
    // getC1BeamRegionFace(section) {
    //     const b = section.paramIn.b * 1000;
    //     const h = section.paramIn.h * 1000;
    //     const t = section.paramIn.t * 1000;
    //     // 原点在左下角
    //     let points: Point[] = [
    //         new Point(0, -h / 2, 0), new Point(b, -h / 2, 0), new Point(b, t - h / 2, 0), new Point(t, t - h / 2, 0),
    //         new Point(t, h / 2 - t, 0), new Point(b, h / 2 - t, 0), new Point(b, h / 2, 0), new Point(0, h / 2, 0)
    //     ]
    //     let curves: Line[] = [];
    //     for (let i = 0; i < points.length; i++) {
    //         if (i != points.length - 1) {
    //             curves.push(new Line(points[i], points[i + 1]));
    //         } else {
    //             curves.push(new Line(points[i], points[0]));
    //         }
    //     }
    //     return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    // }
    // 卷边C型
    getCRolledRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b = section.paramIn.b * 1000 * unitRatio;
        const h = section.paramIn.h * 1000 * unitRatio;
        const t = section.paramIn.t * 1000 * unitRatio;
        const a = section.paramIn.a * 1000 * unitRatio;
        const offsets = [];
        this.node.graph.cache.data.offsets = offsets;
        // 左上角
        let leftTop = new Point(-b / 2, h / 2, 0);
        // 右上角
        let rightTop = new Point(b / 2, h / 2, 0);
        // 左下角
        let leftBottom = new Point(-b / 2, -h / 2, 0);
        // 右下角
        let rightBottom = new Point(b / 2, -h / 2, 0);
        // 原点在形心
        let points = [
            leftBottom, rightBottom, new Point(b / 2, -h / 2 + a, 0), new Point(b / 2 - t, -h / 2 + a, 0), new Point(b / 2 - t, -h / 2 + t, 0), new Point(-b / 2 + t, -h / 2 + t, 0),
            new Point(-b / 2 + t, h / 2 - t, 0), new Point(b / 2 - t, h / 2 - t, 0), new Point(b / 2 - t, h / 2 - a, 0), new Point(b / 2, h / 2 - a, 0), rightTop, leftTop,
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            if (i != points.length - 1) {
                curves.push(new Line(points[i], points[i + 1]));
            }
            else {
                curves.push(new Line(points[i], points[0]));
            }
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    // todo 斜卷边Z型
    getZBeveledRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const h = section.paramIn.h * 1000 * unitRatio; // 高度
        const b = section.paramIn.b * 1000 * unitRatio; // 水平段宽度
        const t = section.paramIn.t * 1000 * unitRatio; // 厚度
        const a = section.paramIn.a * 1000 * unitRatio; // 倾斜段长度
        const theta = section.paramIn.theta; // 倾斜角度（弧度制）
        const angle90Complement = Math.PI / 2 - theta;
        const offsets = [];
        this.node.graph.cache.data.offsets = offsets;
        // 定义各个点的坐标
        const points = [
            new Point(-b + t / 2, -h / 2, 0), // 第1点  左下角
            new Point(t / 2, -h / 2, 0), // 第2点
            new Point(t / 2, h / 2 - t, 0), // 第3点
            new Point(b - t / 2 - t * Math.cos(theta), h / 2 - t, 0), // 第4点
            new Point(b - t / 2 + a * Math.cos(theta) - t * Math.cos(angle90Complement), h / 2 - a * Math.sin(theta) - t * Math.sin(angle90Complement), 0), // 第5点
            new Point(b - t / 2 + a * Math.cos(theta), h / 2 - a * Math.sin(theta), 0), // 第6点
            new Point(b - t / 2, h / 2, 0), // 第7点
            new Point(-t / 2, h / 2, 0), // 第8点
            new Point(-t / 2, -h / 2 + t, 0), // 第9点
            new Point(-b + t / 2 + t * Math.cos(theta), -h / 2 + t, 0), // 第10点
            new Point(-b + t / 2 - a * Math.cos(theta) + t * Math.cos(angle90Complement), -h / 2 + a * Math.sin(theta) + t * Math.sin(angle90Complement), 0), // 第11点
            new Point(-b + t / 2 - a * Math.cos(theta), -h / 2 + a * Math.sin(theta), 0), // 第12点
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            if (i != points.length - 1) {
                curves.push(new Line(points[i], points[i + 1]));
            }
            else {
                curves.push(new Line(points[i], points[0]));
            }
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    // todo 直卷边Z型
    getZRolledRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const h = section.paramIn.h * 1000 * unitRatio; // 高度
        const b = section.paramIn.b * 1000 * unitRatio; // 水平段宽度
        const t = section.paramIn.t * 1000 * unitRatio; // 厚度
        const a = section.paramIn.a * 1000 * unitRatio; // 倾斜段长度
        const offsets = [];
        this.node.graph.cache.data.offsets = offsets;
        // 定义各个点的坐标
        const points = [
            new Point(-b + t / 2, -h / 2, 0), // 第1点 左下角
            new Point(t / 2, -h / 2, 0), // 第2点
            new Point(t / 2, h / 2 - t, 0), // 第3点
            new Point(b - t / 2 - t, h / 2 - t, 0), // 第4点
            new Point(b - t / 2 - t, h / 2 - a, 0), // 第5点
            new Point(b - t / 2, h / 2 - a, 0), // 第6点
            new Point(b - t / 2, h / 2, 0), // 第7点 右上角
            new Point(-t / 2, h / 2, 0), // 第8点
            new Point(-t / 2, -h / 2 + t, 0), // 第9点
            new Point(-b + t / 2 + t, -h / 2 + t, 0), // 第10点
            new Point(-b + t / 2 + t, -h / 2 + a, 0), // 第11点
            new Point(-b + t / 2, -h / 2 + a, 0), // 第12点
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            if (i != points.length - 1) {
                curves.push(new Line(points[i], points[i + 1]));
            }
            else {
                curves.push(new Line(points[i], points[0]));
            }
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    // 等边L型
    getLEquilateralRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b = section.paramIn.b * 1000 * unitRatio;
        const t = section.paramIn.t * 1000 * unitRatio;
        const offsets = [];
        this.node.graph.cache.data.offsets = offsets;
        // 左上角
        let leftTop = new Point(-b / 2, b / 2, 0);
        // 左下角
        let leftBottom = new Point(-b / 2, -b / 2, 0);
        // 右下角
        let rightBottom = new Point(b / 2, -b / 2, 0);
        // 原点在形心
        let points = [
            leftBottom, rightBottom, new Point(b / 2, -b / 2 + t, 0), new Point(-b / 2 + t, -b / 2 + t, 0), new Point(-b / 2 + t, b / 2, 0), leftTop,
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            if (i != points.length - 1) {
                curves.push(new Line(points[i], points[i + 1]));
            }
            else {
                curves.push(new Line(points[i], points[0]));
            }
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    // 卷等边L
    getLRolledRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b = section.paramIn.b * 1000 * unitRatio;
        const t = section.paramIn.t * 1000 * unitRatio;
        const a = section.paramIn.a * 1000 * unitRatio;
        const offsets = [];
        this.node.graph.cache.data.offsets = offsets;
        // 左上角
        let leftTop = new Point(-b / 2, b / 2, 0);
        // 左下角
        let leftBottom = new Point(-b / 2, -b / 2, 0);
        // 右下角
        let rightBottom = new Point(b / 2, -b / 2, 0);
        // 原点在形心
        let points = [
            leftBottom,
            rightBottom,
            new Point(b / 2, -b / 2 + a, 0),
            new Point(b / 2 - t, -b / 2 + a, 0),
            new Point(b / 2 - t, -b / 2 + t, 0),
            new Point(-b / 2 + t, -b / 2 + t, 0),
            new Point(-b / 2 + t, b / 2 - t, 0),
            new Point(-b / 2 + a, b / 2 - t, 0),
            new Point(-b / 2 + a, b / 2, 0),
            leftTop,
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            if (i != points.length - 1) {
                curves.push(new Line(points[i], points[i + 1]));
            }
            else {
                curves.push(new Line(points[i], points[0]));
            }
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    // todo 外伸箱型
    getOverhangBoxRegionFace(section) {
        // 提取参数
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b = section.paramIn.b * 1000 * unitRatio; // 总宽度
        const h = section.paramIn.h * 1000 * unitRatio; // 总高度
        const b_1 = section.paramIn.b_1 * 1000 * unitRatio;
        const t_f1 = section.paramIn.t_f1 * 1000 * unitRatio; // 上翼缘厚度
        const t_f2 = section.paramIn.t_f2 * 1000 * unitRatio; // 下翼缘厚度
        const t_w = section.paramIn.t_w * 1000 * unitRatio; // 腹板厚度
        const offsets = [];
        this.node.graph.cache.data.offsets = offsets;
        // 定义点位
        let points = [
            // 上翼缘 外轮廓曲线
            new Point(-b / 2, -h / 2, 0), // 左下角 1
            new Point(b / 2, -h / 2, 0), // 右下角 2
            new Point(b / 2, -h / 2 + t_f2, 0), // 右下翼缘顶部 3
            new Point(b_1 / 2 + t_w / 2, -h / 2 + t_f2, 0), // 右腹板下方 4
            new Point(b_1 / 2 + t_w / 2, h / 2 - t_f1, 0), // 右腹板上方 5
            new Point(b / 2, h / 2 - t_f1, 0), // 右上翼缘底部 6
            new Point(b / 2, h / 2, 0), // 右上角 7
            new Point(-b / 2, h / 2, 0), // 左上角 8
            new Point(-b / 2, h / 2 - t_f1, 0), // 左上翼缘底部 9
            new Point(-b_1 / 2 - t_w / 2, h / 2 - t_f1, 0), // 左腹板上方 10
            new Point(-b_1 / 2 - t_w / 2, -h / 2 + t_f2, 0), // 左腹板下方 11
            new Point(-b / 2, -h / 2 + t_f2, 0), // 左下翼缘顶部 12
        ];
        // 定义点位
        let inPoints = [
            // 内轮廓曲线
            new Point(-b_1 / 2 + t_w / 2, -h / 2 + t_f2, 0), // 左下角
            new Point(b_1 / 2 - t_w / 2, -h / 2 + t_f2, 0), // 右下角
            new Point(b_1 / 2 - t_w / 2, h / 2 - t_f1, 0), // 右上角
            new Point(-b_1 / 2 + t_w / 2, h / 2 - t_f1, 0), // 左上角
        ];
        // const regionface = new GeneralRegion([points, inPoints], new Vector3(0, 0, 1));
        // 构造外轮廓线段
        let outCurves = [];
        for (let i = 0; i < points.length; i++) {
            if (i !== points.length - 1) {
                outCurves.push(new Line(points[i], points[i + 1]));
            }
            else {
                outCurves.push(new Line(points[i], points[0])); // 闭合外轮廓
            }
        }
        // 构造内轮廓线段
        let inCurves = [];
        for (let i = 0; i < inPoints.length; i++) {
            if (i !== inPoints.length - 1) {
                inCurves.push(new Line(inPoints[i], inPoints[i + 1]));
            }
            else {
                inCurves.push(new Line(inPoints[i], inPoints[0])); // 闭合内轮廓
            }
        }
        // let regionface = new RegionFace();
        // regionface.holes = [new RegionFace([inCurves], new Vector3(1, 0, 0), new Point(0, 0, 0))];
        // regionface.setRegionFace([outCurves]);
        // 构建区域面对象
        let regionface = new RegionFace(outCurves, new Vector3(1, 0, 0), new Point(0, 0, 0));
        regionface.holes = [new RegionFace(inCurves)]; // 将内轮廓作为孔洞
        // 返回结果
        return [regionface];
    }
    // todo 十字形
    getCrossRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const h = section.paramIn.h * 1000 * unitRatio;
        const h_1 = section.paramIn.h_1 * 1000 * unitRatio;
        const b = section.paramIn.b * 1000 * unitRatio;
        const b_1 = section.paramIn.b_1 * 1000 * unitRatio;
        const t_f = section.paramIn.t_f * 1000 * unitRatio;
        const t_w = section.paramIn.t_w * 1000 * unitRatio;
        const offsets = [];
        this.node.graph.cache.data.offsets = offsets;
        // 原点在形心
        let points = [
            new Point(-t_w / 2, -h + h_1, 0), //1 下腹板左顶点，后面的点按照逆时针标
            new Point(t_w / 2, -h + h_1, 0), //2 下腹板右顶点
            new Point(t_w / 2, -t_f / 2, 0), //3 右下翼缘内顶点
            new Point(b - b_1, -t_f / 2, 0), //4 右下翼缘外顶点
            new Point(b - b_1, t_f / 2, 0), //5 右上翼缘外顶点
            new Point(t_w / 2, t_f / 2, 0), //6 右上翼缘内顶点
            new Point(t_w / 2, h_1, 0), //7 上腹板右顶点
            new Point(-t_w / 2, h_1, 0), //8 上腹板左顶点
            new Point(-t_w / 2, t_f / 2, 0), //9 左上翼缘内顶点
            new Point(-b_1, t_f / 2, 0), //10 左上翼缘外顶点
            new Point(-b_1, -t_f / 2, 0), //11 左下翼缘外顶点
            new Point(-t_w / 2, -t_f / 2, 0), //12 左下翼缘内顶点
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            if (i != points.length - 1) {
                curves.push(new Line(points[i], points[i + 1]));
            }
            else {
                curves.push(new Line(points[i], points[0]));
            }
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    // todo 日字型
    getHeliogramRegionFace(section) {
        // 提取参数
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b = section.paramIn.b * 1000 * unitRatio; // 总宽度
        const h = section.paramIn.h * 1000 * unitRatio; // 总高度
        const t_f1 = section.paramIn.t_f1 * 1000 * unitRatio; // 上翼缘厚度
        const t_f2 = section.paramIn.t_f2 * 1000 * unitRatio; // 下翼缘厚度
        const t_w1 = section.paramIn.t_w1 * 1000 * unitRatio; // 左、右腹板厚度
        const t_w2 = section.paramIn.t_w2 * 1000 * unitRatio; // 中间腹板厚度
        // 外轮廓点位
        const outPoints = [
            new Point(-b / 2, h / 2, 0), // 左上角
            new Point(-b / 2, -h / 2, 0), // 左下角
            new Point(b / 2, -h / 2, 0), // 右下角
            new Point(b / 2, h / 2, 0), // 右上角
        ];
        // 内轮廓一点位
        const inPoints1 = [
            new Point(-b / 2 + t_w1, h / 2 - t_f1, 0), // 左上角
            new Point(-b / 2 + t_w1, -h / 2 + t_f2, 0), // 左下角
            new Point(-t_w2 / 2, -h / 2 + t_f2, 0), // 右下角
            new Point(-t_w2 / 2, h / 2 - t_f1, 0), // 右上角
        ];
        // 内轮廓二点位
        const inPoints2 = [
            new Point(t_w2 / 2, h / 2 - t_f1, 0), // 左上角
            new Point(t_w2 / 2, -h / 2 + t_f2, 0), // 左下角
            new Point(b / 2 - t_w1, -h / 2 + t_f2, 0), // 右下角
            new Point(b / 2 - t_w1, h / 2 - t_f1, 0), // 右上角
        ];
        // console.log("outPoints", outPoints)
        // console.log("inPoints1", inPoints1)
        // console.log("inPoints2", inPoints2)
        // const regionface = new GeneralRegion([outPoints, inPoints1, inPoints2], new Vector3(0, 0, 1));
        // 构造外轮廓线段
        const outCurves = [];
        for (let i = 0; i < outPoints.length; i++) {
            if (i !== outPoints.length - 1) {
                outCurves.push(new Line(outPoints[i], outPoints[i + 1]));
            }
            else {
                outCurves.push(new Line(outPoints[i], outPoints[0])); // 闭合外轮廓
            }
        }
        // 构造内轮廓1线段
        const inCurves1 = [];
        for (let i = 0; i < inPoints1.length; i++) {
            if (i !== inPoints1.length - 1) {
                inCurves1.push(new Line(inPoints1[i], inPoints1[i + 1]));
            }
            else {
                inCurves1.push(new Line(inPoints1[i], inPoints1[0])); // 闭合内轮廓1
            }
        }
        // 构造内轮廓2线段
        const inCurves2 = [];
        for (let i = 0; i < inPoints2.length; i++) {
            if (i !== inPoints2.length - 1) {
                inCurves2.push(new Line(inPoints2[i], inPoints2[i + 1]));
            }
            else {
                inCurves2.push(new Line(inPoints2[i], inPoints2[0])); // 闭合内轮廓2
            }
        }
        // 构建区域面对象
        const regionface = new RegionFace(outCurves, new Vector3(1, 0, 0), new Point(0, 0, 0));
        regionface.setRegionFace(outCurves, [new RegionFace(inCurves1), new RegionFace(inCurves2)]);
        // regionface.holes = [  ];
        return [regionface];
    }
    // C型 焊接C形
    getCTubeRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b_1 = (section.paramIn.b || section.paramIn.b_1) * 1000 * unitRatio;
        const b_2 = (section.paramIn.b || section.paramIn.b_2) * 1000 * unitRatio;
        const h = section.paramIn.h * 1000 * unitRatio;
        const t_f1 = (section.paramIn.t || section.paramIn.t_f || section.paramIn.t_f1) * 1000 * unitRatio;
        const t_f2 = (section.paramIn.t || section.paramIn.t_f || section.paramIn.t_f2) * 1000 * unitRatio;
        const t_w = (section.paramIn.t || section.paramIn.t_w) * 1000 * unitRatio;
        const offsets = [];
        offsets.push(-b_1 / 2, -h / 2);
        offsets.push(0, -h / 2);
        offsets.push(b_1 / 2, -h / 2);
        offsets.push(-(b_1 + b_2) / 4, 0);
        offsets.push(0, 0);
        offsets.push((b_1 + b_2) / 4, h / 2);
        offsets.push(-b_2 / 2, h / 2);
        offsets.push(0, h / 2);
        offsets.push(b_2 / 2, h / 2);
        this.node.graph.cache.data.offsets = offsets;
        // 原点在形心
        let points = [
            new Point(-(b_1 + b_2) / 4, -h / 2, 0), new Point(-(b_1 + b_2) / 4 + b_2, -h / 2, 0), new Point(-(b_1 + b_2) / 4 + b_2, t_f2 - h / 2, 0), new Point(-(b_1 + b_2) / 4 + t_w, t_f2 - h / 2, 0),
            new Point(-(b_1 + b_2) / 4 + t_w, h / 2 - t_f1, 0), new Point(-(b_1 + b_2) / 4 + b_1, h / 2 - t_f1, 0), new Point(-(b_1 + b_2) / 4 + b_1, h / 2, 0), new Point(-(b_1 + b_2) / 4, h / 2, 0)
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            if (i != points.length - 1) {
                curves.push(new Line(points[i], points[i + 1]));
            }
            else {
                curves.push(new Line(points[i], points[0]));
            }
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    //箱形、矩形管、方钢管
    getRectTubeRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b = section.paramIn.b * 1000 * unitRatio;
        const h = section.paramIn.h ? section.paramIn.h * 1000 * unitRatio : b; //如果默认没有h参数，则让h=b
        const t_f1 = section.paramIn.t_f1 ? section.paramIn.t_f1 * 1000 * unitRatio : section.paramIn.t * 1000 * unitRatio; //如果没有这4个参数，则让它们等于t
        const t_f2 = section.paramIn.t_f2 ? section.paramIn.t_f2 * 1000 * unitRatio : section.paramIn.t * 1000 * unitRatio;
        const t_w1 = section.paramIn.t_w1 ? section.paramIn.t_w1 * 1000 * unitRatio : section.paramIn.t * 1000 * unitRatio;
        const t_w2 = section.paramIn.t_w2 ? section.paramIn.t_w2 * 1000 * unitRatio : section.paramIn.t * 1000 * unitRatio;
        let outPoints = [
            new Point(b / 2, h / 2, 0), new Point(-b / 2, h / 2, 0), new Point(-b / 2, -h / 2, 0), new Point(b / 2, -h / 2, 0)
        ];
        let inPoints = [
            new Point(b / 2 - t_w2, h / 2 - t_f1, 0), new Point(b / 2 - t_w2, -h / 2 + t_f2, 0),
            new Point(-b / 2 + t_w1, -h / 2 + t_f2, 0), new Point(-b / 2 + t_w1, h / 2 - t_f1, 0)
        ];
        // const regionface = new GeneralRegion([outPoints, inPoints], new Vector3(0, 0, 1));
        let regionface = new RegionFace([], new Vector3(1, 0, 0), new Point(0, 0, 0));
        let outCurves = [];
        let inCurves = [];
        for (let i = 0; i < inPoints.length; i++) {
            if (i != inPoints.length - 1) {
                outCurves.push(new Line(outPoints[i], outPoints[i + 1]));
                inCurves.push(new Line(inPoints[i], inPoints[i + 1]));
            }
            else {
                outCurves.push(new Line(outPoints[i], outPoints[0]));
                inCurves.push(new Line(inPoints[i], inPoints[0]));
            }
        }
        regionface.holes = [new RegionFace(inCurves)];
        regionface.setRegionFace(outCurves);
        return [regionface];
    }
    // 工形预应力梁
    getBeamIRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b = section.paramIn.b * 1000 * unitRatio;
        const h_1 = section.paramIn.h_1 * 1000 * unitRatio;
        const h_2 = section.paramIn.h_2 * 1000 * unitRatio;
        const b_f = section.paramIn.b_f * 1000 * unitRatio;
        const offsetY = (h_2 + h_1) / 4;
        let points = [
            new Point(-b / 2, offsetY, 0), new Point(-b / 2, offsetY - 300, 0), new Point(-b_f / 2, offsetY - 500, 0), new Point(-b_f / 2, offsetY - h_1 + 500, 0), new Point(-b / 2, offsetY - h_1 + 300, 0), new Point(-b / 2, offsetY - h_1, 0),
            new Point(b / 2, offsetY - h_2, 0), new Point(b / 2, offsetY - h_2 + 300, 0), new Point(b_f / 2, offsetY - h_2 + 500, 0), new Point(b_f / 2, offsetY - 500, 0), new Point(b / 2, offsetY - 300, 0), new Point(b / 2, offsetY, 0)
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            if (i != points.length - 1) {
                curves.push(new Line(points[i], points[i + 1]));
            }
            else {
                curves.push(new Line(points[i], points[0]));
            }
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    //实心圆形
    getCircleRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const r = section.paramIn.r * 1000 * unitRatio;
        return [new RegionFace([new Circle(new Point(0, 0, 0), r)], new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    // 实心矩形
    getRecRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b = section.paramIn.b * 1000 * unitRatio;
        const h = section.paramIn.h * 1000 * unitRatio;
        let points = [
            new Point(-b / 2, -h / 2, 0), new Point(b / 2, -h / 2, 0), new Point(b / 2, h / 2, 0), new Point(-b / 2, h / 2, 0)
        ];
        let curves = [];
        for (let i = 0; i < points.length; i++) {
            if (i != points.length - 1) {
                curves.push(new Line(points[i], points[i + 1]));
            }
            else {
                curves.push(new Line(points[i], points[0]));
            }
        }
        return [new RegionFace(curves, new Vector3(1, 0, 0), new Point(0, 0, 0))];
    }
    //圆形钢管混凝土
    getCircleCFSTRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        let outCilcle = new Circle(new Point(0, 0, 0), section.paramIn.d * 1000 / 2 * unitRatio);
        let inCilcle = new Circle(new Point(0, 0, 0), section.paramIn.d * 1000 / 2 * unitRatio - section.paramIn.t * 1000 * unitRatio);
        let regionface1 = new RegionFace();
        regionface1.holes = [new RegionFace([inCilcle], new Vector3(1, 0, 0), new Point(0, 0, 0))];
        regionface1.setRegionFace([outCilcle]);
        let regionface2 = new RegionFace([inCilcle.clone()], new Vector3(1, 0, 0), new Point(0, 0, 0));
        return [regionface1, regionface2];
    }
    //矩形混凝土包工字钢
    getRecIBeamRegionFace(section) {
        const unitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio || 1;
        const b = section.paramIn.b * 1000 * unitRatio;
        const h = section.paramIn.h * 1000 * unitRatio;
        const t = section.paramIn.t * 1000 * unitRatio;
        const b_2 = section.paramIn.b_2 * 1000 * unitRatio;
        const b_3 = section.paramIn.b_3 * 1000 * unitRatio;
        const t_w = section.paramIn.t_w * 1000 * unitRatio;
        let outPoints = [
            new Point(b_3 / 2, b_2 / 2, 0), new Point(-b_3 / 2, b_2 / 2, 0), new Point(-b_3 / 2, -b_2 / 2, 0), new Point(b_3 / 2, -b_2 / 2, 0)
        ];
        let regionface = new RegionFace([], new Vector3(1, 0, 0), new Point(0, 0, 0));
        let outCurves = [];
        for (let i = 0; i < outPoints.length; i++) {
            if (i != outPoints.length - 1) {
                outCurves.push(new Line(outPoints[i], outPoints[i + 1]));
            }
            else {
                outCurves.push(new Line(outPoints[i], outPoints[0]));
            }
        }
        regionface.setRegionFace(outCurves);
        let newSection = {
            paramIn: {
                b_1: b / 1000 / unitRatio,
                b_2: b / 1000 / unitRatio,
                h: h / 1000 / unitRatio,
                t_f1: t / 1000 / unitRatio,
                t_f2: t / 1000 / unitRatio,
                t_w: t_w / 1000 / unitRatio
            }
        };
        let res = this.getIBeamRegionFace(newSection);
        let iBeamRegionFace;
        if (res && res.length === 1) {
            iBeamRegionFace = res[0];
        }
        return [regionface, iBeamRegionFace];
    }
}/**
 * 初始创建拉伸高度
 */
const DefaultExtHeight$1 = 100;
/**
 * 计算构件面域初始方向
 */
class EventHandlerCalStructRegion extends EventHandlerBase {
    run(data) {
        var _a;
        const graphCache = this.node.graph.cache;
        const storage = this.getPythaStorage();
        const region = graphCache.data.section;
        if (!region)
            return true;
        // 把面域转到yoz平面
        const transformMatrix = region.transformMatrix;
        const axisZ = new Vector3(0, 0, 1);
        const axisY = new Vector3(0, 1, 0);
        // 创建一个旋转矩阵，绕 Z 轴旋转 90 度
        const rotationZ = new Matrix4().makeRotationAxis(axisZ, Math.PI / 2);
        // 创建一个旋转矩阵，绕 Y 轴旋转 90 度
        const rotationY = new Matrix4().makeRotationAxis(axisY, Math.PI / 2);
        const m1 = new Matrix4().premultiply(transformMatrix).premultiply(rotationZ).premultiply(rotationY);
        transformMatrix.copy(m1);
        const geo = new ExtrudeSolid(region, DefaultExtHeight$1);
        const primitive = new Primitive(PrimitiveType.ExtrudeSoild);
        graphCache.data.geo = geo;
        graphCache.data.primitive = primitive;
        primitive.geos.push(geo);
        primitive.temporary = true;
        primitive.needRedraw = true;
        primitive.colorByLayer = true;
        const keys = Object.keys(primitive.renderItemDict);
        if (keys.length > 0) {
            let renderItem = primitive.renderItemDict[keys[0]];
            renderItem.geoElements = geo.getGeoElementsIdxList();
            (_a = renderItem.mat) === null || _a === void 0 ? void 0 : _a.color.set(storage.layerStore.getColor());
        }
        graphCache.primitiveList.push(primitive);
        storage.primitiveStore.tempPrimitiveDict[primitive.uuid] = primitive;
        return true;
    }
}/**
 * 两点布置线性构件
 */
class EventGraphStructComTwoPoint extends EventGraphBase {
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node2 = new EventGraphNode(this, EventHandlerListenStructComData);
        const node3 = new EventGraphNode(this, EventHandlerCalPointerDown);
        const node4 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [
                { type: NeedInputEnum.ACTIVE, description: "指定第一个点", option: '', inputTypes: [PythaEventName.POINTER_DOWN] },
            ]
        });
        const node5 = new EventGraphNode(this, EventHandlerDisableSelect);
        const node6 = new EventGraphNode(this, EventHandlerInitCapture);
        const node7 = new EventGraphNode(this, EventHandlerCalPointerMove);
        const node8 = new EventGraphNode(this, EventHandlerCapture);
        const node9 = new EventGraphNodeAND(this, null);
        const node10 = new EventGraphNode(this, EventHandlerCalStructComTwoPoint);
        const node11 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [
                { type: NeedInputEnum.ACTIVE, description: "指定下一个点", option: '', inputTypes: [PythaEventName.POINTER_DOWN] },
            ]
        });
        const node12 = new EventGraphNode(this, EventHandlerCalPointerDown);
        const node13 = new EventGraphNode(this, EventHandlerGrid);
        const node14 = new EventGraphNodeRender(this, true, UpdateType.TEMP);
        const node15 = new EventGraphNode(this, EventHandlerGenPriAndStructInfo);
        const node16 = new EventGraphNode(this, EventHandlerGrid);
        const node17 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node21 = new EventGraphNode(this, EventHandlerClearTempStructCom);
        const node24 = new EventGraphNode(this, EHChangeCrossCursor);
        const node25 = new EventGraphNode(this, EventHandlerConfirm);
        const node26 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node27 = new EventGraphNode(this, EventHandlerListenStructDataContinue);
        const node28 = new EventGraphNode(this, EventHandlerGenSectionRegion);
        const node29 = new EventGraphNode(this, EventHandlerCalStructRegion);
        const node30 = new EventGraphNode(this, EventHandlerGenSectionRegion);
        const node31 = new EventGraphNode(this, EventHandlerCalStructRegion);
        const node32 = new EventGraphNode(this, EventHandlerCalPrimitiveInView);
        this.addEdge(node1, node5);
        this.addEdge(node5, node2);
        this.addEdge(node4, node3);
        this.addEdge(node9, node10);
        this.addEdge(node3, node11);
        this.addEdge(node10, node13);
        this.addEdge(node13, node14);
        this.addEdge(node6, node7);
        this.addEdge(node7, node8);
        this.addEdge(node8, node9);
        this.addEdge(node12, node15);
        this.addEdge(node21, node16);
        this.addEdge(node11, node12);
        this.addEdge(node15, node21);
        this.addEdge(node2, node24);
        this.addEdge(node24, node6);
        this.addEdge(node24, node4);
        this.addEdge(node1, node25);
        this.addEdge(node25, node26);
        this.addEdge(node2, node27);
        this.addEdge(node3, node28);
        this.addEdge(node28, node29);
        this.addEdge(node29, node9);
        this.addEdge(node17, node30);
        this.addEdge(node30, node31);
        this.addEdge(node31, node11);
        this.addEdge(node16, node32);
        this.addEdge(node32, node17);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/116
    }
}
EventGraphStructComTwoPoint.cmd = "STRUCT_COM_TWO_POINT";/**
 * 处理计算选中线段布置线性构件
 */
class EventHandlerCalStructComLine extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        const structComData = graphCache.data.structComData;
        if (structComData == null || Object.keys(structComData).length == 0) {
            return true;
        }
        const storage = this.getPythaStorage();
        const selectPrimitives = storage.selectStore.selectPrimitives;
        const linePrimitives = [];
        selectPrimitives.forEach(primitive => {
            if (primitive.type === PrimitiveType.Line || primitive.type === 'AxisLineSegment' ||
                primitive.type === "VerticalAxisLine") {
                linePrimitives.push(primitive);
            }
        });
        const primitive = graphCache.data.primitive;
        const geo = graphCache.data.geo;
        if (!geo || !primitive)
            return true;
        // 把临时图元补充到足够个数
        if (graphCache.primitiveList.length < linePrimitives.length) {
            const count = linePrimitives.length - graphCache.primitiveList.length;
            for (let i = 0; i < count; i++) {
                const newPri = primitive.clone();
                newPri.temporary = true;
                newPri.needRedraw = true;
                const keys = Object.keys(newPri.renderItemDict);
                if (keys.length > 0) {
                    let renderItem = newPri.renderItemDict[keys[0]];
                    renderItem.geoElements = geo.getGeoElementsIdxList();
                }
                graphCache.primitiveList.push(newPri);
                storage.primitiveStore.tempPrimitiveDict[newPri.uuid] = newPri;
            }
        }
        graphCache.data['struct_com_info'] = [];
        linePrimitives.forEach((linePrimitive, index) => {
            const extPri = graphCache.primitiveList[index];
            this.calcLineStructCom(linePrimitive, extPri, structComData);
        });
        return true;
    }
    calcLineStructCom(linePrimitive, primitive, structComData) {
        const graphCache = this.node.graph.cache;
        const storage = this.getPythaStorage();
        const workPlane = storage.coordinateStore.workPlane;
        if (linePrimitive.type === 'AxisLineSegment' || linePrimitive.type === "VerticalAxisLine") {
            // const axisNetUuid = (linePrimitive as any).axisNetUuid;
            // const axisNet = primitiveDict[axisNetUuid];
            // 异常数据
            // if (!axisNet) return;
            const line = linePrimitive.geos[0];
            startP$3.copy(line.start);
            // startP.applyMatrix4(axisNet.localModelMatrix);
            moveP$3.copy(line.end);
            // moveP.applyMatrix4(axisNet.localModelMatrix);
        }
        else {
            const line = linePrimitive.geos[0];
            startP$3.copy(line.start);
            moveP$3.copy(line.end);
            if (linePrimitive.localModelMatrix != null) {
                startP$3.applyMatrix4(linePrimitive.localModelMatrix);
                moveP$3.applyMatrix4(linePrimitive.localModelMatrix);
            }
        }
        let initMatrix = graphCache.data.initMatrix;
        if (!primitive.localModelMatrix) {
            primitive.localModelMatrix = new Matrix4();
        }
        if (!initMatrix) {
            initMatrix = primitive.localModelMatrix.clone();
            graphCache.data.initMatrix = initMatrix;
        }
        const geo = primitive.geos[0];
        // 每次重置到初始状态
        primitive.localModelMatrix.copy(initMatrix);
        primitive.updateWorldModelMatrix(false, true);
        primitive.needRedraw = true;
        geo.height = 1;
        // 轴转角、k轴
        const orientationType = structComData.orientationType;
        const x = structComData.x;
        const y = structComData.y;
        const z = structComData.z;
        const angle = structComData.angle;
        // 根据K点计算
        if (OrientationType.K === orientationType && x != null && y != null && z != null) {
            // point指向k，point指向move是x轴，x轴叉乘k得到-y轴
            _p$6.set(x, y, z);
            _p1$5.subVectors(_p$6, startP$3).normalize(); // point指向k
            _v1$a.subVectors(moveP$3, startP$3).normalize(); // x轴
            _v2$8.crossVectors(_v1$a, _p1$5).multiplyScalar(-1).normalize(); // y轴
            _v3$4.crossVectors(_v1$a, _v2$8).normalize(); // z轴
            // 构建变换矩阵
            matrix$2.set(_v1$a.x, _v2$8.x, _v3$4.x, 0, _v1$a.y, _v2$8.y, _v3$4.y, 0, _v1$a.z, _v2$8.z, _v3$4.z, 0, 0, 0, 0, 1);
            primitive.localModelMatrix.copy(matrix$2);
            primitive.updateWorldModelMatrix(false, true);
        }
        else if (OrientationType.DIRECTION === orientationType && x != null && y != null && z != null) {
            // point指向move是x轴，x轴叉乘k得到-y轴
            _p$6.set(x, y, z);
            _v1$a.subVectors(moveP$3, startP$3).normalize(); // x轴
            _v2$8.crossVectors(_v1$a, _p$6).multiplyScalar(-1).normalize(); // y轴
            _v3$4.crossVectors(_v1$a, _v2$8).normalize(); // z轴
            // 构建变换矩阵
            matrix$2.set(_v1$a.x, _v2$8.x, _v3$4.x, 0, _v1$a.y, _v2$8.y, _v3$4.y, 0, _v1$a.z, _v2$8.z, _v3$4.z, 0, 0, 0, 0, 1);
            primitive.localModelMatrix.copy(matrix$2);
            primitive.updateWorldModelMatrix(false, true);
        }
        else {
            // 计算旋转角度
            workPlane.getNormalizeX(_p$6);
            _vec$2.subVectors(moveP$3, startP$3).normalize();
            // 计算旋转四元数
            _quaternion$2.setFromUnitVectors(_p$6, _vec$2);
            const rotationMatrix = new Matrix4();
            rotationMatrix.makeRotationFromQuaternion(_quaternion$2);
            primitive.localModelMatrix.premultiply(rotationMatrix);
            primitive.updateWorldModelMatrix(false, true);
        }
        _v1$a.setFromMatrixColumn(primitive.localModelMatrix, 0);
        // if (OrientationType.ANGLE === orientationType && angle != null && Math.abs(angle) > 1e-4) {
        if (angle != null && Math.abs(angle) > 1e-4) {
            primitive.rotate(primitive.getP(), _v1$a, Math.PI * angle / 180);
        }
        // 计算偏移和高度
        const height = this.calcOffset();
        primitive.move(startP$3);
        geo.height = height;
        const structComInfos = graphCache.data['struct_com_info'];
        const uuid = BaseUtils.genUUID();
        const structComInfo = new StructComponentInfo(uuid, primitive.uuid);
        // 更新存储构件信息
        structComInfo.primitiveUuid = primitive.uuid;
        structComInfo.properties = {
            sectionId: structComData.sectionId,
            materialId: structComData.materialId,
            thickness: structComData.thickness,
            height: structComData.height,
            x: structComData.x,
            y: structComData.y,
            z: structComData.z,
            angle: structComData.angle,
            orientationType: structComData.orientationType,
            type: structComData.type,
            comTypeUuid: structComData.comTypeUuid,
        };
        structComInfos.push(structComInfo);
        primitive.needRedraw = true;
    }
    // 计算y和z轴退让
    calcOffset() {
        const graphCache = this.node.graph.cache;
        const structComData = graphCache.data.structComData;
        const geo = graphCache.data.geo;
        const primitive = graphCache.data.primitive;
        const distance = moveP$3.distanceTo(startP$3);
        let height = distance; // 新的拉伸高度
        // 计算偏心参照
        const offsetIndex = structComData.offsetIndex;
        if (offsetIndex != null && !isNaN(offsetIndex) && offsetIndex >= 0 && offsetIndex <= 8) {
            const offsets = graphCache.data.offsets;
            // 获取y和z轴
            _v1$a.setFromMatrixColumn(primitive.localModelMatrix, 1);
            _v2$8.setFromMatrixColumn(primitive.localModelMatrix, 2);
            if (offsets != null && offsets.length === 18) {
                const width = offsets[offsetIndex * 2];
                const height = offsets[offsetIndex * 2 + 1];
                // startP往y轴移动width，z轴移动height
                startP$3.addScaledVector(_v1$a, width);
                startP$3.addScaledVector(_v2$8, height);
            }
        }
        const ey = structComData.ey;
        const ez = structComData.ez;
        const ex = structComData.ex;
        // x轴退让
        if (!!ex && ex.length === 2) {
            const startEx = ex[0];
            const endEx = ex[1];
            const newHeight = -startEx + endEx;
            if (height + newHeight < 1e-4) {
                height = 1;
            }
            else {
                height += newHeight;
            }
            _v3$4.setFromMatrixColumn(primitive.localModelMatrix, 0);
            startP$3.addScaledVector(_v3$4, startEx);
        }
        // z轴退让
        let ezOffset;
        if (!!ez && ez.length === 2) {
            const startEz = ez[0];
            const endEz = ez[1];
            ezOffset = endEz - startEz;
            // 构建起点的偏移向量
            _v3$4.setFromMatrixColumn(primitive.localModelMatrix, 2);
            startP$3.addScaledVector(_v3$4, startEz);
        }
        // y轴退让
        let eyOffset;
        if (!!ey && ey.length === 2) {
            const startEy = ey[0];
            const endEy = ey[1];
            eyOffset = endEy - startEy;
            _v3$4.setFromMatrixColumn(primitive.localModelMatrix, 1);
            startP$3.addScaledVector(_v3$4, startEy);
        }
        let zAxis = false;
        let yAxis = false;
        // 转换到局部坐标
        if (ezOffset != null && Math.abs(ezOffset) > 1e-4) {
            _v1$a.setFromMatrixColumn(primitive.localModelMatrix, 1).normalize();
            _v1$a.multiplyScalar(ezOffset);
            zAxis = true;
        }
        if (eyOffset != null && Math.abs(eyOffset) > 1e-4) {
            _v2$8.setFromMatrixColumn(primitive.localModelMatrix, 2).normalize();
            _v2$8.multiplyScalar(-eyOffset);
            yAxis = true;
        }
        if (zAxis && yAxis) {
            _p1$5.addVectors(_v1$a, _v2$8);
            geo.offsetDirection = _p1$5;
        }
        else if (zAxis) {
            geo.offsetDirection = _v1$a;
        }
        else if (yAxis) {
            geo.offsetDirection = _v2$8;
        }
        return height;
    }
}
const startP$3 = new Vector3();
const moveP$3 = new Vector3();
const _vec$2 = new Vector3();
const _p$6 = new Vector3();
const _p1$5 = new Vector3();
const _v1$a = new Vector3();
const _v2$8 = new Vector3();
const _v3$4 = new Vector3();
const matrix$2 = new Matrix4();
const _quaternion$2 = new Quaternion();/**
 * 选中几何线布置线性构件
 */
class EventGraphStructComLine extends EventGraphBase {
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node2 = new EventGraphNode(this, EventHandlerListenStructComData);
        const node3 = new EventGraphNode(this, EventHandlerDisableSelect);
        const node4 = new EventGraphNode(this, EventHandlerSelectStart);
        const node5 = new EventGraphNodeNotice(this, { cmd: 'Notice', data: '选择选择线段图元或轴线段' });
        const node6 = new EventGraphNode(this, EventHandlerCalPointerUp);
        const node7 = new EventGraphNodeSwitch(this, () => {
            const storage = this.eventMediator.storage;
            const selectPrimitives = storage.selectStore.selectPrimitives;
            if (selectPrimitives.length === 0) {
                return [0];
            }
            let existLine = false;
            for (const primitive of selectPrimitives) {
                if (primitive.type === PrimitiveType.Line || primitive.type === 'AxisLineSegment' || primitive.type === "VerticalAxisLine") {
                    existLine = true;
                    break;
                }
            }
            if (existLine)
                return [1];
            return [0];
        });
        const node8 = new EventGraphNode(this, EventHandlerGrid);
        const node9 = new EventGraphNode(this, EventHandlerCalStructComLine);
        const node10 = new EventGraphNode(this, EventHandlerGenPriAndStructInfo);
        const node11 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node12 = new EventGraphNode(this, EventHandlerEmptySelect);
        const node14 = new EventGraphNode(this, EventHandlerEmptySelect);
        const node15 = new EventGraphNode(this, EventHandlerGrid);
        const node16 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node20 = new EventGraphNode(this, EventHandlerClearTempStructCom);
        const node21 = new EventGraphNode(this, EventHandlerDisableClamp);
        const node23 = new EventGraphNode(this, EventHandlerGenSectionRegion);
        const node24 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node25 = new EventGraphNode(this, EventHandlerConfirm);
        const node26 = new EventGraphNode(this, EHChangePickCursor);
        const node27 = new EventGraphNode(this, EventHandlerListenStructDataContinue);
        const node28 = new EventGraphNode(this, EventHandlerCalStructRegion);
        const node29 = new EventGraphNode(this, EventHandlerCalPrimitiveInView);
        this.addEdge(node1, node3);
        this.addEdge(node5, node6);
        this.addEdge(node6, node7);
        /**没有选中轴线或线段*/
        this.addEdge(node7, node14);
        this.addEdge(node14, node15);
        this.addEdge(node15, node16);
        this.addEdge(node16, node5);
        this.addEdge(node12, node8);
        this.addEdge(node3, node21);
        this.addEdge(node21, node2);
        this.addEdge(node20, node12);
        this.addEdge(node4, node5);
        this.addEdge(node9, node10);
        this.addEdge(node10, node20);
        this.addEdge(node1, node25);
        this.addEdge(node25, node24);
        this.addEdge(node11, node5);
        this.addEdge(node2, node26);
        this.addEdge(node26, node4);
        this.addEdge(node2, node27);
        this.addEdge(node23, node28);
        this.addEdge(node28, node9);
        this.addEdge(node8, node29);
        this.addEdge(node29, node11);
        /**选中轴线或线段*/
        this.addEdge(node7, node23);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/134
    }
}
EventGraphStructComLine.cmd = "STRUCT_COM_LINE";/**
 * 计算布置板的图元
 */
class EventHandlerCalBoard extends EventHandlerBase {
    run(data) {
        var _a;
        const graphCache = this.node.graph.cache;
        const storage = this.getPythaStorage();
        const structComData = graphCache.data.structComData;
        const primitiveList = graphCache.primitiveList; // 选中的面域图元
        if (primitiveList.length !== 1) {
            // 正常的板应该只生成一个面域
            graphCache.primitiveList = [];
            for (let i = 0; i < graphCache.data.primitives.length; i++) {
                const primitive = graphCache.data.primitives[i];
                primitive.needDelete = true;
            }
            return true;
        }
        // 板的厚度，就是拉伸高度
        let thickness = structComData.thickness;
        if (thickness == null || isNaN(thickness) || thickness <= 0) {
            graphCache.primitiveList = [];
            return true;
        }
        const primitive = primitiveList[0];
        const region = primitive.geos[0];
        primitiveList.forEach(p => {
            p.needDelete = true;
        });
        // 根据面域创建拉伸体
        const ext = new ExtrudeSolid(region, thickness);
        const board = new Primitive(PrimitiveType.ExtrudeSoild);
        board.needRedraw = true;
        board.temporary = true;
        board.geos.push(ext);
        graphCache.primitiveList = [];
        graphCache.primitiveList.push(board);
        const keys = Object.keys(board.renderItemDict);
        board.colorByLayer = true;
        if (keys.length > 0) {
            let renderItem = board.renderItemDict[keys[0]];
            renderItem.geoElements = ext.getGeoElementsIdxList();
            (_a = renderItem.mat) === null || _a === void 0 ? void 0 : _a.color.set(storage.layerStore.getColor());
        }
        storage.primitiveStore.tempPrimitiveDict[board.uuid] = board;
        const structComInfos = [];
        const uuid = BaseUtils.genUUID();
        const structComInfo = new StructComponentInfo(uuid, primitive.uuid);
        // 更新存储构件信息
        structComInfo.primitiveUuid = primitive.uuid;
        structComInfo.properties = {
            sectionId: structComData.sectionId,
            materialId: structComData.materialId,
            thickness: structComData.thickness,
            height: structComData.height,
            x: structComData.x,
            y: structComData.y,
            z: structComData.z,
            angle: structComData.angle,
            orientationType: structComData.orientationType,
            type: structComData.type,
            comTypeUuid: structComData.comTypeUuid,
        };
        structComInfos.push(structComInfo);
        graphCache.data['struct_com_info'] = structComInfos;
        primitive.needRedraw = true;
        return true;
    }
}/**
 * 创建布置板的临时线
 */
class EventHandlerCreateBoardLine extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        const storage = this.getPythaStorage();
        storage.coordinateStore.workPlane;
        const point = graphCache.pointerDownList[graphCache.pointerDownList.length - 1].point;
        const movePoint = graphCache.pointerMove.point;
        _v1$9.copy(point);
        _v2$7.copy(movePoint);
        // workPlane.projectPoint(point, _v1);
        // workPlane.projectPoint(movePoint, _v2);
        const line = new Line(_v1$9, _v2$7);
        const primitive = new Primitive(PrimitiveType.Line);
        primitive.needRedraw = true;
        primitive.temporary = true;
        primitive.geos.push(line);
        const keys = Object.keys(primitive.renderItemDict);
        if (keys.length > 0) {
            let renderItem = primitive.renderItemDict[keys[0]];
            renderItem.geoElements = line.getGeoElementsIdxList();
        }
        graphCache.primitiveList.push(primitive);
        storage.primitiveStore.tempPrimitiveDict[primitive.uuid] = primitive;
        graphCache.data.line = line;
        graphCache.data.primitive = primitive;
        return true;
    }
}
const _v1$9 = new Vector3();
const _v2$7 = new Vector3();/**
 * 计算布置板的临时线
 */
class EventHandlerCalBoardCornerPointLine extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        const storage = this.getPythaStorage();
        storage.coordinateStore.workPlane;
        const line = graphCache.data.line;
        const primitive = graphCache.data.primitive;
        if (!line || !primitive)
            return true;
        const movePoint = graphCache.pointerMove.point;
        _v1$8.copy(movePoint);
        // workPlane.projectPoint(movePoint, _v1);
        line.end = _v1$8;
        primitive.needRedraw = true;
        return true;
    }
}
const _v1$8 = new Vector3();/**
 * 闭合临时线
 */
class EventHandlerCloseBoardCornerPoint extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        const storage = this.getPythaStorage();
        storage.coordinateStore.workPlane;
        // 判断是否闭合
        const isClose = graphCache.data.icClose;
        if (isClose) {
            const primitives = [];
            graphCache.primitiveList.forEach(p => {
                primitives.push(p);
            });
            graphCache.primitiveList = [];
            graphCache.data.primitives = primitives; // 用于生成面域
            return true;
        }
        const primitiveList = graphCache.primitiveList;
        const first = primitiveList[0];
        let primitive = graphCache.data.primitive;
        let line = graphCache.data.line;
        // 防止不移动点直接闭合
        if (line == null) {
            const point = graphCache.pointerDownList[graphCache.pointerDownList.length - 1].point;
            const movePoint = graphCache.pointerMove.point;
            _v1$7.copy(point);
            _v2$6.copy(movePoint);
            // workPlane.projectPoint(point, _v1);
            // workPlane.projectPoint(movePoint, _v2);
            line = new Line(_v1$7, _v2$6);
            primitive = new Primitive(PrimitiveType.Line);
            primitive.needRedraw = true;
            primitive.temporary = true;
            primitive.geos.push(line);
            const keys = Object.keys(primitive.renderItemDict);
            if (keys.length > 0) {
                let renderItem = primitive.renderItemDict[keys[0]];
                renderItem.geoElements = line.getGeoElementsIdxList();
            }
            graphCache.primitiveList.push(primitive);
            storage.primitiveStore.tempPrimitiveDict[primitive.uuid] = primitive;
        }
        // 更新最后一条线的终点
        const startL = first.geos[0];
        _v2$6.copy(startL.start);
        // workPlane.projectPoint(_v2, _v1);
        line.end = _v2$6;
        const primitives = [];
        graphCache.primitiveList.forEach(p => {
            primitives.push(p);
        });
        graphCache.primitiveList = [];
        graphCache.data.primitives = primitives; // 用于生成面域
        return true;
    }
}
const _v1$7 = new Vector3();
const _v2$6 = new Vector3();
new Vector3();
new Vector3();/**
 * 计算布置板的偏移
 */
class EventHandlerCalBoardOffset extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        this.getPythaStorage();
        const structComData = graphCache.data.structComData;
        const ez = structComData.ez;
        if (ez == null || !Array.isArray(ez) || ez.length !== 2) {
            return true;
        }
        // 计算偏移,只需要处理ez
        const board = graphCache.primitiveList[0];
        if (!board)
            return true;
        if (!board.localModelMatrix) {
            board.localModelMatrix = new Matrix4();
        }
        _v$2.setFromMatrixColumn(board.localModelMatrix, 2);
        const ezOffset = ez[0];
        _p$5.copy(_p1$4);
        _p$5.addScaledVector(_v$2, ezOffset);
        board.move(_p$5);
        board.needRedraw = true;
        return true;
    }
}
const _v$2 = new Vector3();
const _p$5 = new Vector3();
const _p1$4 = new Vector3();const BOARD_CORNER_POINT_OPTION = {
    CLOSE: 'C',
};
/**
 * 角点布置板
 */
class EventGraphBoardCornerPoint extends EventGraphBase {
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node2 = new EventGraphNode(this, EventHandlerListenStructComData);
        const node3 = new EventGraphNode(this, EventHandlerCalPointerDown);
        const node4 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [{
                    type: NeedInputEnum.ACTIVE,
                    description: "指定第一个点",
                    option: '',
                    inputTypes: [PythaEventName.POINTER_DOWN]
                }]
        });
        const node5 = new EventGraphNode(this, EventHandlerDisableSelect);
        const node6 = new EventGraphNode(this, EventHandlerInitCapture);
        const node7 = new EventGraphNode(this, EventHandlerCalPointerMove);
        const node8 = new EventGraphNode(this, EventHandlerCapture);
        const node9 = new EventGraphNodeAND(this, null);
        const node10 = new EventGraphNode(this, EventHandlerCalBoard);
        const node11 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [{
                    type: NeedInputEnum.ACTIVE,
                    description: "指定下一个点",
                    option: '',
                    inputTypes: [PythaEventName.POINTER_DOWN]
                }]
        });
        const node12 = new EventGraphNode(this, EventHandlerCalPointerDown);
        const node13 = new EventGraphNode(this, EventHandlerGrid);
        const node14 = new EventGraphNodeRender(this, true, UpdateType.TEMP);
        const node16 = new EventGraphNode(this, EventHandlerGrid);
        const node17 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node18 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node21 = new EventGraphNode(this, null);
        const node22 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [{
                    type: NeedInputEnum.ACTIVE,
                    description: "指定下一个点",
                    option: '',
                    inputTypes: [PythaEventName.POINTER_DOWN]
                }, {
                    type: NeedInputEnum.OPTION,
                    description: "闭合",
                    option: BOARD_CORNER_POINT_OPTION.CLOSE
                }]
        });
        const node23 = new EventGraphNodeSwitch(this, () => {
            if (this.cache.pointerDownList.length < 3) {
                return [0];
            }
            return [1];
        });
        const node24 = new EventGraphNode(this, EventHandlerOptionInput);
        const node26 = new EventGraphNode(this, EventHandlerCalBoardCornerPointLine);
        const node28 = new EventGraphNode(this, EventHandlerGenPriAndStructInfo);
        const node33 = new EventGraphNode(this, EventHandlerCreateBoardLine);
        const node35 = new EventGraphNode(this, EventHandlerCloseBoardCornerPoint);
        const node36 = new EventGraphNode(this, EventHandlerCreateBoardLine);
        const node37 = new EventGraphNode(this, EventHandlerGenRegion);
        const node38 = new EventGraphNode(this, EventHandlerCalBoardOffset);
        const node39 = new EventGraphNode(this, EHChangeCrossCursor);
        const node40 = new EventGraphNode(this, EventHandlerConfirm);
        const node41 = new EventGraphNode(this, EventHandlerListenStructDataContinue);
        const node42 = new EventGraphNodeSwitch(this, () => {
            const graphCache = this.cache;
            const firstPoint = graphCache.pointerDownList[0].point;
            const lastPoint = graphCache.pointerDownList[graphCache.pointerDownList.length - 1].point;
            if (Math.abs(firstPoint.x - lastPoint.x) < 1e-4 && Math.abs(firstPoint.y - lastPoint.y) < 1e-4 &&
                Math.abs(firstPoint.z - lastPoint.z) < 1e-4) {
                const primitives = [];
                graphCache.primitiveList.forEach(p => {
                    primitives.push(p);
                });
                graphCache.primitiveList = [];
                graphCache.data.primitives = primitives; // 用于生成面域
                return [0];
            }
            return [1];
        });
        const node43 = new EventGraphNode(this, EventHandlerCalPrimitiveInView);
        const node44 = new EventGraphNodeSwitch(this, () => {
            if (this.cache.primitiveList.length !== 1) {
                return [1];
            }
            return [0];
        });
        const node45 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [{
                    type: NeedInputEnum.ACTIVE,
                    description: "无法生成面域，重新指定第一个点",
                    option: '',
                    inputTypes: [PythaEventName.POINTER_DOWN]
                }]
        });
        const node46 = new EventGraphNodeRender(this, true, UpdateType.TEMP);
        const node47 = new EventGraphNode(this, EventHandlerCalPointerDown);
        this.addEdge(node1, node5);
        this.addEdge(node5, node2);
        this.addEdge(node4, node3);
        this.addEdge(node11, node12);
        this.addEdge(node13, node14);
        this.addEdge(node16, node17);
        this.addEdge(node3, node21);
        this.addEdge(node21, node23);
        this.addEdge(node23, node11);
        this.addEdge(node22, node24);
        this.addEdge(node22, node12);
        this.addEdge(node9, node26);
        this.addEdge(node3, node33);
        this.addEdge(node33, node9);
        this.addEdge(node26, node13);
        this.addEdge(node24, node35);
        this.addEdge(node36, node21);
        this.addEdge(node35, node37);
        this.addEdge(node37, node10);
        this.addEdge(node6, node7);
        this.addEdge(node7, node8);
        this.addEdge(node8, node9);
        this.addEdge(node38, node28);
        this.addEdge(node28, node16);
        this.addEdge(node2, node39);
        this.addEdge(node39, node6);
        this.addEdge(node39, node4);
        this.addEdge(node1, node40);
        this.addEdge(node40, node18);
        this.addEdge(node2, node41);
        this.addEdge(node12, node42);
        this.addEdge(node42, node37);
        this.addEdge(node17, node43);
        this.addEdge(node43, node18);
        this.addEdge(node10, node44);
        /**正常生成了面域*/
        this.addEdge(node44, node38);
        this.addEdge(node46, node45);
        this.addEdge(node45, node47);
        this.addEdge(node47, node21);
        this.addEdge(node23, node22);
        /**确定点不与起点重合*/
        this.addEdge(node42, node36);
        /**无法形成正常面域*/
        this.addEdge(node44, node46);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/121
    }
}
EventGraphBoardCornerPoint.cmd = "STRUCT_PLATE_CORNER_POINT";/**
 * 计算面域布置板构件的临时图元
 */
class EventHandlerCalStructBoardRegion extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        const structComData = graphCache.data.structComData;
        if (structComData == null || Object.keys(structComData).length == 0) {
            return true;
        }
        const height = structComData.thickness;
        if (height === null || isNaN(height) || height <= 0)
            return true;
        const storage = this.getPythaStorage();
        const selectPrimitives = storage.selectStore.selectPrimitives;
        const regionPrimitives = [];
        selectPrimitives.forEach(primitive => {
            if (primitive.type === PrimitiveType.RegionFace) {
                regionPrimitives.push(primitive);
            }
        });
        graphCache.data['struct_com_info'] = [];
        regionPrimitives.forEach(regionPrimitive => {
            this.calcRegionStructBoard(regionPrimitive, structComData, height);
        });
        return true;
    }
    // 计算面域布置板构件的临时图元
    calcRegionStructBoard(regionPrimitive, structComData, height) {
        var _a;
        const graphCache = this.node.graph.cache;
        const storage = this.getPythaStorage();
        const region = regionPrimitive.geos[0];
        const ext = new ExtrudeSolid(region, height);
        const primitive = new Primitive(PrimitiveType.ExtrudeSoild);
        primitive.geos.push(ext);
        primitive.localModelMatrix = new Matrix4();
        if (regionPrimitive.localModelMatrix != null) {
            primitive.localModelMatrix.copy(regionPrimitive.localModelMatrix);
            primitive.updateWorldModelMatrix(false, true);
        }
        primitive.temporary = true;
        primitive.needRedraw = true;
        primitive.colorByLayer = true;
        const keys = Object.keys(primitive.renderItemDict);
        if (keys.length > 0) {
            let renderItem = primitive.renderItemDict[keys[0]];
            renderItem.geoElements = ext.getGeoElementsIdxList();
            (_a = renderItem.mat) === null || _a === void 0 ? void 0 : _a.color.set(storage.layerStore.getColor());
        }
        graphCache.primitiveList.push(primitive);
        storage.primitiveStore.tempPrimitiveDict[primitive.uuid] = primitive;
        // 处理偏移
        const ez = structComData.ez;
        if (ez == null || !Array.isArray(ez) || ez.length !== 2) {
            return;
        }
        if (!primitive.localModelMatrix) {
            primitive.localModelMatrix = new Matrix4();
        }
        _v$1.setFromMatrixColumn(primitive.localModelMatrix, 2);
        const ezOffset = ez[0];
        _p$4.copy(_p1$3);
        _p$4.addScaledVector(_v$1, ezOffset);
        primitive.move(_p$4);
        // 生成构件信息
        const structComInfos = graphCache.data['struct_com_info'];
        const uuid = BaseUtils.genUUID();
        const structComInfo = new StructComponentInfo(uuid, primitive.uuid);
        structComInfo.properties = {
            sectionId: structComData.sectionId,
            materialId: structComData.materialId,
            thickness: structComData.thickness,
            height: structComData.height,
            x: structComData.x,
            y: structComData.y,
            z: structComData.z,
            angle: structComData.angle,
            orientationType: structComData.orientationType,
            type: structComData.type,
            comTypeUuid: structComData.comTypeUuid,
        };
        structComInfos.push(structComInfo);
    }
}
const _v$1 = new Vector3();
const _p$4 = new Vector3();
const _p1$3 = new Vector3();/**
 * 面域布置板
 */
class EventGraphStructBoardRegion extends EventGraphBase {
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node2 = new EventGraphNode(this, EventHandlerDisableSelect);
        const node3 = new EventGraphNode(this, EventHandlerListenStructComData);
        const node4 = new EventGraphNode(this, EventHandlerSelectStart);
        const node6 = new EventGraphNodeNotice(this, { cmd: 'Notice', data: '请选择一个面域' });
        const node7 = new EventGraphNode(this, EventHandlerCalPointerUp);
        const node8 = new EventGraphNodeSwitch(this, () => {
            const storage = this.eventMediator.storage;
            const selectPrimitives = storage.selectStore.selectPrimitives;
            if (selectPrimitives.length === 0) {
                return [0];
            }
            let existRegion = false;
            for (const primitive of selectPrimitives) {
                if (primitive.type === PrimitiveType.RegionFace) {
                    existRegion = true;
                    break;
                }
            }
            if (existRegion)
                return [1];
            return [0];
        });
        const node9 = new EventGraphNode(this, EventHandlerEmptySelect);
        const node12 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node13 = new EventGraphNode(this, EventHandlerGrid);
        const node15 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node21 = new EventGraphNode(this, EventHandlerGenPriAndStructInfo);
        const node22 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node23 = new EventGraphNode(this, EventHandlerCalStructBoardRegion);
        const node24 = new EventGraphNode(this, EventHandlerEmptySelect);
        const node25 = new EventGraphNode(this, EventHandlerConfirm);
        const node26 = new EventGraphNode(this, EventHandlerClearTempStructCom);
        const node27 = new EventGraphNode(this, EHChangePickCursor);
        const node28 = new EventGraphNode(this, EventHandlerListenStructDataContinue);
        const node29 = new EventGraphNode(this, EventHandlerCalPrimitiveInView);
        this.addEdge(node1, node2);
        this.addEdge(node2, node3);
        this.addEdge(node6, node7);
        this.addEdge(node7, node8);
        /**没有选中面域*/
        this.addEdge(node8, node9);
        this.addEdge(node12, node6);
        this.addEdge(node13, node15);
        this.addEdge(node4, node6);
        this.addEdge(node23, node21);
        this.addEdge(node21, node24);
        this.addEdge(node24, node13);
        this.addEdge(node1, node25);
        this.addEdge(node25, node22);
        this.addEdge(node9, node12);
        this.addEdge(node15, node26);
        this.addEdge(node3, node27);
        this.addEdge(node27, node4);
        this.addEdge(node3, node28);
        this.addEdge(node26, node29);
        this.addEdge(node29, node6);
        /**至少选中一个面域*/
        this.addEdge(node8, node23);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/122
    }
}
EventGraphStructBoardRegion.cmd = "STRUCT_PLATE_REGION";/**
 * 初始创建拉伸高度
 */
const DefaultExtHeight = 1;
/**
 * 创建墙的拉伸体
 */
class EventHandlerGenStructWallExt extends EventHandlerBase {
    run(data) {
        // 这里生成的底面和梁保持一致，旋转让世界坐标的x作为z轴
        this.generate();
        return true;
    }
    generate() {
        var _a;
        const graphCache = this.node.graph.cache;
        const structComData = graphCache.data.structComData;
        const thickness = structComData.thickness;
        const height = structComData.height;
        if (height == null || thickness == null || isNaN(height) || isNaN(thickness) || height <= 0 || thickness <= 0) {
            return;
        }
        // 底面的线
        _v1$6.set(-thickness / 2, -height / 2, 0);
        _v2$5.set(thickness / 2, -height / 2, 0);
        const b1 = new Line(_v1$6, _v2$5);
        // 顶面的线
        _v1$6.set(thickness / 2, height / 2, 0);
        _v2$5.set(-thickness / 2, height / 2, 0);
        const t1 = new Line(_v1$6, _v2$5);
        // 左侧面线
        _v1$6.set(-thickness / 2, height / 2, 0);
        _v2$5.set(-thickness / 2, -height / 2, 0);
        const l1 = new Line(_v1$6, _v2$5);
        // 右侧面线
        _v1$6.set(thickness / 2, -height / 2, 0);
        _v2$5.set(thickness / 2, height / 2, 0);
        const r1 = new Line(_v1$6, _v2$5);
        // 生成面域
        const region = new RegionFace([b1, r1, t1, l1]);
        // 面域转向
        const storage = this.getPythaStorage();
        const transformMatrix = region.transformMatrix;
        const axisZ = new Vector3(0, 0, 1);
        const axisY = new Vector3(0, 1, 0);
        // 创建一个旋转矩阵，绕 Z 轴旋转 90 度
        const rotationZ = new Matrix4().makeRotationAxis(axisZ, Math.PI / 2);
        // 创建一个旋转矩阵，绕 Y 轴旋转 90 度
        const rotationY = new Matrix4().makeRotationAxis(axisY, Math.PI / 2);
        const m1 = new Matrix4().premultiply(transformMatrix).premultiply(rotationZ).premultiply(rotationY);
        transformMatrix.copy(m1);
        if (graphCache.data.geo != null && graphCache.data.primitive != null) {
            graphCache.data.primitive.needDelete = true;
            const idx = graphCache.primitiveList.findIndex(p => p.uuid === graphCache.data.primitive.uuid);
            if (idx !== -1) {
                graphCache.primitiveList.splice(idx, 1);
            }
        }
        const geo = new ExtrudeSolid(region, DefaultExtHeight);
        const primitive = new Primitive(PrimitiveType.ExtrudeSoild);
        graphCache.data.geo = geo;
        graphCache.data.primitive = primitive;
        primitive.geos.push(geo);
        primitive.temporary = true;
        primitive.needRedraw = true;
        const keys = Object.keys(primitive.renderItemDict);
        primitive.colorByLayer = true;
        if (keys.length > 0) {
            let renderItem = primitive.renderItemDict[keys[0]];
            renderItem.geoElements = geo.getGeoElementsIdxList();
            (_a = renderItem.mat) === null || _a === void 0 ? void 0 : _a.color.set(storage.layerStore.getColor());
        }
        graphCache.primitiveList.push(primitive);
        storage.primitiveStore.tempPrimitiveDict[primitive.uuid] = primitive;
    }
}
const _v1$6 = new Vector3();
const _v2$5 = new Vector3();/**
 * 角点布置墙
 */
class EventHandlerCalWallCornerPoint extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        const storage = this.getPythaStorage();
        const workPlane = storage.coordinateStore.workPlane;
        const structComData = graphCache.data.structComData;
        startP$2.copy(graphCache.pointerDownList[graphCache.pointerDownList.length - 1].point);
        moveP$2.copy(graphCache.pointerMove.point);
        let initMatrix = graphCache.data.initMatrix;
        const primitive = graphCache.data.primitive;
        const geo = graphCache.data.geo;
        if (!geo || !primitive)
            return true;
        if (!primitive.localModelMatrix) {
            primitive.localModelMatrix = new Matrix4();
        }
        if (!initMatrix) {
            initMatrix = primitive.localModelMatrix.clone();
            graphCache.data.initMatrix = initMatrix;
        }
        // 每次重置到初始状态
        primitive.localModelMatrix.copy(initMatrix);
        primitive.updateWorldModelMatrix(false, true);
        primitive.needRedraw = true;
        geo.height = 1;
        // 轴转角、k轴
        const orientationType = structComData.orientationType;
        const x = structComData.x;
        const y = structComData.y;
        const z = structComData.z;
        // 根据轴转角计算
        if (OrientationType.K === orientationType && x != null && y != null && z != null) {
            // point指向k，point指向move是x轴，x轴叉乘k得到-y轴
            _p$3.set(x, y, z);
            _p1$2.subVectors(_p$3, startP$2).normalize(); // point指向k
            _v1$5.subVectors(moveP$2, startP$2).normalize(); // x轴
            _v2$4.crossVectors(_v1$5, _p1$2).multiplyScalar(-1).normalize(); // y轴
            _v3$3.crossVectors(_v1$5, _v2$4).normalize(); // z轴
            // 构建变换矩阵
            matrix$1.set(_v1$5.x, _v2$4.x, _v3$3.x, 0, _v1$5.y, _v2$4.y, _v3$3.y, 0, _v1$5.z, _v2$4.z, _v3$3.z, 0, 0, 0, 0, 1);
            primitive.localModelMatrix.copy(matrix$1);
            primitive.updateWorldModelMatrix(false, true);
        }
        else if (orientationType == null || OrientationType.DIRECTION === orientationType) {
            // point指向move是x轴，x轴叉乘k得到-y轴
            _p$3.set(0, 0, 1);
            _v1$5.subVectors(moveP$2, startP$2).normalize(); // x轴
            _v2$4.crossVectors(_v1$5, _p$3).multiplyScalar(-1).normalize(); // y轴
            _v3$3.crossVectors(_v1$5, _v2$4).normalize(); // z轴
            // 构建变换矩阵
            matrix$1.set(_v1$5.x, _v2$4.x, _v3$3.x, 0, _v1$5.y, _v2$4.y, _v3$3.y, 0, _v1$5.z, _v2$4.z, _v3$3.z, 0, 0, 0, 0, 1);
            primitive.localModelMatrix.copy(matrix$1);
            primitive.updateWorldModelMatrix(false, true);
        }
        else {
            // 计算旋转角度
            workPlane.getNormalizeX(_p$3);
            _vec$1.subVectors(moveP$2, startP$2).normalize();
            // 计算旋转四元数
            _quaternion$1.setFromUnitVectors(_p$3, _vec$1);
            const rotationMatrix = new Matrix4();
            rotationMatrix.makeRotationFromQuaternion(_quaternion$1);
            primitive.localModelMatrix.premultiply(rotationMatrix);
            primitive.updateWorldModelMatrix(false, true);
        }
        graphCache.data.startP = startP$2;
        graphCache.data.moveP = moveP$2;
        return true;
    }
}
const startP$2 = new Vector3();
const moveP$2 = new Vector3();
const _vec$1 = new Vector3();
const _p$3 = new Vector3();
const _p1$2 = new Vector3();
const _v1$5 = new Vector3();
const _v2$4 = new Vector3();
const _v3$3 = new Vector3();
const matrix$1 = new Matrix4();
const _quaternion$1 = new Quaternion();/**
 * 计算布置墙的偏移
 */
class EventHandlerCalStructWallOffset extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        const primitive = graphCache.data.primitive;
        const geo = graphCache.data.geo;
        if (!geo || !primitive)
            return true;
        const structComData = graphCache.data.structComData;
        if (!structComData)
            return true;
        startP$1.copy(graphCache.data.startP);
        moveP$1.copy(graphCache.data.moveP);
        const height = this.calcOffset();
        primitive.move(startP$1);
        geo.height = height;
        primitive.needRedraw = true;
        const structComInfos = [];
        const uuid = BaseUtils.genUUID();
        const structComInfo = new StructComponentInfo(uuid, primitive.uuid);
        structComInfo.properties = {
            sectionId: structComData.sectionId,
            materialId: structComData.materialId,
            thickness: structComData.thickness,
            height: structComData.height,
            x: structComData.x,
            y: structComData.y,
            z: structComData.z,
            angle: structComData.angle,
            orientationType: structComData.orientationType,
            type: structComData.type,
            comTypeUuid: structComData.comTypeUuid,
        };
        structComInfos.push(structComInfo);
        graphCache.data['struct_com_info'] = structComInfos;
        return true;
    }
    // 计算y和z轴退让
    calcOffset() {
        const graphCache = this.node.graph.cache;
        const structComData = graphCache.data.structComData;
        const primitive = graphCache.data.primitive;
        const halfThickness = structComData.thickness / 2;
        const halfHeight = structComData.height / 2;
        if (halfThickness == null || isNaN(halfThickness) || halfThickness <= 0)
            return 0;
        if (halfHeight == null || isNaN(halfHeight) || halfHeight <= 0)
            return 0;
        const distance = moveP$1.distanceTo(startP$1);
        let height = distance; // 新的拉伸高度
        // 计算偏心参照
        const offsetIndex = structComData.offsetIndex;
        if (offsetIndex != null && !isNaN(offsetIndex) && offsetIndex >= 0 && offsetIndex <= 8) {
            let width = 0;
            let height = 0;
            if (offsetIndex == 0) {
                width = -halfThickness;
                height = -halfHeight;
            }
            else if (offsetIndex === 1) {
                height = -halfHeight;
            }
            else if (offsetIndex === 2) {
                width = halfThickness;
                height = -halfHeight;
            }
            else if (offsetIndex === 3) {
                width = -halfThickness;
            }
            else if (offsetIndex === 5) {
                width = halfThickness;
            }
            else if (offsetIndex === 6) {
                width = -halfThickness;
                height = halfHeight;
            }
            else if (offsetIndex === 7) {
                height = halfHeight;
            }
            else if (offsetIndex === 8) {
                width = halfThickness;
                height = halfHeight;
            }
            _v1$4.setFromMatrixColumn(primitive.localModelMatrix, 1);
            _v2$3.setFromMatrixColumn(primitive.localModelMatrix, 2);
            startP$1.addScaledVector(_v1$4, width);
            startP$1.addScaledVector(_v2$3, height);
        }
        const ez = structComData.ez;
        // z轴退让
        if (!!ez && ez.length === 2) {
            const ezOffset = ez[0];
            // 构建起点的偏移向量
            if (!primitive.localModelMatrix)
                primitive.localModelMatrix = new Matrix4();
            _v3$2.setFromMatrixColumn(primitive.localModelMatrix, 1);
            startP$1.addScaledVector(_v3$2, ezOffset);
        }
        return height;
    }
}
const startP$1 = new Vector3();
const moveP$1 = new Vector3();
const _v1$4 = new Vector3();
const _v2$3 = new Vector3();
const _v3$2 = new Vector3();/**
 * 删除当前临时图元
 */
class EventHandlerDeleteTempPri extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        graphCache.primitiveList.forEach(pri => {
            pri.needDelete = true;
        });
        return true;
    }
}/**
 * 结构角点布置墙
 */
class EventGraphStructWallCornerPoint extends EventGraphBase {
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node2 = new EventGraphNode(this, EventHandlerListenStructComData);
        const node3 = new EventGraphNode(this, EventHandlerCalPointerDown);
        const node4 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [
                { type: NeedInputEnum.ACTIVE, description: "指定第一个点", option: '', inputTypes: [PythaEventName.POINTER_DOWN] },
            ]
        });
        const node5 = new EventGraphNode(this, EventHandlerDisableSelect);
        const node6 = new EventGraphNode(this, EventHandlerInitCapture);
        const node7 = new EventGraphNode(this, EventHandlerCalPointerMove);
        const node8 = new EventGraphNode(this, EventHandlerCapture);
        const node9 = new EventGraphNodeAND(this, null);
        const node10 = new EventGraphNode(this, EventHandlerCalWallCornerPoint);
        const node11 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [
                { type: NeedInputEnum.ACTIVE, description: "指定下一个点", option: '', inputTypes: [PythaEventName.POINTER_DOWN] },
            ]
        });
        const node12 = new EventGraphNode(this, EventHandlerCalPointerDown);
        const node13 = new EventGraphNode(this, EventHandlerGrid);
        const node14 = new EventGraphNodeRender(this, true, UpdateType.TEMP);
        const node15 = new EventGraphNode(this, EventHandlerGenPriAndStructInfo);
        const node16 = new EventGraphNode(this, EventHandlerGrid);
        const node17 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node21 = new EventGraphNode(this, EventHandlerClearTempStructCom);
        const node22 = new EventGraphNode(this, EventHandlerCalStructWallOffset);
        const node23 = new EventGraphNode(this, EventHandlerGenStructWallExt);
        const node24 = new EventGraphNode(this, EHChangeCrossCursor);
        const node25 = new EventGraphNode(this, EventHandlerConfirm);
        const node26 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node27 = new EventGraphNode(this, EventHandlerListenStructDataContinue);
        const node28 = new EventGraphNode(this, EventHandlerDeleteTempPri);
        const node29 = new EventGraphNode(this, EventHandlerGenStructWallExt);
        const node30 = new EventGraphNodeRender(this, true, UpdateType.TEMP);
        const node31 = new EventGraphNode(this, EventHandlerCalPrimitiveInView);
        this.addEdge(node1, node5);
        this.addEdge(node5, node2);
        this.addEdge(node4, node3);
        this.addEdge(node9, node10);
        this.addEdge(node3, node11);
        this.addEdge(node11, node12);
        this.addEdge(node13, node14);
        this.addEdge(node21, node16);
        this.addEdge(node10, node22);
        this.addEdge(node22, node13);
        this.addEdge(node3, node23);
        this.addEdge(node23, node9);
        this.addEdge(node6, node7);
        this.addEdge(node7, node8);
        this.addEdge(node8, node9);
        this.addEdge(node17, node4);
        this.addEdge(node12, node15);
        this.addEdge(node15, node21);
        this.addEdge(node2, node24);
        this.addEdge(node24, node6);
        this.addEdge(node24, node4);
        this.addEdge(node1, node25);
        this.addEdge(node25, node26);
        this.addEdge(node2, node27);
        this.addEdge(node27, node28);
        this.addEdge(node28, node29);
        this.addEdge(node29, node30);
        this.addEdge(node16, node31);
        this.addEdge(node31, node17);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/125
    }
}
EventGraphStructWallCornerPoint.cmd = "STRUCT_WALL_POINT";/**
 * 计算面域布置墙的临时图元
 */
class EventHandlerCalStructWallRegion extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        const structComData = graphCache.data.structComData;
        if (structComData == null || Object.keys(structComData).length == 0) {
            return true;
        }
        const height = structComData.height;
        if (height === null || isNaN(height) || height <= 0)
            return true;
        const storage = this.getPythaStorage();
        const selectPrimitives = storage.selectStore.selectPrimitives;
        const regionPrimitives = [];
        selectPrimitives.forEach(primitive => {
            if (primitive.type === PrimitiveType.RegionFace) {
                regionPrimitives.push(primitive);
            }
        });
        graphCache.data['struct_com_info'] = [];
        regionPrimitives.forEach(regionPrimitive => {
            this.calcRegionStructWall(regionPrimitive, structComData, height);
        });
        return true;
    }
    // 计算墙临时图元
    calcRegionStructWall(regionPrimitive, structComData, height) {
        var _a;
        const graphCache = this.node.graph.cache;
        const storage = this.getPythaStorage();
        const region = regionPrimitive.geos[0];
        const ext = new ExtrudeSolid(region, height);
        const primitive = new Primitive(PrimitiveType.ExtrudeSoild);
        primitive.geos.push(ext);
        primitive.localModelMatrix = new Matrix4();
        if (regionPrimitive.localModelMatrix != null) {
            primitive.localModelMatrix.copy(regionPrimitive.localModelMatrix);
            primitive.updateWorldModelMatrix(false, true);
        }
        primitive.temporary = true;
        primitive.needRedraw = true;
        primitive.colorByLayer = true;
        const keys = Object.keys(primitive.renderItemDict);
        if (keys.length > 0) {
            let renderItem = primitive.renderItemDict[keys[0]];
            renderItem.geoElements = ext.getGeoElementsIdxList();
            (_a = renderItem.mat) === null || _a === void 0 ? void 0 : _a.color.set(storage.layerStore.getColor());
        }
        graphCache.primitiveList.push(primitive);
        storage.primitiveStore.tempPrimitiveDict[primitive.uuid] = primitive;
        const ez = structComData.ez;
        // z轴退让
        if (!!ez && ez.length === 2) {
            const ezOffset = ez[0];
            if (!primitive.localModelMatrix)
                primitive.localModelMatrix = new Matrix4();
            // 构建起点的偏移向量
            _v.setFromMatrixColumn(primitive.localModelMatrix, 2);
            _p$2.copy(_p1$1);
            _p$2.addScaledVector(_v, ezOffset);
            primitive.move(_p$2);
        }
        // 生成构件信息
        const structComInfos = graphCache.data['struct_com_info'];
        const uuid = BaseUtils.genUUID();
        const structComInfo = new StructComponentInfo(uuid, primitive.uuid);
        structComInfo.properties = {
            sectionId: structComData.sectionId,
            materialId: structComData.materialId,
            thickness: structComData.thickness,
            height: structComData.height,
            x: structComData.x,
            y: structComData.y,
            z: structComData.z,
            angle: structComData.angle,
            orientationType: structComData.orientationType,
            type: structComData.type,
            comTypeUuid: structComData.comTypeUuid,
        };
        structComInfos.push(structComInfo);
    }
}
const _v = new Vector3();
const _p$2 = new Vector3();
const _p1$1 = new Vector3();/**
 * 面域布置墙
 */
class EventGraphStructWallRegion extends EventGraphBase {
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node2 = new EventGraphNode(this, EventHandlerDisableSelect);
        const node3 = new EventGraphNode(this, EventHandlerListenStructComData);
        const node4 = new EventGraphNode(this, EventHandlerSelectStart);
        const node6 = new EventGraphNodeNotice(this, { cmd: 'Notice', data: '请选择面域' });
        const node7 = new EventGraphNode(this, EventHandlerCalPointerUp);
        const node8 = new EventGraphNodeSwitch(this, () => {
            const storage = this.eventMediator.storage;
            const selectPrimitives = storage.selectStore.selectPrimitives;
            if (selectPrimitives.length === 0) {
                return [0];
            }
            let existRegion = false;
            for (const primitive of selectPrimitives) {
                if (primitive.type === PrimitiveType.RegionFace) {
                    existRegion = true;
                    break;
                }
            }
            if (existRegion)
                return [1];
            return [0];
        });
        const node9 = new EventGraphNode(this, EventHandlerEmptySelect);
        const node11 = new EventGraphNode(this, EventHandlerCalStructWallRegion);
        const node12 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node13 = new EventGraphNode(this, EventHandlerGrid);
        const node14 = new EventGraphNode(this, EventHandlerGenPriAndStructInfo);
        const node15 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node20 = new EventGraphNode(this, EventHandlerEmptySelect);
        const node22 = new EventGraphNode(this, EventHandlerConfirm);
        const node23 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node24 = new EventGraphNode(this, EventHandlerClearTempStructCom);
        const node25 = new EventGraphNode(this, EHChangePickCursor);
        const node26 = new EventGraphNode(this, EventHandlerListenStructDataContinue);
        this.addEdge(node1, node2);
        this.addEdge(node2, node3);
        this.addEdge(node6, node7);
        this.addEdge(node7, node8);
        /**没有选中面域*/
        this.addEdge(node8, node9);
        this.addEdge(node12, node6);
        this.addEdge(node13, node15);
        this.addEdge(node20, node13);
        this.addEdge(node11, node14);
        this.addEdge(node14, node20);
        this.addEdge(node4, node6);
        this.addEdge(node9, node12);
        this.addEdge(node1, node22);
        this.addEdge(node22, node23);
        this.addEdge(node15, node24);
        this.addEdge(node24, node6);
        this.addEdge(node3, node25);
        this.addEdge(node25, node4);
        this.addEdge(node3, node26);
        /**选中了面域*/
        this.addEdge(node8, node11);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/133
    }
}
EventGraphStructWallRegion.cmd = "STRUCT_WALL_REGION";/**
 * 计算轴线布置墙构件临时图元
 */
class EventHandlerCalStructWallAxisLine extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        const structComData = graphCache.data.structComData;
        if (structComData == null || Object.keys(structComData).length == 0) {
            return true;
        }
        const storage = this.getPythaStorage();
        const selectPrimitives = storage.selectStore.selectPrimitives;
        const linePrimitives = [];
        selectPrimitives.forEach(primitive => {
            if (primitive.type === 'AxisLineSegment' || primitive.type === "VerticalAxisLine") {
                linePrimitives.push(primitive);
            }
        });
        const primitive = graphCache.data.primitive;
        const geo = graphCache.data.geo;
        if (!geo || !primitive)
            return true;
        // 把临时图元补充到足够个数
        if (graphCache.primitiveList.length < linePrimitives.length) {
            const count = linePrimitives.length - graphCache.primitiveList.length;
            for (let i = 0; i < count; i++) {
                const newPri = primitive.clone();
                newPri.temporary = true;
                newPri.needRedraw = true;
                const keys = Object.keys(newPri.renderItemDict);
                if (keys.length > 0) {
                    let renderItem = newPri.renderItemDict[keys[0]];
                    renderItem.geoElements = geo.getGeoElementsIdxList();
                }
                graphCache.primitiveList.push(newPri);
                storage.primitiveStore.tempPrimitiveDict[newPri.uuid] = newPri;
            }
        }
        graphCache.data['struct_com_info'] = [];
        linePrimitives.forEach((linePrimitive, index) => {
            const extPri = graphCache.primitiveList[index];
            this.calcLineStructWall(linePrimitive, extPri, structComData);
        });
        return true;
    }
    calcLineStructWall(linePrimitive, primitive, structComData) {
        const graphCache = this.node.graph.cache;
        const storage = this.getPythaStorage();
        storage.primitiveStore.primitiveDict;
        const workPlane = storage.coordinateStore.workPlane;
        if (linePrimitive.type === 'AxisLineSegment' || linePrimitive.type === "VerticalAxisLine") {
            // const axisNetUuid = (linePrimitive as any).axisNetUuid;
            // const axisNet = primitiveDict[axisNetUuid];
            // 异常数据
            // if (!axisNet) return;
            const line = linePrimitive.geos[0];
            startP.copy(line.start);
            // startP.applyMatrix4(axisNet.localModelMatrix);
            moveP.copy(line.end);
            // moveP.applyMatrix4(axisNet.localModelMatrix);
        }
        else {
            const line = linePrimitive.geos[0];
            startP.copy(line.start);
            moveP.copy(line.end);
        }
        const geo = primitive.geos[0];
        // 每次重置到初始状态
        primitive.localModelMatrix = new Matrix4();
        primitive.needRedraw = true;
        geo.height = 1;
        // 轴转角、k轴
        const orientationType = structComData.orientationType;
        const x = structComData.x;
        const y = structComData.y;
        const z = structComData.z;
        const angle = structComData.angle;
        // 根据轴转角计算
        if (OrientationType.K === orientationType && x != null && y != null && z != null) {
            // point指向k，point指向move是x轴，x轴叉乘k得到-y轴
            _p$1.set(x, y, z);
            _p1.subVectors(_p$1, startP).normalize(); // point指向k
            _v1$3.subVectors(moveP, startP).normalize(); // x轴
            _v2$2.crossVectors(_v1$3, _p1).multiplyScalar(-1).normalize(); // y轴
            _v3$1.crossVectors(_v1$3, _v2$2).normalize(); // z轴
            // 构建变换矩阵
            matrix.set(_v1$3.x, _v2$2.x, _v3$1.x, 0, _v1$3.y, _v2$2.y, _v3$1.y, 0, _v1$3.z, _v2$2.z, _v3$1.z, 0, 0, 0, 0, 1);
            primitive.localModelMatrix.copy(matrix);
            primitive.updateWorldModelMatrix(false, true);
        }
        else if (orientationType == null || OrientationType.DIRECTION === orientationType) {
            // point指向move是x轴，x轴叉乘k得到-y轴
            _p$1.set(0, 0, 1);
            _v1$3.subVectors(moveP, startP).normalize(); // x轴
            _v2$2.crossVectors(_v1$3, _p$1).multiplyScalar(-1).normalize(); // y轴
            _v3$1.crossVectors(_v1$3, _v2$2).normalize(); // z轴
            // 构建变换矩阵
            matrix.set(_v1$3.x, _v2$2.x, _v3$1.x, 0, _v1$3.y, _v2$2.y, _v3$1.y, 0, _v1$3.z, _v2$2.z, _v3$1.z, 0, 0, 0, 0, 1);
            primitive.localModelMatrix.copy(matrix);
            primitive.updateWorldModelMatrix(false, true);
        }
        else {
            // 计算旋转角度
            workPlane.getNormalizeX(_p$1);
            _vec.subVectors(moveP, startP).normalize();
            // 计算旋转四元数
            _quaternion.setFromUnitVectors(_p$1, _vec);
            const rotationMatrix = new Matrix4();
            rotationMatrix.makeRotationFromQuaternion(_quaternion);
            primitive.localModelMatrix.premultiply(rotationMatrix);
            primitive.updateWorldModelMatrix(false, true);
            // if (OrientationType.ANGLE === orientationType && angle != null && Math.abs(angle) > 1e-4) {
            if (angle != null && Math.abs(angle) > 1e-4) {
                primitive.rotate(workPlane.getP(), workPlane.getNormalizeZ(), Math.PI * angle / 180);
            }
        }
        graphCache.data.startP = startP;
        graphCache.data.moveP = moveP;
        const height = this.calcOffset(structComData, primitive);
        primitive.move(startP);
        geo.height = height;
        const structComInfos = graphCache.data['struct_com_info'];
        const uuid = BaseUtils.genUUID();
        const structComInfo = new StructComponentInfo(uuid, primitive.uuid);
        // 更新存储构件信息
        structComInfo.primitiveUuid = primitive.uuid;
        structComInfo.properties = {
            sectionId: structComData.sectionId,
            materialId: structComData.materialId,
            thickness: structComData.thickness,
            height: structComData.height,
            x: structComData.x,
            y: structComData.y,
            z: structComData.z,
            angle: structComData.angle,
            orientationType: structComData.orientationType,
            type: structComData.type,
            comTypeUuid: structComData.comTypeUuid,
        };
        structComInfos.push(structComInfo);
        primitive.needRedraw = true;
    }
    // 计算y和z轴退让
    calcOffset(structComData, primitive) {
        const halfThickness = structComData.thickness / 2;
        const halfHeight = structComData.height / 2;
        if (halfThickness == null || isNaN(halfThickness) || halfThickness <= 0)
            return 0;
        if (halfHeight == null || isNaN(halfHeight) || halfHeight <= 0)
            return 0;
        const distance = moveP.distanceTo(startP);
        let height = distance; // 新的拉伸高度
        // 计算偏心参照
        const offsetIndex = structComData.offsetIndex;
        if (offsetIndex != null && !isNaN(offsetIndex) && offsetIndex >= 0 && offsetIndex <= 8) {
            let width = 0;
            let height = 0;
            if (offsetIndex == 0) {
                width = -halfThickness;
                height = -halfHeight;
            }
            else if (offsetIndex === 1) {
                height = -halfHeight;
            }
            else if (offsetIndex === 2) {
                width = halfThickness;
                height = -halfHeight;
            }
            else if (offsetIndex === 3) {
                width = -halfThickness;
            }
            else if (offsetIndex === 5) {
                width = halfThickness;
            }
            else if (offsetIndex === 6) {
                width = -halfThickness;
                height = halfHeight;
            }
            else if (offsetIndex === 7) {
                height = halfHeight;
            }
            else if (offsetIndex === 8) {
                width = halfThickness;
                height = halfHeight;
            }
            _v1$3.setFromMatrixColumn(primitive.localModelMatrix, 1);
            _v2$2.setFromMatrixColumn(primitive.localModelMatrix, 2);
            startP.addScaledVector(_v1$3, width);
            startP.addScaledVector(_v2$2, height);
        }
        const ez = structComData.ez;
        // z轴退让
        if (!!ez && ez.length === 2) {
            const ezOffset = ez[0];
            // 构建起点的偏移向量
            if (!primitive.localModelMatrix)
                primitive.localModelMatrix = new Matrix4();
            _v3$1.setFromMatrixColumn(primitive.localModelMatrix, 1);
            startP.addScaledVector(_v3$1, -ezOffset);
        }
        return height;
    }
}
const startP = new Vector3();
const moveP = new Vector3();
const _vec = new Vector3();
const _p$1 = new Vector3();
const _p1 = new Vector3();
const _v1$3 = new Vector3();
const _v2$2 = new Vector3();
const _v3$1 = new Vector3();
const matrix = new Matrix4();
const _quaternion = new Quaternion();/**
 * 轴线段布置墙构件
 */
class EventGraphStructWallAxisLine extends EventGraphBase {
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node2 = new EventGraphNode(this, EventHandlerListenStructComData);
        const node3 = new EventGraphNode(this, EventHandlerDisableSelect);
        const node4 = new EventGraphNode(this, EventHandlerSelectStart);
        const node5 = new EventGraphNodeNotice(this, { cmd: 'Notice', data: '选择轴线段' });
        const node6 = new EventGraphNode(this, EventHandlerCalPointerUp);
        const node7 = new EventGraphNodeSwitch(this, () => {
            const storage = this.eventMediator.storage;
            const selectPrimitives = storage.selectStore.selectPrimitives;
            if (selectPrimitives.length === 0) {
                return [0];
            }
            let existLine = false;
            for (const primitive of selectPrimitives) {
                if (primitive.type === PrimitiveType.Line || primitive.type === 'AxisLineSegment' || primitive.type === "VerticalAxisLine") {
                    existLine = true;
                    break;
                }
            }
            if (existLine)
                return [1];
            return [0];
        });
        const node8 = new EventGraphNode(this, EventHandlerGrid);
        const node9 = new EventGraphNode(this, EventHandlerCalStructWallAxisLine);
        const node10 = new EventGraphNode(this, EventHandlerGenPriAndStructInfo);
        const node11 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node12 = new EventGraphNode(this, EventHandlerEmptySelect);
        const node14 = new EventGraphNode(this, EventHandlerEmptySelect);
        const node15 = new EventGraphNode(this, EventHandlerGrid);
        const node16 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node20 = new EventGraphNode(this, EventHandlerClearTempStructCom);
        const node21 = new EventGraphNode(this, EventHandlerDisableClamp);
        const node23 = new EventGraphNode(this, EventHandlerGenStructWallExt);
        const node24 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node25 = new EventGraphNode(this, EventHandlerConfirm);
        const node26 = new EventGraphNode(this, EHChangePickCursor);
        const node27 = new EventGraphNode(this, EventHandlerListenStructDataContinue);
        const node28 = new EventGraphNode(this, EventHandlerCalPrimitiveInView);
        this.addEdge(node1, node3);
        this.addEdge(node5, node6);
        this.addEdge(node6, node7);
        this.addEdge(node8, node11);
        /**没有选中轴线段*/
        this.addEdge(node7, node14);
        this.addEdge(node14, node15);
        this.addEdge(node15, node16);
        this.addEdge(node16, node5);
        this.addEdge(node12, node8);
        this.addEdge(node3, node21);
        this.addEdge(node21, node2);
        this.addEdge(node23, node9);
        this.addEdge(node10, node20);
        this.addEdge(node9, node10);
        this.addEdge(node1, node25);
        this.addEdge(node25, node24);
        this.addEdge(node11, node5);
        this.addEdge(node2, node4);
        this.addEdge(node4, node26);
        this.addEdge(node26, node5);
        this.addEdge(node2, node27);
        this.addEdge(node20, node28);
        this.addEdge(node28, node12);
        /**选中了轴线段*/
        this.addEdge(node7, node23);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/135
    }
}
EventGraphStructWallAxisLine.cmd = "STRUCT_WALL_AXIS_LINE";/**
 * 删除图元的构件信息
 */
class DeleteStructInfoCmd extends CommandBase {
    constructor(primitiveUuids, eventMediator) {
        super();
        this.primitiveUuids = primitiveUuids;
        this.eventMediator = eventMediator;
    }
    execute() {
        const axios = PythaPluginStructure.webData.axios;
        axios.post(`/plugin/api-structure/projects/struct-datas/multi-delete`, this.primitiveUuids);
    }
    undo() {
        const axios = PythaPluginStructure.webData.axios;
        axios.post(`/plugin/api-structure/projects/struct-datas/multi-undo`, this.primitiveUuids);
    }
}/**
 * 生成删除构件命令
 */
class EventHandlerGenDeleteStructInfo extends EventHandlerBase {
    run(data) {
        const graph = this.node.graph;
        const parentGraph = graph.parentGraph;
        const graphCache = parentGraph.cache;
        let commandGroup = graphCache.data['commandGroup'];
        if (commandGroup == null) {
            commandGroup = new CommandGroup();
            graphCache.data['commandGroup'] = commandGroup;
        }
        const storage = this.getPythaStorage();
        const selectPrimitives = storage.selectStore.selectPrimitives;
        const primitiveUuids = [];
        selectPrimitives.forEach(primitive => {
            primitiveUuids.push(primitive.uuid);
        });
        if (primitiveUuids.length > 0) {
            const cmd = new DeleteStructInfoCmd(primitiveUuids, this.node.graph.eventMediator);
            commandGroup.addCmd(cmd);
        }
        return true;
    }
}/**
 * 删除构件信息
 */
class EventGraphDeleteStructInfo extends EventGraphPluginBase {
    constructor(mediator) {
        super(mediator);
        this._graftKey = EventGraphDelete.cmd;
        this._graftOrder = GraftOrderEnum.BEFORE;
    }
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        // const node2 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node3 = new EventGraphNode(this, EventHandlerGenDeleteStructInfo);
        const node4 = new EventGraphNode(this, EventHandlerGenCommandGroup);
        this.addEdge(node1, node4);
        this.addEdge(node4, node3);
        // this.addEdge(node3, node2);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/137
    }
}
EventGraphDeleteStructInfo.cmd = "DELETE_STRUCT_INFO";/**
 * 生成面域图元
 */
class EventHandlerCalSectionRegion extends EventHandlerBase {
    run(data) {
        var _a;
        const graphCache = this.node.graph.cache;
        const section = graphCache.data.section;
        const storage = this.getPythaStorage();
        if (!section)
            return true;
        // 把截面旋转到yoz
        this.rotateRegion(section);
        const primitive = new Primitive(PrimitiveType.RegionFace);
        primitive.geos.push(section);
        primitive.temporary = false;
        primitive.needRedraw = true;
        const keys = Object.keys(primitive.renderItemDict);
        if (keys.length > 0) {
            let renderItem = primitive.renderItemDict[keys[0]];
            renderItem.geoElements = section.getGeoElementsIdxList();
            (_a = renderItem.mat) === null || _a === void 0 ? void 0 : _a.color.set(storage.layerStore.getColor());
        }
        storage.primitiveStore.primitiveDict[primitive.uuid] = primitive;
        return true;
    }
    // 把截面旋转到yoz
    rotateRegion(region) {
        // 把面域转到yoz平面
        const transformMatrix = region.transformMatrix;
        const axisZ = new Vector3(0, 0, 1);
        const axisY = new Vector3(0, 1, 0);
        // 创建一个旋转矩阵，绕 Z 轴旋转 90 度
        const rotationZ = new Matrix4().makeRotationAxis(axisZ, Math.PI / 2);
        // 创建一个旋转矩阵，绕 Y 轴旋转 90 度
        const rotationY = new Matrix4().makeRotationAxis(axisY, Math.PI / 2);
        const m1 = new Matrix4().premultiply(transformMatrix).premultiply(rotationZ).premultiply(rotationY);
        transformMatrix.copy(m1);
    }
}/**
 * 生成截面面域
 */
class EventGraphGenSectionRegion extends EventGraphBase {
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node2 = new EventGraphNode(this, EventHandlerGenSectionRegion);
        const node3 = new EventGraphNodeRender(this, true, UpdateType.TEMP);
        const node4 = new EventGraphNode(this, EventHandlerCalSectionRegion);
        const node5 = new EventGraphNode(this, EventHandlerListenStructComData);
        const node6 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node7 = new EventGraphNode(this, EventHandlerGrid);
        const node8 = new EventGraphNode(this, EventHanderCalAllBoundingBox);
        const node9 = new EventGraphNode(this, EventHandlerCalPrimitiveInView);
        const node10 = new EventGraphNode(this, EventHanderShowAllInRect);
        this.addEdge(node2, node4);
        this.addEdge(node1, node5);
        this.addEdge(node5, node2);
        this.addEdge(node3, node6);
        this.addEdge(node7, node3);
        this.addEdge(node4, node8);
        this.addEdge(node8, node10);
        this.addEdge(node10, node9);
        this.addEdge(node9, node7);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/146
    }
    end() {
        const storage = this.eventMediator.storage;
        const viewItem = storage.viewportStore.viewportItems[storage.viewportStore.currentViewportKey];
        const camera = viewItem.camera;
        storage.coordinateStore.workPlane.copy(_plane);
        camera.position.copy(_v1$2);
        camera.matrix.set(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
        camera.up.copy(_v3);
        camera.lookAt(_v4);
        camera.updateMatrixWorld(true);
        viewItem.target.copy(_v4);
        super.end();
    }
}
EventGraphGenSectionRegion.cmd = "GEN_SECTION_REGION";
const _v1$2 = new Vector3(100000, 0, 0);
const _v2$1 = new Vector3(1, 0, 0);
const _v3 = new Vector3(0, 0, 1);
const _v4 = new Vector3(0, 0, 0);
const _plane = new Plane(_v2$1);/**
 * 文字标签
 */
class TextLabel extends LabelBase {
    constructor(text, className) {
        super(false);
        this.generateTextLabel(text, className);
    }
    generateTextLabel(text, className) {
        const root = this.obj.element;
        this.obj.renderOrder = 200;
        const textLabel = document.createElement('div');
        // 写入class
        textLabel.className = `struct-textLabel-${className}`;
        // 往div中写入文本
        textLabel.innerText = text;
        textLabel.style.backgroundColor = "transparent";
        textLabel.style.width = "16px";
        textLabel.style.height = "16px";
        textLabel.style.fontSize = "40px";
        textLabel.style.color = "#000000";
        root.appendChild(textLabel);
    }
}/**
 * 计算构件详情图元
 * 把面域旋转到yoz，拉伸
 */
class EventHandlerCalStructPrimitive extends EventHandlerBase {
    run(data) {
        const graphCache = this.node.graph.cache;
        const storage = this.getPythaStorage();
        const section = graphCache.data.section;
        if (!section)
            return true;
        // 把图元旋转到yoz
        this.rotateRegion(section);
        // 计算面域的包围盒
        const box = section.getBoundingBox();
        const min = Math.min(box.min.x, box.min.y, box.min.z);
        const max = Math.max(box.max.x, box.max.y, box.max.z);
        const distance = Math.abs(max - min);
        // 创建拉伸体
        const geo = new ExtrudeSolid(section, distance * 6);
        const primitive = new Primitive(PrimitiveType.ExtrudeSoild);
        primitive.geos.push(geo);
        const keys = Object.keys(primitive.renderItemDict);
        if (keys.length > 0) {
            let renderItem = primitive.renderItemDict[keys[0]];
            renderItem.geoElements = geo.getGeoElementsIdxList();
        }
        primitive.temporary = false;
        primitive.needRedraw = true;
        storage.primitiveStore.primitiveDict[primitive.uuid] = primitive;
        const _v3 = new Vector3().addScaledVector(_v2, distance * 6);
        this.generateLine(_v1$1, _v3);
        this.generateText(_v1$1, _v3);
        return true;
    }
    rotateRegion(region) {
        // 把面域转到yoz平面
        const transformMatrix = region.transformMatrix;
        const axisZ = new Vector3(0, 0, 1);
        const axisY = new Vector3(0, 1, 0);
        // 创建一个旋转矩阵，绕 Z 轴旋转 90 度
        const rotationZ = new Matrix4().makeRotationAxis(axisZ, Math.PI / 2);
        // 创建一个旋转矩阵，绕 Y 轴旋转 90 度
        const rotationY = new Matrix4().makeRotationAxis(axisY, Math.PI / 2);
        const m1 = new Matrix4().premultiply(transformMatrix).premultiply(rotationZ).premultiply(rotationY);
        transformMatrix.copy(m1);
    }
    // 生成ij连线
    generateLine(start, end) {
        var _a;
        const storage = this.getPythaStorage();
        const line = new Line(start, end);
        const primitive = new Primitive(PrimitiveType.Line);
        primitive.geos.push(line);
        const keys = Object.keys(primitive.renderItemDict);
        if (keys.length > 0) {
            let renderItem = primitive.renderItemDict[keys[0]];
            renderItem.geoElements = line.getGeoElementsIdxList();
            (_a = renderItem.mat) === null || _a === void 0 ? void 0 : _a.color.set("#FF0000");
        }
        storage.primitiveStore.primitiveDict[primitive.uuid] = primitive;
        primitive.needRedraw = true;
        primitive.temporary = false;
        const iPoint = new Primitive("Point");
        iPoint.move(start);
        const ipkeys = Object.keys(iPoint.renderItemDict);
        if (ipkeys.length > 0) {
            let renderItem = iPoint.renderItemDict[ipkeys[0]];
            renderItem.geoElements = [];
            renderItem.mat.color.set("#FF0000");
            renderItem.renderType = RenderItemType.POINT;
        }
        iPoint.needRedraw = true;
        iPoint.temporary = false;
        storage.primitiveStore.primitiveDict[iPoint.uuid] = iPoint;
        const jPoint = new Primitive("Point");
        jPoint.move(end);
        const jpkeys = Object.keys(jPoint.renderItemDict);
        if (jpkeys.length > 0) {
            let renderItem = jPoint.renderItemDict[jpkeys[0]];
            renderItem.geoElements = [];
            renderItem.mat.color.set("#FF0000");
            renderItem.renderType = RenderItemType.POINT;
        }
        jPoint.needRedraw = true;
        jPoint.temporary = false;
        storage.primitiveStore.primitiveDict[jPoint.uuid] = jPoint;
    }
    // 生成ij文字
    generateText(start, end) {
        const storage = this.getPythaStorage();
        const i = new TextLabel('i', "primitive");
        const j = new TextLabel('j', "primitive");
        i.setPosition(start);
        j.setPosition(end);
        storage.viewportStore.scene.add(i.obj); // 显示标签
        storage.viewportStore.scene.add(j.obj); // 显示标签
    }
}
const _v1$1 = new Vector3(0, 0, 0);
const _v2 = new Vector3(1, 0, 0);/**
 * 生成构件详情拉伸体
 */
class EventGraphGenStructPrimitive extends EventGraphBase {
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node2 = new EventGraphNode(this, EventHandlerListenStructComData);
        const node3 = new EventGraphNode(this, EventHandlerGenSectionRegion);
        const node4 = new EventGraphNode(this, EventHandlerCalStructPrimitive);
        const node5 = new EventGraphNode(this, EventHandlerGrid);
        const node6 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node7 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node8 = new EventGraphNode(this, EventHanderCalAllBoundingBox);
        const node9 = new EventGraphNode(this, EventHanderShowAllInRect);
        const node10 = new EventGraphNode(this, EventHandlerCalPrimitiveInView);
        this.addEdge(node1, node2);
        this.addEdge(node2, node3);
        this.addEdge(node3, node4);
        this.addEdge(node5, node6);
        this.addEdge(node6, node7);
        this.addEdge(node4, node8);
        this.addEdge(node8, node9);
        this.addEdge(node9, node10);
        this.addEdge(node10, node5);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/148
    }
    end() {
        const graphCache = this.cache;
        const structComData = graphCache.data.structComData;
        if (!!structComData.needRotate) {
            const storage = this.eventMediator.storage;
            const viewItem = storage.viewportStore.viewportItems[storage.viewportStore.currentViewportKey];
            const camera = viewItem.camera;
            // 获取相机当前位置
            const position = camera.position.clone();
            const axis = new Vector3(0, 0, -1);
            const angle = Math.PI / 2;
            const rotationMatrix = new Matrix4().makeRotationAxis(axis, angle);
            position.applyMatrix4(rotationMatrix);
            camera.position.copy(position);
            camera.lookAt(0, 0, 0);
            camera.updateMatrixWorld(true);
            viewItem.target.set(0, 0, 0);
        }
        super.end();
    }
}
EventGraphGenStructPrimitive.cmd = "GEN_STRUCT_PRIMITIVE";const StructComType = {
    BEAM: 1, //梁
    BAR: 2, //杆
    COLUMN: 3, //柱
    CABLE: 4, //索
    BRACE: 7, //支撑
};
/**
 * 计算一点加高度布置线性构件的另外一点坐标
 */
class EventHandlerStructComPoint extends EventHandlerBase {
    run(data) {
        const storage = this.getPythaStorage();
        const graphCache = this.node.graph.cache;
        const point = graphCache.pointerDownList[graphCache.pointerDownList.length - 1].point;
        graphCache.data.startPoint = point;
        const workPlane = storage.coordinateStore.workPlane;
        const height = graphCache.inputNumber['H'];
        const structComData = graphCache.data.structComData;
        const type = structComData.type;
        if (type == null)
            return false;
        // 这里确定拉伸方向
        if (type === StructComType.COLUMN || type == StructComType.BRACE) {
            workPlane.getNormalizeZ(_v1);
            // 交换ez和ex
            const temp = structComData.ez;
            structComData.ez = structComData.ex;
            structComData.ex = temp;
        }
        else {
            workPlane.getNormalizeX(_v1);
        }
        // 负高度就反向
        _p.copy(point);
        _p.addScaledVector(_v1, height);
        graphCache.data.endPoint = _p;
        return true;
    }
}
const _v1 = new Vector3();
const _p = new Point();/**
 * 一点加高度布置
 */
class EventGraphStructComHeight extends EventGraphBase {
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node2 = new EventGraphNode(this, EventHandlerListenStructComData);
        const node4 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [
                { type: NeedInputEnum.ACTIVE, description: "指定一个高度", option: 'H', inputTypes: [PythaEventName.NUMBER] },
            ]
        });
        const node5 = new EventGraphNode(this, EventHandlerDisableSelect);
        const node6 = new EventGraphNode(this, EventHandlerInitCapture);
        const node8 = new EventGraphNode(this, EventHandlerCapture);
        const node9 = new EventGraphNodeAND(this, null);
        const node11 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [
                { type: NeedInputEnum.ACTIVE, description: "指定一个点", option: '', inputTypes: [PythaEventName.POINTER_DOWN] },
            ]
        });
        const node12 = new EventGraphNode(this, EventHandlerCalPointerDown);
        const node15 = new EventGraphNode(this, EventHandlerGenPriAndStructInfo);
        const node16 = new EventGraphNode(this, EventHandlerGrid);
        const node17 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node21 = new EventGraphNode(this, EventHandlerClearTempStructCom);
        const node24 = new EventGraphNode(this, EHChangeCrossCursor);
        const node25 = new EventGraphNode(this, EventHandlerConfirm);
        const node26 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node27 = new EventGraphNode(this, EventHandlerListenStructDataContinue);
        const node28 = new EventGraphNode(this, EventHandlerGenSectionRegion);
        const node29 = new EventGraphNode(this, EventHandlerCalStructRegion);
        const node32 = new EventGraphNode(this, EventHandlerCalPointerMove);
        const node33 = new EventGraphNode(this, EventHandlerNumberInput);
        const node34 = new EventGraphNodeSwitch(this, () => {
            const inputValue = this.cache.inputNumber['H'];
            if (inputValue == null || isNaN(+inputValue)) {
                return [0];
            }
            return [1, 2];
        });
        const node35 = new EventGraphNode(this, EventHandlerStructComPoint);
        const node36 = new EventGraphNode(this, EventHandlerCalStructComTwoPoint);
        const node37 = new EventGraphNode(this, EventHandlerCalPrimitiveInView);
        this.addEdge(node1, node5);
        this.addEdge(node5, node2);
        this.addEdge(node21, node16);
        this.addEdge(node11, node12);
        this.addEdge(node15, node21);
        this.addEdge(node24, node6);
        this.addEdge(node1, node25);
        this.addEdge(node25, node26);
        this.addEdge(node2, node27);
        this.addEdge(node28, node29);
        this.addEdge(node6, node32);
        this.addEdge(node32, node8);
        this.addEdge(node4, node33);
        this.addEdge(node33, node34);
        /**输入的高度为空或者非正常数值*/
        this.addEdge(node34, node4);
        this.addEdge(node2, node4);
        this.addEdge(node9, node24);
        this.addEdge(node35, node36);
        this.addEdge(node36, node15);
        this.addEdge(node12, node28);
        this.addEdge(node29, node35);
        this.addEdge(node17, node11);
        this.addEdge(node16, node37);
        this.addEdge(node37, node17);
        /**输入高度正常*/
        this.addEdge(node34, node9);
        /**输入的高度正常*/
        this.addEdge(node34, node11);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/158
    }
}
EventGraphStructComHeight.cmd = "STRUCT_COM_HEIGHT";function updateComDataMaterial(coreAxios, projectUuid, param, materialId, params) {
    return coreAxios.post(`/plugin/api-structure/projects/${projectUuid}/structure-datas/materials`, param, {
        params: Object.assign(Object.assign({}, params), { materialId: materialId }),
    });
}
function updateComDataSection(coreAxios, projectUuid, param, sectionId, params) {
    return coreAxios.post(`/plugin/api-structure/projects/${projectUuid}/structure-datas/sections`, param, {
        params: Object.assign(Object.assign({}, params), { sectionId: sectionId }),
    });
}
function updateComDataType(coreAxios, projectUuid, param, type, params) {
    return coreAxios.post(`/plugin/api-structure/projects/${projectUuid}/structure-datas/types`, param, {
        params: Object.assign(Object.assign({}, params), { type: type }),
    });
}/**
 * 修改构件材料信息
 */
class EventHandlerUpdateComponentMaterial extends EventHandlerBase {
    run(data) {
        var _a;
        // console.log('修改构件材料信息 run');
        // 获取选中图元
        const storge = this.getPythaStorage();
        let selectPrimitives = storge.selectStore.selectPrimitives;
        if ((selectPrimitives === null || selectPrimitives === void 0 ? void 0 : selectPrimitives.length) === 0) {
            return true;
        }
        const param = this.node.graph.cache.inputObject['M'];
        const projectUuid = param.projectUuid;
        const materialId = param.materialId;
        let uuids = ((_a = selectPrimitives === null || selectPrimitives === void 0 ? void 0 : selectPrimitives.filter(item => item.type === PrimitiveType.ExtrudeSoild)) === null || _a === void 0 ? void 0 : _a.map(item => item.uuid)) || [];
        if ((uuids === null || uuids === void 0 ? void 0 : uuids.length) === 0)
            return true;
        updateComDataMaterial(PythaPluginStructure.webData.axios, projectUuid, uuids, materialId).then(res => {
            let event = new PythaEventMaterialChange();
            event.data = res.data;
            this.node.graph.eventMediator.emitEvent(event);
        }).catch(err => {
            console.log('修改构件材料信息失败', err);
            let event = new PythaEventMaterialChange();
            event.data = 0;
            this.node.graph.eventMediator.emitEvent(event);
        });
        return true;
    }
}/**
 * 监听修改构件材料完成的信号
 */
class EventHandlerUpdateMaterialFinish extends EventHandlerBase {
    constructor(node) {
        super(node);
        this.needEvent = new PythaEventMaterialChange();
        this.needEventOnce = true;
    }
    run(event) {
        if (event.cmd != this.needEvent.cmd)
            return false; // 如果传入事件不符 则不执行处理器
        // 发送通知
        let outputData = {
            type: OutputDataType.STRING,
            data: `成功修改 ${event.data} 个构件`
        };
        this.node.graph.eventMediator.noticeHandler.notice(this.node, new PythaEventOutputData(outputData));
        return true;
    }
}/**
 * 修改构件材料信息
 */
class EventGraphUpdateComponentMaterial extends EventGraphBase {
    constructor() {
        super(...arguments);
        /**
         * 不添加到右键
         */
        this.putInLastCmd = false;
    }
    /**
     * 创建图
     */
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node5 = new EventGraphNodeNotice(this, { cmd: 'Notice', data: '请选择要修改的构件' });
        const node6 = new EventGraphNode(this, EventHandlerConfirm);
        const node7 = new EventGraphNodeSwitch(this, () => {
            if (this.checkSelectCom()) {
                return [1];
            }
            else {
                return [0];
            }
        });
        const node8 = new EventGraphNode(this, EventHandlerUpdateComponentMaterial);
        const node11 = new EventGraphNodeNotice(this, { cmd: 'Notice', data: '未选中构件，请重新选择' });
        const node14 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node16 = new EventGraphNodeSwitch(this, () => {
            if (this.checkSelectCom()) {
                return [1];
            }
            else {
                return [0];
            }
        });
        const node17 = new EventGraphNode(this, EventHandlerEmptySelect);
        const node18 = new EventGraphNode(this, EHChangePickCursor);
        const node19 = new EventGraphNode(this, EHChangePickCursor);
        const node20 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [{
                    type: NeedInputEnum.ACTIVE,
                    description: "",
                    option: 'M',
                    inputTypes: [PythaEventName.OBJECT]
                }]
        });
        const node21 = new EventGraphNode(this, EventHandlerObjectInput);
        const node22 = new EventGraphNode(this, EventHandlerUpdateMaterialFinish);
        this.addEdge(node6, node7);
        /**没有选中构件*/
        this.addEdge(node7, node11);
        /**没有选中构件*/
        this.addEdge(node16, node5);
        this.addEdge(node17, node6);
        this.addEdge(node5, node18);
        this.addEdge(node18, node17);
        this.addEdge(node11, node19);
        this.addEdge(node19, node17);
        this.addEdge(node1, node20);
        this.addEdge(node20, node21);
        this.addEdge(node21, node16);
        this.addEdge(node8, node22);
        this.addEdge(node22, node14);
        /**选中若干构件*/
        this.addEdge(node7, node8);
        /**选中若干构件*/
        this.addEdge(node16, node8);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/167
    }
    /**
     * 判断是否选择了构件
     * @returns
     */
    checkSelectCom() {
        let selectPrimitives = this.eventMediator.storage.selectStore.selectPrimitives || [];
        // 构件一定是拉伸体
        return selectPrimitives === null || selectPrimitives === void 0 ? void 0 : selectPrimitives.some(item => item.type === PrimitiveType.ExtrudeSoild);
    }
}
/**
 * UpdateComponentMaterial指令 用于注册事件图
 */
EventGraphUpdateComponentMaterial.cmd = 'UPDATE_COMPONENT_MATERIAL';/**
 * 修改构件截面信息
 */
class EventHandlerUpdateComponentSection extends EventHandlerBase {
    run(data) {
        var _a;
        // console.log('修改构件截面信息 run');
        // 获取选中图元
        const storge = this.getPythaStorage();
        let selectPrimitives = storge.selectStore.selectPrimitives;
        if ((selectPrimitives === null || selectPrimitives === void 0 ? void 0 : selectPrimitives.length) === 0) {
            return true;
        }
        const param = this.node.graph.cache.inputObject['M'];
        const projectUuid = param.projectUuid;
        const sectionId = param.sectionId;
        let uuids = ((_a = selectPrimitives === null || selectPrimitives === void 0 ? void 0 : selectPrimitives.filter(item => item.type === PrimitiveType.ExtrudeSoild)) === null || _a === void 0 ? void 0 : _a.map(item => item.uuid)) || [];
        if ((uuids === null || uuids === void 0 ? void 0 : uuids.length) === 0)
            return true;
        updateComDataSection(PythaPluginStructure.webData.axios, projectUuid, uuids, sectionId).then(res => {
            let event = new PythaEventSectionChange();
            event.data = res.data;
            this.node.graph.eventMediator.emitEvent(event);
        }).catch(err => {
            console.log('修改构件截面信息失败', err);
            let event = new PythaEventSectionChange();
            event.data = 0;
            this.node.graph.eventMediator.emitEvent(event);
        });
        return true;
    }
}/**
 * 监听修改构件截面完成的信号
 */
class EventHandlerUpdateSectionFinish extends EventHandlerBase {
    constructor(node) {
        super(node);
        this.needEvent = new PythaEventSectionChange();
        this.needEventOnce = true;
    }
    run(event) {
        if (event.cmd != this.needEvent.cmd)
            return false; // 如果传入事件不符 则不执行处理器
        // 获取后端传来的数据
        const sections = event.data;
        const pris = sections.primitiveUuids;
        this.node.graph.cache.data.primitiveUuids = pris;
        let storage = this.getPythaStorage();
        // 构建截面的数据
        const data = {};
        data['paramIn'] = Object.assign({}, sections.section.paramIn);
        if (data['thickness'] != null) {
            data['thickness'] = data['thickness'] * (storage.coordinateStore.lengthUnitRatio || 1);
        }
        if (data['height'] != null) {
            data['height'] = data['height'] * (storage.coordinateStore.lengthUnitRatio || 1);
        }
        if (data['ez'] != null) {
            const unitRatio = storage.coordinateStore.lengthUnitRatio || 1;
            data['ez'][0] = data['ez'][0] * unitRatio;
            data['ez'][1] = data['ez'][1] * unitRatio;
        }
        if (data['ex'] != null) {
            const unitRatio = storage.coordinateStore.lengthUnitRatio || 1;
            data['ex'][0] = data['ex'][0] * unitRatio;
            data['ex'][1] = data['ex'][1] * unitRatio;
        }
        if (data['ey'] != null) {
            const unitRatio = storage.coordinateStore.lengthUnitRatio || 1;
            data['ey'][0] = data['ey'][0] * unitRatio;
            data['ey'][1] = data['ey'][1] * unitRatio;
        }
        data["sectionProtoType"] = sections.section.type;
        this.node.graph.cache.data.structComData = data;
        return true;
    }
}/**
 * 修改构件截面
 */
class EventHandlerUpdateStructureSection extends EventHandlerBase {
    run() {
        const section = this.node.graph.cache.data.section;
        if (!section) {
            return false;
        }
        const primitiveUuids = this.node.graph.cache.data.primitiveUuids;
        if ((primitiveUuids === null || primitiveUuids === void 0 ? void 0 : primitiveUuids.length) === 0) {
            return false;
        }
        // 把面域转到yoz平面
        const transformMatrix = section.transformMatrix;
        const axisZ = new Vector3(0, 0, 1);
        const axisY = new Vector3(0, 1, 0);
        // 创建一个旋转矩阵，绕 Z 轴旋转 90 度
        const rotationZ = new Matrix4().makeRotationAxis(axisZ, Math.PI / 2);
        // 创建一个旋转矩阵，绕 Y 轴旋转 90 度
        const rotationY = new Matrix4().makeRotationAxis(axisY, Math.PI / 2);
        const m1 = new Matrix4().premultiply(transformMatrix).premultiply(rotationZ).premultiply(rotationY);
        transformMatrix.copy(m1);
        let storage = this.getPythaStorage();
        let primitiveDict = storage.primitiveStore.primitiveDict;
        let count = 0;
        for (let i = 0; i < primitiveUuids.length; i++) {
            let pri = primitiveDict[primitiveUuids[i]];
            if (pri.type != "ExtrudeSoild")
                continue;
            let priGeo = pri.geos[0];
            let priClone = pri.clone();
            priClone.uuid = pri.uuid;
            const geo = new ExtrudeSolid(section.clone(), priGeo.height);
            priClone.geos = [geo];
            priClone.temporary = true;
            const keys = Object.keys(priClone.renderItemDict);
            if (keys.length > 0) {
                priClone.geoElements = [];
                let renderItem = priClone.renderItemDict[keys[0]];
                renderItem.geoElements = geo.getGeoElementsIdxList();
            }
            this.node.graph.cache.primitiveList.push(priClone);
            storage.primitiveStore.tempPrimitiveDict[priClone.uuid] = priClone;
            priClone.needRedraw = true;
            count++;
        }
        // 发送通知
        let outputData = {
            type: OutputDataType.STRING,
            data: `成功修改 ${count} 个构件`
        };
        this.node.graph.eventMediator.noticeHandler.notice(this.node, new PythaEventOutputData(outputData));
        return true;
    }
}/**
 * 修改构件截面信息
 */
class EventGraphUpdateComponentSection extends EventGraphBase {
    constructor() {
        super(...arguments);
        /**
         * 不添加到右键
         */
        this.putInLastCmd = false;
    }
    /**
     * 创建图
     */
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node2 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [{
                    type: NeedInputEnum.ACTIVE,
                    description: "",
                    option: 'M',
                    inputTypes: [PythaEventName.OBJECT]
                }]
        });
        const node3 = new EventGraphNode(this, EventHandlerObjectInput);
        const node4 = new EventGraphNodeSwitch(this, () => {
            if (this.checkSelectCom()) {
                return [1];
            }
            else {
                return [0];
            }
        });
        const node5 = new EventGraphNodeNotice(this, { cmd: 'Notice', data: '请选择要修改的构件' });
        const node6 = new EventGraphNode(this, EHChangePickCursor);
        const node7 = new EventGraphNode(this, EventHandlerEmptySelect);
        const node8 = new EventGraphNode(this, EventHandlerConfirm);
        const node9 = new EventGraphNodeSwitch(this, () => {
            if (this.checkSelectCom()) {
                return [1];
            }
            else {
                return [0];
            }
        });
        const node10 = new EventGraphNode(this, EventHandlerUpdateComponentSection);
        const node11 = new EventGraphNode(this, EventHandlerUpdateSectionFinish);
        const node12 = new EventGraphNodeNotice(this, { cmd: 'Notice', data: '未选中构件，请重新选择' });
        const node13 = new EventGraphNode(this, EHChangePickCursor);
        const node14 = new EventGraphNode(this, EventHandlerCmdEnd);
        const node15 = new EventGraphNode(this, EventHandlerGenSectionRegion);
        const node16 = new EventGraphNode(this, EventHandlerUpdateStructureSection);
        const node17 = new EventGraphNodeRender(this, true, UpdateType.ALL);
        const node18 = new EventGraphNode(this, EventHandlerGrid);
        const node19 = new EventGraphNode(this, EventHandlerEditPri);
        const node20 = new EventGraphNode(this, EventHandlerCalPrimitiveInView);
        this.addEdge(node1, node2);
        this.addEdge(node2, node3);
        this.addEdge(node3, node4);
        /**没有选中构件*/
        this.addEdge(node4, node5);
        this.addEdge(node5, node6);
        this.addEdge(node6, node7);
        this.addEdge(node7, node8);
        this.addEdge(node8, node9);
        /**没有选中构件*/
        this.addEdge(node9, node12);
        this.addEdge(node12, node13);
        this.addEdge(node13, node7);
        this.addEdge(node10, node11);
        this.addEdge(node11, node15);
        this.addEdge(node15, node16);
        this.addEdge(node17, node14);
        this.addEdge(node16, node19);
        this.addEdge(node19, node18);
        this.addEdge(node18, node20);
        this.addEdge(node20, node17);
        /**选中若干构件*/
        this.addEdge(node9, node10);
        /**选中若干构件*/
        this.addEdge(node4, node10);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/169
    }
    /**
     * 判断是否选择了构件
     * @returns
     */
    checkSelectCom() {
        let selectPrimitives = this.eventMediator.storage.selectStore.selectPrimitives || [];
        // 构件一定是拉伸体
        return selectPrimitives === null || selectPrimitives === void 0 ? void 0 : selectPrimitives.some(item => item.type === PrimitiveType.ExtrudeSoild);
    }
}
/**
 * UpdateComponentSection指令 用于注册事件图
 */
EventGraphUpdateComponentSection.cmd = 'UPDATE_COMPONENT_SECTION';/**
 * 修改构件类型信息
 */
class EventHandlerUpdateComponentType extends EventHandlerBase {
    run(data) {
        var _a;
        // console.log('修改构件类型信息 run');
        // 获取选中图元
        const storge = this.getPythaStorage();
        let selectPrimitives = storge.selectStore.selectPrimitives;
        if ((selectPrimitives === null || selectPrimitives === void 0 ? void 0 : selectPrimitives.length) === 0) {
            return true;
        }
        const param = this.node.graph.cache.inputObject['M'];
        const projectUuid = param.projectUuid;
        const type = param.type;
        let uuids = ((_a = selectPrimitives === null || selectPrimitives === void 0 ? void 0 : selectPrimitives.filter(item => item.type === PrimitiveType.ExtrudeSoild)) === null || _a === void 0 ? void 0 : _a.map(item => item.uuid)) || [];
        if ((uuids === null || uuids === void 0 ? void 0 : uuids.length) === 0)
            return true;
        updateComDataType(PythaPluginStructure.webData.axios, projectUuid, uuids, type).then(res => {
            let event = new PythaEventTypeChange();
            event.data = res.data;
            this.node.graph.eventMediator.emitEvent(event);
        }).catch(err => {
            console.log('修改构件材料信息失败', err);
            let event = new PythaEventTypeChange();
            event.data = 0;
            this.node.graph.eventMediator.emitEvent(event);
        });
        return true;
    }
}/**
 * 监听修改构件材料完成的信号
 */
class EventHandlerUpdateTypeFinish extends EventHandlerBase {
    constructor(node) {
        super(node);
        this.needEvent = new PythaEventTypeChange();
        this.needEventOnce = true;
    }
    run(event) {
        if (event.cmd != this.needEvent.cmd)
            return false; // 如果传入事件不符 则不执行处理器
        // 发送通知
        let outputData = {
            type: OutputDataType.STRING,
            data: `成功修改 ${event.data} 个构件`
        };
        this.node.graph.eventMediator.noticeHandler.notice(this.node, new PythaEventOutputData(outputData));
        return true;
    }
}/**
 * 修改构件类型信息
 */
class EventGraphUpdateComponentType extends EventGraphBase {
    constructor() {
        super(...arguments);
        /**
         * 不添加到右键
         */
        this.putInLastCmd = false;
    }
    /**
     * 创建图
     */
    generate() {
        const node1 = new EventGraphNode(this, EventHandlerStart);
        const node2 = new EventGraphNodeNotice(this, {
            cmd: 'need_input', data: [{
                    type: NeedInputEnum.ACTIVE,
                    description: "",
                    option: 'M',
                    inputTypes: [PythaEventName.OBJECT]
                }]
        });
        const node3 = new EventGraphNode(this, EventHandlerObjectInput);
        const node4 = new EventGraphNodeSwitch(this, () => {
            if (this.checkSelectCom()) {
                return [1];
            }
            else {
                return [0];
            }
        });
        const node5 = new EventGraphNodeNotice(this, { cmd: 'Notice', data: '请选择要修改的构件' });
        const node6 = new EventGraphNode(this, EHChangePickCursor);
        const node7 = new EventGraphNode(this, EventHandlerEmptySelect);
        const node8 = new EventGraphNode(this, EventHandlerConfirm);
        const node9 = new EventGraphNodeSwitch(this, () => {
            if (this.checkSelectCom()) {
                return [1];
            }
            else {
                return [0];
            }
        });
        const node10 = new EventGraphNodeNotice(this, { cmd: 'Notice', data: '未选中构件，请重新选择' });
        const node11 = new EventGraphNode(this, EventHandlerUpdateComponentType);
        const node13 = new EventGraphNode(this, EventHandlerUpdateTypeFinish);
        const node15 = new EventGraphNode(this, EventHandlerCmdEnd);
        this.addEdge(node1, node2);
        this.addEdge(node2, node3);
        this.addEdge(node3, node4);
        this.addEdge(node5, node6);
        this.addEdge(node6, node7);
        this.addEdge(node7, node8);
        /**没有选中构件*/
        this.addEdge(node4, node5);
        this.addEdge(node8, node9);
        /**没有选中构件*/
        this.addEdge(node9, node10);
        this.addEdge(node11, node13);
        this.addEdge(node13, node15);
        this.addEdge(node10, node6);
        /**选中若干构件*/
        this.addEdge(node9, node11);
        /**选中若干构件*/
        this.addEdge(node4, node11);
        // 指定初始状态下的当前节点和头节点
        this.currentPointers = [node1];
        this.head = node1;
        // https://event.graph.tncet.com/graphs/170
    }
    /**
     * 判断是否选择了构件
     * @returns
     */
    checkSelectCom() {
        let selectPrimitives = this.eventMediator.storage.selectStore.selectPrimitives || [];
        console.log('selectPrimitives', selectPrimitives);
        // 构件一定是拉伸体
        return selectPrimitives === null || selectPrimitives === void 0 ? void 0 : selectPrimitives.some(item => item.type === PrimitiveType.ExtrudeSoild);
    }
}
/**
 * UpdateComponentType指令 用于注册事件图
 */
EventGraphUpdateComponentType.cmd = 'UPDATE_COMPONENT_TYPE';class PythaPluginStructure extends PythaPluginBase {
    /**
     * 所有定义的graph和GEO在这里注册
     */
    generate(data) {
        this.graphs.push({ cmd: EventGraphStructComTwoPoint.cmd, constructor: EventGraphStructComTwoPoint });
        this.graphs.push({ cmd: EventGraphStructComLine.cmd, constructor: EventGraphStructComLine });
        this.graphs.push({ cmd: EventGraphBoardCornerPoint.cmd, constructor: EventGraphBoardCornerPoint });
        this.graphs.push({ cmd: EventGraphStructBoardRegion.cmd, constructor: EventGraphStructBoardRegion });
        this.graphs.push({ cmd: EventGraphStructWallCornerPoint.cmd, constructor: EventGraphStructWallCornerPoint });
        this.graphs.push({ cmd: EventGraphStructWallRegion.cmd, constructor: EventGraphStructWallRegion });
        this.graphs.push({ cmd: EventGraphStructWallAxisLine.cmd, constructor: EventGraphStructWallAxisLine });
        this.graphs.push({ cmd: EventGraphGenSectionRegion.cmd, constructor: EventGraphGenSectionRegion });
        this.graphs.push({ cmd: EventGraphGenStructPrimitive.cmd, constructor: EventGraphGenStructPrimitive });
        this.graphs.push({ cmd: EventGraphStructComHeight.cmd, constructor: EventGraphStructComHeight });
        this.graphs.push({ cmd: EventGraphUpdateComponentMaterial.cmd, constructor: EventGraphUpdateComponentMaterial });
        this.graphs.push({ cmd: EventGraphUpdateComponentSection.cmd, constructor: EventGraphUpdateComponentSection });
        this.graphs.push({ cmd: EventGraphUpdateComponentType.cmd, constructor: EventGraphUpdateComponentType });
        PythaPluginStructure.webData = data;
        this.graftGraphs.push({ cmd: EventGraphDelete.cmd, constructor: EventGraphDeleteStructInfo });
    }
}
PythaPluginStructure.webData = {};export{EventHandlerGenSectionRegion,ExtraPythaEventName,PythaEventMaterialChange,PythaEventSectionChange,PythaEventStructComData,PythaEventTypeChange,PythaPluginStructure,PythaStructEventDataChange,PythaStructEventName};